import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import { useState, useEffect, useMemo } from 'react';
import { GET, PUT, DELETE, UPLOAD, POST, PATCH } from '../../../configs/api';
import { api, states } from '../../../utils/constants';
import {
  ACCEPT_ATTACHMENTS,
  isImageFile,
  isVideoFile,
  isPreviewableMedia,
  isUploadCancelledError,
  uploadViaPresignedPost,
  validateAttachmentFile,
} from '../../../utils/fileTypes';
import { getStatusLabel, getStatusChipStyles, getStatusOptions, canSetJobStatusToInvoiced as canSetJobStatusToInvoicedRule, isNteZeroingStatus, isLockingStatus, JOB_STATUSES } from '../../../utils/jobStatuses';
import { getPriorityLabel, PRIORITY_OPTIONS } from '../../../utils/jobPriorities';
import {
  formatSlaDisplay,
  getMinimumSlaInputValue,
  getSlaPayloadValue,
  getSlaValidationMessage,
  toLocalDateTimeInputValue,
} from '../../../utils/jobSla';
import {
  formatWallClockDate,
  formatWallClockDateTime,
  nowAsStoredWallClock,
} from '../../../utils/invoiceDateTime';
import { fetchStoreNameSuggestions, addStoreNameSuggestion, removeStoreNameSuggestion, upsertStoreNameOption } from '../../../utils/storeNameSuggestions';
import { fetchJobTags } from '../../../utils/jobTagSuggestions';
import JobTagsField from './JobTagsField';
import GenerateProposalButton, { appendGeneratedBillableItems } from './GenerateProposalButton';
import {
  BILLABLE_ITEMS_GRID_COLUMNS,
  getBillableItemsPanelSx,
  getBillableTotalRowSx,
  getModernFieldSurface,
  getModernTextFieldSx,
} from './modernFormFieldStyles';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { useSelector, useDispatch } from 'react-redux';
import { updateJob, deleteJob, removePendingProposal } from '../../../store/slices/jobsSlice';
import { fetchAccountingTabCounts } from '../../../store/slices/accountingCountsSlice';
import { getActiveAdmins, getActiveDispatchers, getTechnicians, getUsersList } from '../../../store/slices/usersSlice';
import { formatUserDisplayName, resolveUserDisplayNameByEmail, resolveUserDisplayNameById } from '../../../utils/userDisplayName';
import DismissJobUnlockRequestDialog from './DismissJobUnlockRequestDialog';
import InvoiceDateChoiceDialog from './InvoiceDateChoiceDialog';
import {
  isOwner,
  isAdmin,
  isAccountant,
  isAccounting,
  canAccessAccounting,
  isDispatcher,
  isGeneralManager,
  isOnlyDispatcher,
  isAssignedDispatcher,
  isManagement,
  isOwnerOrAdminOrManagement,
  canCreateOrEditJob,
  canUnlockJob as canUnlockJobPermission,
  canRequestJobUnlock as canRequestJobUnlockPermission,
  canEditJobNotes as canEditJobNotesPermission,
  canEditSentToClientOnJob,
  canSeeQuickbooksInvoice,
  canViewQuickbooksInvoiceAttachments,
  filterJobAttachmentsForUser,
  canInvoiceProposal as canInvoiceProposalPermission,
  canCreatePart as canCreatePartPermission,
  canEditPart as canEditPartPermission,
  canDeletePart as canDeletePartPermission,
  canRequestPartApproval as canRequestPartApprovalPermission,
  canRequestPartUnlock as canRequestPartUnlockPermission,
  canUploadPrivateAttachment,
  canUpdateAttachmentPrivacyOnJob,
  canManageJobAttachmentOnJob,
  canDeleteJobAttachment,
  primaryRole,
} from '../../../utils/permissions';
import { isTechnicianAssignable } from '../../../utils/technicianStatus';
import { getClients } from '../../../store/slices/clientsSlice';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import JSZip from 'jszip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import JobLinksSection from './JobLinksSection';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Checkbox from '@mui/material/Checkbox';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import BlockIcon from '@mui/icons-material/Block';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SignpostIcon from '@mui/icons-material/Signpost';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlagIcon from '@mui/icons-material/Flag';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import BuildIcon from '@mui/icons-material/Build';
import InfoIcon from '@mui/icons-material/Info';
import InvoiceTab from './tabs/InvoiceTab';
import TechniciansTab from './tabs/TechniciansTab';
import CommentsTab from './tabs/CommentsTab';
import ActivityTab from './tabs/ActivityTab';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

function PreviewPaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-preview-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Helper function to get display name for technician/vendor
const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') {
    return tech.company_name || '';
  }
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `job-tab-${index}`,
    'aria-controls': `job-tabpanel-${index}`,
  };
}

// Attachment categories - mapping between display name and database value
// Database stores: 'Before', 'After', 'Sign off', 'from_client', 'Other', 'quickbooks_invoice'
// Display shows: 'Before', 'After', 'Sign off', 'From client', 'Other', 'Quickbooks Invoice'
const ATTACHMENT_CATEGORIES_MAP = {
  'quickbooks_invoice': 'Quickbooks Invoice',
  'Before': 'Before',
  'After': 'After', 
  'Sign off': 'Sign off',
  'from_client': 'From client',
  'Other': 'Other'
};

// For Autocomplete options - use database values (keys)
const ATTACHMENT_CATEGORIES = Object.keys(ATTACHMENT_CATEGORIES_MAP);

// Full QB manage/upload access (accounting). View-only for admin uses canViewQuickbooksInvoiceAttachments.
const canAccessQuickbooksInvoice = canSeeQuickbooksInvoice;

// Helper to get display name from database value
const getCategoryDisplayName = (dbValue) => ATTACHMENT_CATEGORIES_MAP[dbValue] || dbValue;

// Billable item types for proposals (value stored in DB, label shown in UI)
const PART_ORDER_STATUSES = [
  { value: 1, label: 'Pending' },
  { value: 2, label: 'Ordered' },
  { value: 3, label: 'Delivered' },
];

const BILLABLE_ITEM_TYPES = [
  { value: 'labor', label: 'Labor' },
  { value: 'parts', label: 'Parts' },
  { value: 'materials', label: 'Materials' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'trip', label: 'Trip' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'service_call', label: 'Service Call' },
];
const BILLABLE_ITEM_TYPE_SUGGESTIONS_KEY = 'proposalBillableTypeSuggestions';
const MAX_BILLABLE_ITEM_TYPE_SUGGESTIONS = 50;
const billableItemTypeFilter = createFilterOptions();

/** Allow partial numeric strings while typing (empty, decimals, optional leading minus for rates). */
const isPartialBillableQuantityInput = (raw) => raw === '' || /^\d*\.?\d*$/.test(raw);
const isPartialBillableRateInput = (raw) => raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw);

const billableLineAmount = (quantity, unitPrice) => {
  const qty = parseFloat(quantity) || 0;
  const rate = typeof unitPrice === 'string' ? parseFloat(unitPrice) : parseFloat(unitPrice);
  if (Number.isNaN(rate)) return 0;
  return qty * rate;
};

const getBillableItemTypeSuggestions = () => {
  try {
    const raw = localStorage.getItem(BILLABLE_ITEM_TYPE_SUGGESTIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const normalized = parsed
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean);
    return [...new Set(normalized)].slice(0, MAX_BILLABLE_ITEM_TYPE_SUGGESTIONS);
  } catch {
    return [];
  }
};

const addBillableItemTypeSuggestion = (typeValue) => {
  const value = typeof typeValue === 'string' ? typeValue.trim() : '';
  if (!value) return;

  const isDefaultType = BILLABLE_ITEM_TYPES.some(
    (t) => t.value.toLowerCase() === value.toLowerCase() || t.label.toLowerCase() === value.toLowerCase()
  );
  if (isDefaultType) return;

  const existing = getBillableItemTypeSuggestions();
  const deduped = [value, ...existing.filter((v) => v.toLowerCase() !== value.toLowerCase())]
    .slice(0, MAX_BILLABLE_ITEM_TYPE_SUGGESTIONS);
  try {
    localStorage.setItem(BILLABLE_ITEM_TYPE_SUGGESTIONS_KEY, JSON.stringify(deduped));
  } catch (e) {
    console.warn('Failed to save billable item type suggestion:', e);
  }
};

const getBillableItemLabel = (value) => BILLABLE_ITEM_TYPES.find((t) => t.value === value)?.label ?? (value ? `${value.charAt(0).toUpperCase()}${value.slice(1).replace(/_/g, ' ')}` : 'Labor');


// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

export default function JobDetailsDialog({ selectedJob, setSelectedJob, user: currentUser, initialTabValue = 0 }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const user = useMemo(
    () => ({ ...(currentUser || {}), role: currentUser?.role || primaryRole(currentUser) }),
    [currentUser]
  );
  const admins = useSelector(getActiveAdmins);
  const dispatchers = useSelector(getActiveDispatchers);
  const technicians = useSelector(getTechnicians);
  const usersList = useSelector(getUsersList);

  const displayUserByEmail = useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u])
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);
  const clients = useSelector(getClients);
  const canPickDispatcher = canCreateOrEditJob(user);
  const canPickClient = canCreateOrEditJob(user);
  /** True for users who may edit core job fields in the dialog (not primary-role–based). */
  const canEditJobFieldsInDialog = !isOnlyDispatcher(user);
  const jobs = useSelector(state => state.jobs.jobs); // Get jobs from Redux
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Loading state for additional data (technicians, parts, attachments)
  const [loadingAdditionalData, setLoadingAdditionalData] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const clientOptionsForEdit = useMemo(() => {
    const all = clients.clients || [];
    const active = all.filter((client) => Number(client.client_status) === 1);
    const currentId = editJob?.client_id;
    if (!currentId) return active;
    if (active.some((client) => client.id === currentId)) return active;

    // Keep the job's existing client visible even if it became inactive after assignment.
    const current = all.find((client) => client.id === currentId);
    if (current) return [current, ...active];

    // Fallback when Redux list is empty/stale but the job still has a client name.
    if (jobDetails?.client_id === currentId && jobDetails?.client_name) {
      return [{
        id: jobDetails.client_id,
        client_name: jobDetails.client_name,
        client_status: 0,
      }, ...active];
    }
    return active;
  }, [clients.clients, editJob?.client_id, jobDetails?.client_id, jobDetails?.client_name]);
  const [storeNameOptions, setStoreNameOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const canManageStoreNames = isManagement(user);
  const canCreateJobTags = isManagement(user);
  const canDeleteJobTags = isManagement(user);
  const [parts, setParts] = useState([]);
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  // const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelNteConfirm, setShowCancelNteConfirm] = useState(false);
  const [pendingCancelStatus, setPendingCancelStatus] = useState(null);
  const [showNoTechnicianConfirm, setShowNoTechnicianConfirm] = useState(false);
  const [pendingNoTechnicianAction, setPendingNoTechnicianAction] = useState(null);
  const [showInvoiceDateDialog, setShowInvoiceDateDialog] = useState(false);
  const [pendingInvoiceProposalId, setPendingInvoiceProposalId] = useState(null);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState({});
  const [loadingAttachmentPreviews, setLoadingAttachmentPreviews] = useState({});
  const [failedAttachmentPreviews, setFailedAttachmentPreviews] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState({});
  const [downloadProgress, setDownloadProgress] = useState({});
  const [downloadErrors, setDownloadErrors] = useState({});
  const [downloadingAllAttachments, setDownloadingAllAttachments] = useState(false);
  
  const [deletingAttachments, setDeletingAttachments] = useState({});
  const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [editingCategory, setEditingCategory] = useState({});
  const [jobLinks, setJobLinks] = useState([]);
  
  // Drag and drop state
  const [draggedAttachment, setDraggedAttachment] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isExternalAttachmentDrag, setIsExternalAttachmentDrag] = useState(false);
  
  // Attachment modal state (multi-file with per-file metadata)
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [modalFiles, setModalFiles] = useState([]); // { id, file, fileName, category, is_private, preview? }[]
  const [modalDragOver, setModalDragOver] = useState(false);
  const [modalUploading, setModalUploading] = useState(false);
  const [modalErrors, setModalErrors] = useState({}); // { fileId: errorMessage }
  const [modalUploadProgress, setModalUploadProgress] = useState({}); // { fileId: percent } - videos only
  const modalUploadAbortRef = React.useRef(null); // AbortController for in-flight video uploads
  
  // Change tracking state
  const [partsChanges, setPartsChanges] = useState({ added: [], modified: [], deleted: [] });

  // Proposals state
  const [proposals, setProposals] = useState([]);
  const [editingProposal, setEditingProposal] = useState(null);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const getEmptyProposalForm = () => ({
    proposal_text: '',
    items: [{ type: 'labor', description: '', quantity: 1, unit_price: 0 }],
    // Admin/management-created proposals are approved by default (no create-time toggle)
    is_approved: isAdmin(user) || isManagement(user),
    client_approval: false
  });
  const [proposalForm, setProposalForm] = useState(getEmptyProposalForm);
  const [proposalErrors, setProposalErrors] = useState({});
  const [loadingProposals, setLoadingProposals] = useState({});
  const [expandedProposal, setExpandedProposal] = useState(null);
  const [showDeleteProposalConfirm, setShowDeleteProposalConfirm] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [showInvoicedEditWarn, setShowInvoicedEditWarn] = useState(false);
  const [pendingInvoicedEditProposal, setPendingInvoicedEditProposal] = useState(null);
  const [customBillableItemTypes, setCustomBillableItemTypes] = useState(() => getBillableItemTypeSuggestions());
  const billableItemTypeOptions = useMemo(() => {
    const defaultValues = new Set(BILLABLE_ITEM_TYPES.map((t) => t.value.toLowerCase()));
    const customOptions = customBillableItemTypes
      .filter((v) => !defaultValues.has(v.toLowerCase()))
      .map((v) => ({ value: v, label: getBillableItemLabel(v) }));
    return [...BILLABLE_ITEM_TYPES, ...customOptions];
  }, [customBillableItemTypes]);

  const persistCustomBillableItemType = (typeValue) => {
    const value = typeof typeValue === 'string' ? typeValue.trim() : '';
    if (!value) return;
    const isDefaultType = BILLABLE_ITEM_TYPES.some(
      (t) => t.value.toLowerCase() === value.toLowerCase() || t.label.toLowerCase() === value.toLowerCase()
    );
    if (isDefaultType) return;

    addBillableItemTypeSuggestion(value);
    setCustomBillableItemTypes((prev) => {
      if (prev.some((v) => v.toLowerCase() === value.toLowerCase())) return prev;
      return [value, ...prev].slice(0, MAX_BILLABLE_ITEM_TYPE_SUGGESTIONS);
    });
  };
  
  // Rejection state
  const [rejectingProposal, setRejectingProposal] = useState(null);
  const [rejectionForm, setRejectionForm] = useState({ notes: '', rejectWithoutNote: false });
  const [rejectionErrors, setRejectionErrors] = useState({});

  // Parts state (workflow-based, independent of edit mode)
  const [editingPart, setEditingPart] = useState(null);
  const [creatingPart, setCreatingPart] = useState(false);
  const [partForm, setPartForm] = useState({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
  const [partErrors, setPartErrors] = useState({});
  const [loadingParts, setLoadingParts] = useState({});
  const [expandedPart, setExpandedPart] = useState(null);
  const [showDeletePartConfirm, setShowDeletePartConfirm] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);
  const [rejectingPart, setRejectingPart] = useState(null);
  const [partRejectionForm, setPartRejectionForm] = useState({ notes: '', rejectWithoutNote: false });
  const [partRejectionErrors, setPartRejectionErrors] = useState({});

  // Part attachments state
  const [partAttachments, setPartAttachments] = useState({}); // { partId: [attachments] }
  const [uploadingPartFiles, setUploadingPartFiles] = useState({}); // { partId: { fileId: progress } }
  const [deletingPartAttachments, setDeletingPartAttachments] = useState({}); // { attachmentId: boolean }
  const [partAttachmentErrors, setPartAttachmentErrors] = useState({}); // { partId: errorMessage }

  // Financial toggles state
  const [loadingQuickbooksInvoiced, setLoadingQuickbooksInvoiced] = useState(false);
  const [loadingFactored, setLoadingFactored] = useState(false);
  const [loadingPaid, setLoadingPaid] = useState(false);
  const [loadingSentToClient, setLoadingSentToClient] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogRefreshToken, setActivityLogRefreshToken] = useState(0);

  useEffect(() => {
    const clearExternalDragState = () => {
      setIsExternalAttachmentDrag(false);
      setDragOverCategory(null);
    };

    window.addEventListener('drop', clearExternalDragState);
    window.addEventListener('dragend', clearExternalDragState);

    return () => {
      window.removeEventListener('drop', clearExternalDragState);
      window.removeEventListener('dragend', clearExternalDragState);
    };
  }, []);

  useEffect(() => {
    if (selectedJob) {
      // Set initial tab when opening the dialog
      setTabValue(typeof initialTabValue === 'number' ? initialTabValue : 0);
      // Set basic job details immediately from selectedJob
      setJobDetails(selectedJob);
      setError('');
      
      // Fetch additional job details (technicians, parts, attachments)
      fetchJobDetails(selectedJob.id);
    } else {
      // Clear job details when dialog closes
      setJobDetails(null);
      setError('');
      setEditMode(false);
      setEditJob(null);
      setAttachments([]);
      setJobLinks([]);
      setAttachmentPreviewUrls({});
      setLoadingAttachmentPreviews({});
      setFailedAttachmentPreviews({});
      setProposals([]);
      setTabValue(0);
      setCreatingProposal(false);
      setEditingProposal(null);
      setProposalForm(getEmptyProposalForm());
      setProposalErrors({});
      setLoadingProposals({});
      setExpandedProposal(null);
      setRejectingProposal(null);
      setRejectionForm({ notes: '', rejectWithoutNote: false });
      setRejectionErrors({});
      // Parts state cleanup
      setParts([]);
      setCreatingPart(false);
      setEditingPart(null);
      setPartForm({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
      setPartErrors({});
      setLoadingParts({});
      setExpandedPart(null);
      setRejectingPart(null);
      setPartRejectionForm({ notes: '', rejectWithoutNote: false });
      setPartRejectionErrors({});
      setLoadingAttachments({});
      setDownloadProgress({});
      setDownloadErrors({});
      setDownloadingAllAttachments(false);
      setLoadingAdditionalData(false);
      setCommentsCount(0);
      setComments([]);
      setActivitiesCount(0);
      setActivityLogs([]);
    }
  }, [selectedJob]);

  // Sync jobDetails from Redux when jobs array updates
  // This ensures the badge count and other derived data stay in sync
  useEffect(() => {
    if (selectedJob?.id) {
      const updatedJob = jobs.find(j => j.id === selectedJob.id);
      if (updatedJob) {
        setJobDetails(prev => {
          // Only update if there are actual changes to avoid unnecessary re-renders
          if (!prev || JSON.stringify(prev.technicians) !== JSON.stringify(updatedJob.technicians)) {
            return { ...prev, ...updatedJob };
          }
          return prev;
        });
      }
    }
  }, [jobs, selectedJob?.id]);

  // Load shared store name and tag suggestions when a job is selected
  useEffect(() => {
    if (!selectedJob) return;
    let cancelled = false;
    fetchStoreNameSuggestions().then((options) => {
      if (!cancelled) setStoreNameOptions(options);
    });
    fetchJobTags({ activeOnly: true }).then((options) => {
      if (!cancelled) setTagOptions(options);
    });
    return () => { cancelled = true; };
  }, [selectedJob]);

  // Set attachments, proposals, and parts when jobDetails is available
  useEffect(() => {
    if (jobDetails) {
      setAttachments(jobDetails.attachments || []);
      setJobLinks(jobDetails.links || []);
      setProposals(jobDetails.proposals || []);
      // Set parts from jobDetails (independent of edit mode)
      if (jobDetails.parts) {
        const partsData = jobDetails.parts.map(part => ({
          id: part.id,
          name: part.name,
          description: part.description || '',
          url: part.url || '',
          cost: part.cost || 0,
          order_status: part.order_status || 1,
          technician_id: part.technician_id || null,
          technician_type: part.technician_type,
          technician_first_name: part.technician_first_name,
          technician_last_name: part.technician_last_name,
          technician_company_name: part.technician_company_name,
          notes: part.notes || '',
          created_by: part.created_by || part.created_by_email || '',
          created_by_email: part.created_by_email || part.created_by || '',
          created_at: part.created_at,
          updated_at: part.updated_at,
          updated_by: part.updated_by,
          is_approved: part.is_approved,
          is_locked: part.is_locked,
          approved_by: part.approved_by,
          approved_at: part.approved_at,
          locked_by: part.locked_by,
          locked_at: part.locked_at,
          rejected_by: part.rejected_by,
          rejected_at: part.rejected_at,
          rejection_notes: part.rejection_notes,
          is_rejected: part.is_rejected,
          requested_approval_at: part.requested_approval_at,
          requested_unlock_at: part.requested_unlock_at
        }));
        setParts(partsData);
      } else {
        setParts([]);
      }
    }
  }, [jobDetails]);

  // Fetch presigned URLs for image and video thumbnails shown in attachment cards.
  useEffect(() => {
    const mediaAttachments = (attachments || []).filter(
      (attachment) => isPreviewableMedia(attachment.file_type)
    );

    const missingPreviewAttachments = mediaAttachments.filter(
      (attachment) =>
        !attachmentPreviewUrls[attachment.id] &&
        !loadingAttachmentPreviews[attachment.id] &&
        !failedAttachmentPreviews[attachment.id]
    );

    if (!missingPreviewAttachments.length) return;

    missingPreviewAttachments.forEach(async (attachment) => {
      setLoadingAttachmentPreviews((prev) => ({ ...prev, [attachment.id]: true }));

      try {
        const { data, status } = await GET(`${api.getAttachmentDownloadUrl}/${attachment.id}`);
        const previewUrl = data?.data?.downloadUrl;

        if (status === 200 && previewUrl) {
          setAttachmentPreviewUrls((prev) => ({ ...prev, [attachment.id]: previewUrl }));
        } else {
          setFailedAttachmentPreviews((prev) => ({ ...prev, [attachment.id]: true }));
        }
      } catch (err) {
        console.error('Failed to load attachment preview URL:', err);
        setFailedAttachmentPreviews((prev) => ({ ...prev, [attachment.id]: true }));
      } finally {
        setLoadingAttachmentPreviews((prev) => {
          const next = { ...prev };
          delete next[attachment.id];
          return next;
        });
      }
    });
  }, [attachments, attachmentPreviewUrls, loadingAttachmentPreviews, failedAttachmentPreviews]);

  // Initialize technician assignments and parts when entering edit mode
  useEffect(() => {
    if (jobDetails && editMode) {
      // Initialize parts
      if (jobDetails.parts) {
        const partsData = jobDetails.parts.map(part => ({
          id: part.id,
          name: part.name,
          description: part.description || '',
          url: part.url || '',
          cost: part.cost || 0,
          order_status: part.order_status || 1,
          technician_id: part.technician_id,
          technician_type: part.technician_type,
          technician_first_name: part.technician_first_name,
          technician_last_name: part.technician_last_name,
          technician_company_name: part.technician_company_name,
          notes: part.notes || '',
          created_by_email: part.created_by_email || ''
        }));
        setParts(partsData);
      }
      
      // Reset change tracking
      setPartsChanges({ added: [], modified: [], deleted: [] });
    }
  }, [jobDetails, editMode]);

  // First invoiced proposal date (earliest invoiced_at among invoiced proposals); null if none
  const firstInvoicedDate = useMemo(() => {
    if (!proposals?.length) return null;
    const invoiced = proposals.filter(p => p.invoiced && p.invoiced_at);
    if (!invoiced.length) return null;
    const sorted = [...invoiced].sort((a, b) => new Date(a.invoiced_at) - new Date(b.invoiced_at));
    return sorted[0].invoiced_at;
  }, [proposals]);

  // Most recent invoiced_at on any proposal for this job, including after uninvoice / unlock
  // (latest invoice date used — same baseline as API notify / re-invoice “original”).
  const latestPreservedInvoiceDate = useMemo(() => {
    if (!proposals?.length) return null;
    const withDate = proposals.filter((proposal) => proposal.invoiced_at);
    if (!withDate.length) return null;
    const sorted = [...withDate].sort((a, b) => new Date(b.invoiced_at) - new Date(a.invoiced_at));
    return sorted[0].invoiced_at;
  }, [proposals]);

  // Who invoiced the job (invoiced_by from the earliest invoiced proposal); null if none
  const firstInvoicedBy = useMemo(() => {
    if (!proposals?.length) return null;
    const invoiced = proposals.filter(p => p.invoiced && p.invoiced_at);
    if (!invoiced.length) return null;
    const sorted = [...invoiced].sort((a, b) => new Date(a.invoiced_at) - new Date(b.invoiced_at));
    const invoicedBy = sorted[0].invoiced_by;
    return invoicedBy ? resolveUserDisplayNameByEmail(invoicedBy, usersList) : null;
  }, [proposals, usersList]);

  const fetchJobDetails = async (jobId) => {
    setLoadingAdditionalData(true);
    setError('');
    try {
      const { data, status } = await GET(`${api.getJobById}/${jobId}`);
      if (status === 200) {
        // Update Redux store with the fetched job data
        // This will trigger the useEffect that syncs jobDetails
        dispatch(updateJob(data));
        
        // Also update local state for immediate availability
        setJobDetails(prev => ({
          ...prev,
          ...data,
          // Server-first when present; fall back to prev only if the payload omitted the field (nullish).
          status: data.status ?? prev?.status,
          is_locked: data.is_locked ?? prev?.is_locked,
          locked_at: data.locked_at ?? prev?.locked_at,
          locked_by: data.locked_by ?? prev?.locked_by,
          locked_reason_status: data.locked_reason_status ?? prev?.locked_reason_status,
          // Preserve basic job info from selectedJob when the list row had richer display data.
          client_name: prev?.client_name || data.client_name,
          admin: prev?.admin || data.admin,
          dispatcher: prev?.dispatcher || data.dispatcher,
          priority: prev?.priority || data.priority,
          state: prev?.state || data.state,
          city: prev?.city || data.city,
          address: prev?.address || data.address,
          address_2: data.address_2 ?? prev?.address_2 ?? null,
          description: prev?.description || data.description,
          created_at: prev?.created_at || data.created_at,
        }));

        // Organize part attachments by part_id
        if (data.partAttachments && Array.isArray(data.partAttachments)) {
          const attachmentsByPart = {};
          data.partAttachments.forEach(attachment => {
            if (!attachmentsByPart[attachment.entity_id]) {
              attachmentsByPart[attachment.entity_id] = [];
            }
            attachmentsByPart[attachment.entity_id].push(attachment);
          });
          setPartAttachments(attachmentsByPart);
        }

        const commentsResponse = await GET(`${api.getJobComments}/${jobId}/comments`);
        if (commentsResponse.status === 200) {
          const payload = commentsResponse.data;
          const nextComments = Array.isArray(payload)
            ? payload
            : (Array.isArray(payload?.comments) ? payload.comments : []);
          setComments(nextComments);
          setCommentsCount(nextComments.length);
        }

        const logsResponse = await GET(`${api.getJobLogs}/${jobId}/logs`);
        if (logsResponse.status === 200) {
          const nextLogs = Array.isArray(logsResponse.data) ? logsResponse.data : [];
          setActivityLogs(nextLogs);
          setActivitiesCount(nextLogs.length);
        }

        setActivityLogRefreshToken((current) => current + 1);
        
        setLoadingAdditionalData(false);
      } else {
        setError('Failed to fetch work order details');
        setLoadingAdditionalData(false);
      }
    } catch (error) {
      setError('Something went wrong, please try again later.');
      setLoadingAdditionalData(false);
    }
  };

  const handleCopyWorkOrder = async (event) => {
    event?.stopPropagation?.();
    const workOrder = jobDetails?.work_order && String(jobDetails.work_order).trim();
    if (!workOrder) return;
    try {
      await navigator.clipboard.writeText(workOrder);
      setShowCopyToast(true);
    } catch (err) {
      console.error('Failed to copy work order:', err);
      setShowError('Failed to copy work order to clipboard');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (addressData) => {
    // Format zip code if provided, otherwise clear it
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim()) 
      ? formatZipCode(addressData.zipCode) 
      : '';
    
    setEditJob({
      ...editJob,
      address: addressData.address || editJob.address,
      // Clear city if not provided, otherwise use the provided value
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      // Clear state if not provided, otherwise use the provided value
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip_code: formattedZip,
      // Store coordinates if available
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    });
  };

  const handleClose = () => {
    setSelectedJob(null);
    setTabValue(0);
    setEditMode(false);
    setEditJob(null);
    setParts([]);
    setErrors({});
    setShowError('');
    setPartsChanges({ added: [], modified: [], deleted: [] });

    setDeletingAttachments({});
    setJobLinks([]);
    
    // Clear drag and drop state
    setDraggedAttachment(null);
    setDragOverCategory(null);

    if (canAccessAccounting(user)) {
      dispatch(fetchAccountingTabCounts());
    }
  };

  const handleJobLinksChange = (links) => {
    setJobLinks(links);
    setJobDetails((prev) => (prev ? { ...prev, links } : prev));
  };

  // Validation function
  const validateJob = () => {
    let newErrors = {};

    if (!editJob?.work_order || !String(editJob.work_order).trim()) {
      newErrors.work_order = 'Work order is required';
    } else if (String(editJob.work_order).trim().length > 100) {
      newErrors.work_order = 'Work order must not exceed 100 characters';
    }
    if (!editJob?.trade) {
      newErrors.trade = 'Trade is required';
    }
    if (!editJob?.store_name || !String(editJob.store_name).trim()) {
      newErrors.store_name = 'Store name is required';
    } else if (String(editJob.store_name).trim().length > 255) {
      newErrors.store_name = 'Store name must not exceed 255 characters';
    }
    if (!editJob?.dispatcher_id) {
      newErrors.dispatcher_id = 'Dispatcher is required';
    }
    if (!editJob?.description || editJob?.description?.trim().length === 0) {
      newErrors.description = 'Description is required';
    }
    if (editJob?.nte === '' || editJob?.nte === null || editJob?.nte === undefined) {
      newErrors.nte = 'NTE is required';
    } else if (Number.isNaN(parseFloat(editJob.nte)) || parseFloat(editJob.nte) < 0) {
      newErrors.nte = 'NTE must be a non-negative number';
    }
    if (editJob?.status === '' || editJob?.status === null || editJob?.status === undefined) {
      newErrors.status = 'Status is required';
    }
    if (editJob?.priority === '' || editJob?.priority === null || editJob?.priority === undefined) {
      newErrors.priority = 'Priority is required';
    }
    if (!editJob?.city || editJob?.city?.trim().length === 0) {
      newErrors.city = 'City is required';
    }
    if (!editJob?.state || editJob?.state?.trim().length === 0) {
      newErrors.state = 'State is required';
    }
    if (!editJob?.address || editJob?.address?.trim().length < 3) {
      newErrors.address = 'Address must be at least 3 characters';
    }
    if (editJob?.address_2 && editJob.address_2.length > 500) {
      newErrors.address_2 = 'Address line 2 must not exceed 500 characters';
    }
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!editJob?.zip_code || !zipRegex.test(editJob.zip_code)) {
      newErrors.zip_code = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)';
    }
    if (editJob?.notes != null && editJob.notes.length > 500) {
      newErrors.notes = 'Notes must not exceed 500 characters';
    }
    const slaError = getSlaValidationMessage({
      value: editJob?.sla,
      originalValue: jobDetails?.sla,
      allowUnchangedPast: true,
      required: true,
    });
    if (slaError) {
      newErrors.sla = slaError;
    }
    
    // Validate parts
    const partsErrors = validateParts(parts);
    if (Object.keys(partsErrors).length > 0) {
      newErrors.parts = partsErrors;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateParts = (partsToValidate) => {
    const partsErrors = {};
    
    partsToValidate.forEach((part, index) => {
      const partErrors = {};
      
      // Validate part name - cannot be empty
      if (!part.name || part.name.trim().length === 0) {
        partErrors.name = 'Part name is required';
      }
      
      // Validate part cost - must be a positive number if provided
      if (part.cost !== '' && part.cost !== null && part.cost !== undefined) {
        const cost = parseFloat(part.cost);
        if (isNaN(cost) || cost < 0) {
          partErrors.cost = 'Part cost must be a positive number';
        }
      }
      
      // Validate order status - must be 1, 2, or 3
      if (part.order_status !== undefined && part.order_status !== null) {
        const orderStatus = parseInt(part.order_status);
        if (isNaN(orderStatus) || ![1, 2, 3].includes(orderStatus)) {
          partErrors.order_status = 'Order status must be 1, 2, or 3';
        }
      }
      
      // Validate technician ID - must be a valid UUID if provided
      if (part.technician_id) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(part.technician_id)) {
          partErrors.technician = 'Invalid technician ID';
        }
      }
      
      // Validate notes - cannot be empty if provided
      if (part.notes && part.notes.trim().length === 0) {
        partErrors.notes = 'Part notes cannot be empty';
      }

      // Validate description - cannot be empty if provided
      if (part.description && part.description.trim().length === 0) {
        partErrors.description = 'Part description cannot be empty';
      }
      
      // Only add part errors if there are any
      if (Object.keys(partErrors).length > 0) {
        partsErrors[index] = partErrors;
      }
    });
    
    return partsErrors;
  };

  // Save job function
  const handleSaveJob = async () => {
    if (!validateJob()) {
      setShowError('Please correct the highlighted fields');
      return;
    }

    const isDispatcherTransitioningToReadyForInvoicing =
      isOnlyDispatcher(user) &&
      Number(jobDetails?.status) !== 12 &&
      Number(editJob?.status) === 12;

    if (isDispatcherTransitioningToReadyForInvoicing) {
      setShowError('Dispatchers cannot set status to Ready for Invoicing');
      return;
    }

    if (Number(editJob?.status) === 13 && !canSetJobStatusToInvoiced()) {
      setShowError('At least one proposal should be invoiced');
      return;
    }

    // Block terminal (auto-locking) statuses when any technician is unpaid or unlocked
    if (isLockingStatus(Number(editJob?.status))) {
      const technicians = jobDetails?.technicians || [];
      if (technicians.length > 0) {
        const unpaidOrUnlocked = technicians.filter(tech => !tech.paid_date || !tech.is_locked);
        if (unpaidOrUnlocked.length > 0) {
          const names = unpaidOrUnlocked.map(tech => `${tech.first_name || ''} ${tech.last_name || ''}`.trim() || 'Unknown').join(', ');
          setShowError(
            `All assigned technicians must be paid and locked before setting the job to this status. ` +
            `The following technician${unpaidOrUnlocked.length > 1 ? 's need' : ' needs'} attention: ${names}`
          );
          return;
        }
      }
    }

    // Store current state for rollback
    const rollbackState = {
      jobDetails: JSON.parse(JSON.stringify(jobDetails)),
      parts: JSON.parse(JSON.stringify(parts))
    };
    
    // Optimistically update UI
    const optimisticNotes = (editJob.notes != null && editJob.notes.trim()) ? editJob.notes.trim().slice(0, 500) : null;
    const optimisticSla = getSlaPayloadValue(editJob.sla, jobDetails?.sla);
    const optimisticNte = isNteZeroingStatus(Number(editJob.status))
      ? 0
      : (typeof editJob.nte === 'number' ? editJob.nte : (parseFloat(editJob.nte) || 0));
    const optimisticJobDetails = {
      ...jobDetails,
      status: editJob.status,
      priority: editJob.priority,
      description: editJob.description,
      city: editJob.city,
      state: editJob.state,
      address: editJob.address,
      address_2: editJob.address_2 || null,
      zip_code: editJob.zip_code || '',
      latitude: editJob.latitude || null,
      longitude: editJob.longitude || null,
      trade: editJob.trade,
      store_name: (editJob.store_name && String(editJob.store_name).trim()) || '',
      tags: editJob.tags || [],
      nte: optimisticNte,
      work_order: (editJob.work_order && String(editJob.work_order).trim()) || '',
      notes: optimisticNotes,
      sla: optimisticSla,
      admin_id: editJob.admin_id,
      dispatcher_id: editJob.dispatcher_id,
      client_id: editJob.client_id,
      client_name: editJob.client_id ? clients.clients?.find(c => c.id === editJob.client_id)?.client_name : jobDetails.client_name
    };
    
    setJobDetails(optimisticJobDetails);
    dispatch(updateJob(optimisticJobDetails));
    setEditMode(false);
    setShowSuccessToast(true);
    
    // Background save
    setLoading(true);
    setShowError('');
    
    // Prepare parts changes
    const partsChangesToSend = {
      added: partsChanges.added.map(p => ({
        name: p.name,
        description: p.description,
        url: p.url,
        cost: parseFloat(p.cost) || 0,
        order_status: parseInt(p.order_status) || 1,
        technician_id: p.technician?.id || null,
        notes: p.notes || ''
      })),
      modified: partsChanges.modified.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        url: p.url,
        cost: parseFloat(p.cost) || 0,
        order_status: parseInt(p.order_status) || 1,
        technician_id: p.technician?.id || null,
        notes: p.notes || ''
      })),
      deleted: partsChanges.deleted
    };
    
    let body = {
      status: editJob.status,
      priority: editJob.priority,
      description: editJob.description,
      city: editJob.city,
      state: editJob.state,
      address: editJob.address,
      address_2: editJob.address_2 || null,
      zip_code: editJob.zip_code || '',
      latitude: editJob.latitude || null,
      longitude: editJob.longitude || null,
      trade: editJob.trade,
      store_name: (editJob.store_name && String(editJob.store_name).trim()) || '',
      tag_ids: (editJob.tags || []).map((tag) => tag.id),
      nte: optimisticNte,
      work_order: (editJob.work_order && String(editJob.work_order).trim()) || '',
      notes: (editJob.notes != null && editJob.notes.trim()) ? editJob.notes.trim().slice(0, 500) : null,
      sla: getSlaPayloadValue(editJob.sla, jobDetails?.sla),
      dispatcher_id: editJob.dispatcher_id,
      partsChanges: partsChangesToSend
    };

    // Owner can reassign admin; owner / GM / admin can reassign client (matches canPickClient).
    if (isOwner(user)) {
      body.admin_id = editJob.admin_id;
    }
    if (canPickClient) {
      body.client_id = editJob.client_id;
    }

    const { status, data, message } = await PUT(`${api.updateJob}/${selectedJob.id}`, {}, body);

    if (status === 200) {
      setJobDetails(data);
      dispatch(updateJob(data));
      setPartsChanges({ added: [], modified: [], deleted: [] });
    } else {
      // Rollback on failure
      setJobDetails(rollbackState.jobDetails);
      setParts(rollbackState.parts);
      dispatch(updateJob(rollbackState.jobDetails));
      setEditMode(true); // Re-enter edit mode
      setShowSuccessToast(false);
      
      // Check for field-specific errors (e.g., work_order duplicate)
      if (data?.errors && Array.isArray(data.errors)) {
        const fieldErrors = {};
        data.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path] = err.msg;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
      }
      
      setShowError(message || 'Update failed. Changes have been reverted. Please try again.');
    }
    
    setLoading(false);
  };

  // Delete job function
  const handleDeleteJob = async () => {
    setLoading(true);
    setShowError('');
    
    const { status } = await DELETE(`${api.deleteJob}/${selectedJob.id}`);

    if (status === 200) {
      // setShowDeleteSuccessToast(true);
      dispatch(deleteJob(selectedJob.id));
      setSelectedJob(null);
      setShowDeleteConfirm(false);
    } else {
      setShowError('Something went wrong, please try again later.');
    }
    setLoading(false);
  };

  // ===== JOB LOCK STATE =====
  // When a job is locked (status moved to Recall No Charge / Invoiced /
  // Cancelled / Invoiced/Loss) most of the dialog becomes read-only.
  // Attachments are an exception: users may still add new files, but cannot
  // delete or modify existing ones. Owner / GM / accountant can unlock; all
  // other job users can request an unlock. The backend enforces the same rule
  // via assertJobUnlocked() and 423.
  const isJobLocked = !!jobDetails?.is_locked;
  const canManageAttachmentsWhenLocked = isManagement(user);
  const canUnlockThisJob = canUnlockJobPermission(user);
  const canRequestUnlockThisJob =
    isJobLocked &&
    !canUnlockThisJob &&
    canRequestJobUnlockPermission(user, jobDetails);
  const [unlockingJob, setUnlockingJob] = useState(false);
  const [requestingUnlockJob, setRequestingUnlockJob] = useState(false);
  const [showRequestUnlockDialog, setShowRequestUnlockDialog] = useState(false);
  const [requestUnlockReason, setRequestUnlockReason] = useState('');
  const [dismissingUnlockRequest, setDismissingUnlockRequest] = useState(false);
  const [showDismissUnlockDialog, setShowDismissUnlockDialog] = useState(false);
  const [dismissUnlockReason, setDismissUnlockReason] = useState('');

  const handleUnlockJob = async () => {
    if (!jobDetails || !canUnlockThisJob || unlockingJob) return;
    setUnlockingJob(true);
    try {
      const response = await PATCH(`${api.unlockJob}/${jobDetails.id}/unlock`, {}, {});
      if (response?.error) {
        setShowError(response.message || 'Failed to unlock job');
      } else if (response?.data) {
        setJobDetails(prev => ({ ...(prev || {}), ...response.data }));
        dispatch(updateJob(response.data));
        // Full job fetch: backend uninvoices proposals on job unlock; PATCH only returns the job row.
        await fetchJobDetails(jobDetails.id);
        setShowSuccessToast(true);
      }
    } catch (err) {
      setShowError(err?.message || 'Failed to unlock job');
    } finally {
      setUnlockingJob(false);
    }
  };

  const handleRequestUnlockJob = async () => {
    if (!jobDetails || !canRequestUnlockThisJob || requestingUnlockJob) return;
    setRequestingUnlockJob(true);
    try {
      const response = await POST(
        `${api.requestUnlockJob}/${jobDetails.id}/request-unlock`,
        {},
        { reason: requestUnlockReason?.trim() || undefined }
      );
      if (response?.error) {
        setShowError(response.message || 'Failed to request unlock');
      } else if (response?.data?.job) {
        setJobDetails(prev => ({ ...(prev || {}), ...response.data.job }));
        dispatch(updateJob(response.data.job));
        setShowSuccessToast(true);
        setShowRequestUnlockDialog(false);
        setRequestUnlockReason('');
      }
    } catch (err) {
      setShowError(err?.message || 'Failed to request unlock');
    } finally {
      setRequestingUnlockJob(false);
    }
  };

  const handleDismissUnlockRequest = async () => {
    if (!jobDetails || !canUnlockThisJob || dismissingUnlockRequest) return;
    setDismissingUnlockRequest(true);
    try {
      const response = await POST(
        `${api.dismissUnlockRequest}/${jobDetails.id}/dismiss-unlock-request`,
        {},
        { reason: dismissUnlockReason?.trim() || undefined }
      );
      if (response?.error) {
        setShowError(response.message || 'Failed to dismiss unlock request');
      } else if (response?.data) {
        setJobDetails(prev => ({ ...(prev || {}), ...response.data }));
        dispatch(updateJob(response.data));
        setShowSuccessToast(true);
        setShowDismissUnlockDialog(false);
        setDismissUnlockReason('');
      }
    } catch (err) {
      setShowError(err?.message || 'Failed to dismiss unlock request');
    } finally {
      setDismissingUnlockRequest(false);
    }
  };

  // ===== PROPOSAL FUNCTIONS =====

  // Permission helper functions for proposals
  const canCreateProposal = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    if (isOwnerOrAdminOrManagement(user)) return true;
    if (isAssignedDispatcher(user, jobDetails)) return true;
    return false;
  };

  const canEditProposal = (proposal) => {
    if (!jobDetails || !proposal) return false;
    // Owner / GM / accountant: invoiced proposal only while the job is locked.
    if (isJobLocked) {
      return !!(proposal.invoiced && isAccounting(user));
    }
    if (proposal.invoiced) {
      return isAccounting(user) || isAdmin(user);
    }
    if (isAccounting(user) || isOwnerOrAdminOrManagement(user)) return true;
    if (isAssignedDispatcher(user, jobDetails) &&
        proposal.created_by === user.email &&
        !proposal.is_locked) {
      return true;
    }
    return false;
  };

  const canDeleteProposal = (proposal) => {
    if (!proposal) return false;
    if (isJobLocked) return false;
    // Invoiced proposals cannot be deleted
    if (proposal.invoiced) return false;
    if (isOwner(user)) return true;
    if (proposal.created_by === user.email) return true;
    return false;
  };

  const canApproveProposal = (proposal = null) => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    // If a specific proposal is provided, check if it's invoiced
    if (proposal && proposal.invoiced) return false;
    if (isOwnerOrAdminOrManagement(user)) return true;
    return false;
  };

  const getClientApprovedProposal = (excludeProposalId = null) => {
    return (proposals || []).find(
      (p) => p.client_approval && (!excludeProposalId || p.id !== excludeProposalId)
    ) || null;
  };

  const formatProposalClientApprovalLabel = (proposal) => {
    if (!proposal) return 'another proposal';
    const text = (proposal.proposal_text || '').trim();
    if (text) return text.length > 60 ? `${text.slice(0, 60)}…` : text;
    return `$${parseFloat(proposal.price || 0).toFixed(2)} proposal`;
  };

  const getClientApprovalBlockReason = (proposal = null) => {
    const other = getClientApprovedProposal(proposal?.id || null);
    if (!other) return '';
    return `Only one proposal can be approved by the client per job. Revoke client approval on "${formatProposalClientApprovalLabel(other)}" first.`;
  };

  const canSetClientApproval = (proposal = null) => {
    if (proposal?.client_approval) return true; // allow revoke
    return !getClientApprovedProposal(proposal?.id || null);
  };

  const canLockProposal = (proposal = null) => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    // If a specific proposal is provided, check if it's invoiced
    if (proposal && proposal.invoiced) return false;
    if (isOwnerOrAdminOrManagement(user)) return true;
    return false;
  };

  const canRejectProposal = (proposal = null) => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    // If a specific proposal is provided, check if it's invoiced
    if (proposal && proposal.invoiced) return false;
    if (isOwnerOrAdminOrManagement(user)) return true;
    return false;
  };

  const canRequestUnlock = (proposal) => {
    if (!jobDetails || !proposal) return false;
    if (isJobLocked) return false;
    // Check if proposal is locked or invoiced
    if (!proposal.is_locked && !proposal.invoiced) return false;

    // Locker unlocks directly (or waits); they never request their own unlock
    const lockedBy = (proposal.locked_by || '').toLowerCase();
    const userEmail = (user?.email || '').toLowerCase();
    if (lockedBy && userEmail && lockedBy === userEmail) return false;

    // Admin first: users with admin + dispatcher roles may not be the assigned dispatcher
    if (isAdmin(user)) return true;

    // Dispatcher: assigned to this job (same as legacy behaviour)
    if (isAssignedDispatcher(user, jobDetails)) return true;

    return false;
  };

  const canUninvoice = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    // Only owners can modify invoice
    if (isOwner(user)) return true;
    return false;
  };

  const canInvoiceProposal = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    return canInvoiceProposalPermission(user);
  };

  const invoiceTechnicianBlockReason = useMemo(() => {
    const techniciansToCheck = jobDetails?.technicians || [];

    if (techniciansToCheck.length === 0) return '';

    const hasBlockingTechnician = techniciansToCheck.some(
      tech => !tech.paid_date || !tech.is_locked
    );

    return hasBlockingTechnician
      ? 'All assigned technicians must be paid and locked before invoicing a proposal'
      : '';
  }, [jobDetails?.technicians]);

  // QB invoiced / factored / paid: owner / accountant / GM; allowed even when job is locked (API matches).
  const canEditFinancialToggles = () => isAccounting(user);

  // Sent to client: accounting always (incl. locked); admin only when locked (API also checks user_client_access).
  const canEditSentToClientToggle = () => canEditSentToClientOnJob(user, isJobLocked);

  // Notes are editable only by admins with access to this client (owner, accountant, gm, admin). Dispatchers cannot edit.
  const canEditJobNotes = () => {
    if (isJobLocked) return false;
    return canEditJobNotesPermission(user);
  };

  // Handler for toggling quickbooks_invoiced
  const handleToggleQuickbooksInvoiced = async () => {
    if (!jobDetails || !canEditFinancialToggles()) return;
    
    setLoadingQuickbooksInvoiced(true);
    try {
      const response = await PATCH(`${api.updateQuickbooksInvoiced}/${jobDetails.id}/quickbooks-invoiced`, {}, {
        value: !jobDetails.quickbooks_invoiced
      });

      if (response?.error) {
        setShowError(response.message || 'Failed to update job');
      } else {
        setJobDetails(prev => ({ ...prev, quickbooks_invoiced: !prev.quickbooks_invoiced }));
        dispatch(updateJob({ ...jobDetails, quickbooks_invoiced: !jobDetails.quickbooks_invoiced }));
        setShowSuccessToast(true);
      }
    } catch (error) {
      setShowError(error.message || 'Failed to update job');
    } finally {
      setLoadingQuickbooksInvoiced(false);
    }
  };

  // Handler for toggling factored
  const handleToggleFactored = async () => {
    if (!jobDetails || !canEditFinancialToggles()) return;
    
    setLoadingFactored(true);
    try {
      const response = await PATCH(`${api.updateFactored}/${jobDetails.id}/factored`, {}, {
        value: !jobDetails.factored
      });

      if (response?.error) {
        setShowError(response.message || 'Failed to update job');
      } else {
        setJobDetails(prev => ({ ...prev, factored: !prev.factored }));
        dispatch(updateJob({ ...jobDetails, factored: !jobDetails.factored }));
        setShowSuccessToast(true);
      }
    } catch (error) {
      setShowError(error.message || 'Failed to update job');
    } finally {
      setLoadingFactored(false);
    }
  };

  // Handler for toggling paid (sets paid_date when true, clears when false)
  const handleTogglePaid = async () => {
    if (!jobDetails || !canEditFinancialToggles()) return;
    
    setLoadingPaid(true);
    try {
      const newValue = !jobDetails.paid;
      const response = await PATCH(`${api.updatePaid}/${jobDetails.id}/paid`, {}, {
        value: newValue
      });

      if (response?.error) {
        setShowError(response.message || 'Failed to update job');
      } else {
        const paidDate = newValue ? (response.data?.paid_date || new Date().toISOString()) : null;
        setJobDetails(prev => ({
          ...prev,
          paid: newValue,
          paid_date: paidDate
        }));
        dispatch(updateJob({
          ...jobDetails,
          paid: newValue,
          paid_date: paidDate
        }));
        setShowSuccessToast(true);
      }
    } catch (error) {
      setShowError(error.message || 'Failed to update job');
    } finally {
      setLoadingPaid(false);
    }
  };

  const handleToggleSentToClient = async () => {
    if (!jobDetails || !canEditSentToClientToggle()) return;

    setLoadingSentToClient(true);
    try {
      const response = await PATCH(`${api.updateSentToClient}/${jobDetails.id}/sent-to-client`, {}, {
        value: !jobDetails.sent_to_client
      });

      if (response?.error) {
        setShowError(response.message || 'Failed to update job');
      } else {
        setJobDetails(prev => ({ ...prev, sent_to_client: !prev.sent_to_client }));
        dispatch(updateJob({ ...jobDetails, sent_to_client: !jobDetails.sent_to_client }));
        setShowSuccessToast(true);
      }
    } catch (error) {
      setShowError(error.message || 'Failed to update job');
    } finally {
      setLoadingSentToClient(false);
    }
  };

  // Check if any proposal (other than the specified one) is already invoiced for this job
  const hasInvoicedProposal = (excludeProposalId = null) => {
    if (!proposals || proposals.length === 0) return false;
    return proposals.some(p => p.invoiced && p.id !== excludeProposalId);
  };

  const getInvoiceProposalDisabledReason = (proposalId) => {
    if (hasInvoicedProposal(proposalId)) {
      return 'Only one proposal can be invoiced per work order';
    }

    return invoiceTechnicianBlockReason;
  };

  const canSetJobStatusToInvoiced = () => canSetJobStatusToInvoicedRule(proposals);

  const jobStatusEditOptions = useMemo(() => {
    const current = Number(editJob?.status);
    const restrictDispatcherStatuses = isOnlyDispatcher(user);
    return getStatusOptions().filter((option) => {
      if (restrictDispatcherStatuses && option.value == 12) return false;
      if (option.value == 13 || option.value == 15) {
        return current == option.value;
      }
      return true;
    });
  }, [user, editJob?.status]);

  const cancelConfirmNteAlreadyZero = Number(editJob?.nte) === 0;

  const NO_TECHNICIAN_WARN_STATUS_IDS = [
    JOB_STATUSES.DONE.id,
    JOB_STATUSES.READY_FOR_INVOICING.id,
    JOB_STATUSES.INVOICED.id,
    JOB_STATUSES.CANCELLED.id,
    JOB_STATUSES.RECALL_NO_CHARGE.id,
    JOB_STATUSES.INVOICED_LOSS.id,
  ];

  const jobHasNoTechnicianAssigned = () => {
    const techniciansToCheck = jobDetails?.technicians || [];
    return techniciansToCheck.length === 0;
  };

  const handleStatusChange = (value) => {
    const nextStatus = value?.value || 1;
    // Soft-warn first when closing out with no tech (incl. Cancelled / Recall / Loss),
    // then NTE-zeroing confirm if applicable.
    if (
      NO_TECHNICIAN_WARN_STATUS_IDS.includes(Number(nextStatus)) &&
      Number(editJob?.status) !== Number(nextStatus) &&
      jobHasNoTechnicianAssigned()
    ) {
      setPendingNoTechnicianAction({ type: 'status', status: nextStatus });
      setShowNoTechnicianConfirm(true);
      return;
    }
    if (isNteZeroingStatus(nextStatus)) {
      setPendingCancelStatus(nextStatus);
      setShowCancelNteConfirm(true);
      return;
    }
    setEditJob({ ...editJob, status: nextStatus });
  };

  const handleConfirmCancelStatus = () => {
    setEditJob({ ...editJob, status: pendingCancelStatus, nte: 0 });
    setShowCancelNteConfirm(false);
    setPendingCancelStatus(null);
  };

  const handleDiscardNoTechnicianAction = () => {
    setPendingNoTechnicianAction(null);
    setShowNoTechnicianConfirm(false);
  };

  const getPreservedInvoiceNumber = (proposalId) => {
    const proposal = proposals?.find((p) => p.id === proposalId);
    const trimmed = proposal?.invoice_number ? String(proposal.invoice_number).trim() : '';
    return trimmed || null;
  };

  const performInvoiceProposal = async (proposalId, options = {}) => {
    const invoicedAt = options.invoicedAt ?? nowAsStoredWallClock();
    const forceNewInvoiceNumber = options.forceNewInvoiceNumber === true;
    setLoadingProposals(prev => ({ ...prev, [`invoice_${proposalId}`]: true }));

    try {
      const body = { invoiced_at: invoicedAt };
      if (forceNewInvoiceNumber) {
        body.force_new_invoice_number = true;
      }
      const { status, message, error } = await PATCH(
        `${api.invoiceProposal}/${selectedJob.id}/proposals/${proposalId}/invoice`,
        {},
        body
      );

      if (!error && status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
        return true;
      }
      setShowError(message || 'Failed to invoice proposal');
      return false;
    } catch (err) {
      setShowError('Failed to invoice proposal');
      return false;
    } finally {
      setEditMode(false);
      setLoadingProposals(prev => ({ ...prev, [`invoice_${proposalId}`]: false }));
    }
  };

  const promptInvoiceDateOrInvoice = (proposalId) => {
    const preservedNumber = getPreservedInvoiceNumber(proposalId);
    if (isAccounting(user) && (latestPreservedInvoiceDate || preservedNumber)) {
      setPendingInvoiceProposalId(proposalId);
      setShowInvoiceDateDialog(true);
      return;
    }
    performInvoiceProposal(proposalId);
  };

  const handleDiscardInvoiceDateChoice = () => {
    if (pendingInvoiceProposalId && loadingProposals[`invoice_${pendingInvoiceProposalId}`]) return;
    setShowInvoiceDateDialog(false);
    setPendingInvoiceProposalId(null);
  };

  const handleConfirmInvoiceDateChoice = async ({ invoicedAt, forceNewInvoiceNumber } = {}) => {
    const proposalId = pendingInvoiceProposalId;
    if (!proposalId) return;
    const ok = await performInvoiceProposal(proposalId, {
      invoicedAt: invoicedAt ?? nowAsStoredWallClock(),
      forceNewInvoiceNumber,
    });
    if (ok) {
      setShowInvoiceDateDialog(false);
      setPendingInvoiceProposalId(null);
    }
  };

  const handleConfirmNoTechnicianAction = () => {
    const action = pendingNoTechnicianAction;
    setPendingNoTechnicianAction(null);
    setShowNoTechnicianConfirm(false);

    if (!action) return;

    if (action.type === 'status' && action.status != null) {
      if (isNteZeroingStatus(action.status)) {
        setPendingCancelStatus(action.status);
        setShowCancelNteConfirm(true);
        return;
      }
      setEditJob({ ...editJob, status: action.status });
      return;
    }

    if (action.type === 'invoice' && action.proposalId) {
      promptInvoiceDateOrInvoice(action.proposalId);
    }
  };

  // ============================================
  // Parts Permission Functions
  // ============================================

  const canCreatePart = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    return canCreatePartPermission(user, jobDetails);
  };

  const canEditPart = (part) => {
    if (!jobDetails || !part) return false;
    if (isJobLocked) return false;
    return canEditPartPermission(user, part, jobDetails);
  };

  const canDeletePart = (part) => {
    if (!part) return false;
    if (isJobLocked) return false;
    // Locked parts cannot be deleted
    if (part.is_locked) return false;
    return canDeletePartPermission(user, part);
  };

  const canApprovePart = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    if (isOwner(user)) return true;
    if (isAccountant(user)) return true;
    return false;
  };

  const canLockPart = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    if (isOwner(user)) return true;
    if (isAccountant(user)) return true;
    return false;
  };

  const canRejectPart = () => {
    if (!jobDetails) return false;
    if (isJobLocked) return false;
    if (isOwner(user)) return true;
    if (isAccountant(user)) return true;
    return false;
  };

  const canRequestPartApproval = (part) => {
    if (!jobDetails || !part) return false;
    if (isJobLocked) return false;
    return canRequestPartApprovalPermission(user, part, jobDetails);
  };

  const canRequestPartUnlock = (part) => {
    if (!jobDetails || !part) return false;
    if (isJobLocked) return false;
    if (!part.is_locked) return false;
    return canRequestPartUnlockPermission(user, part, jobDetails);
  };

  // ============================================
  // Parts Validation
  // ============================================

  const validatePartForm = (form) => {
    const errors = {};
    if (!form.name || form.name.trim().length < 1) {
      errors.name = 'Part name is required';
    }
    if (form.name && form.name.length > 255) {
      errors.name = 'Part name must not exceed 255 characters';
    }
    if (form.cost !== '' && form.cost !== null && form.cost !== undefined) {
      const costNum = parseFloat(form.cost);
      if (isNaN(costNum) || costNum < 0) {
        errors.cost = 'Cost must be a positive number';
      }
    }
    if (form.order_status && (form.order_status < 1 || form.order_status > 3)) {
      errors.order_status = 'Order status must be 1 (Pending), 2 (Ordered), or 3 (Delivered)';
    }
    return errors;
  };

  // Validate proposal form
  const validateProposalForm = (form) => {
    const errors = {};
    
    // Validate proposal text
    if (!form.proposal_text || form.proposal_text.trim().length < 10) {
      errors.proposal_text = 'Proposal text must be at least 10 characters';
    }
    if (form.proposal_text && form.proposal_text.length > 10000) {
      errors.proposal_text = 'Proposal text must not exceed 10000 characters';
    }
    
    // Validate items array
    if (!form.items || !Array.isArray(form.items) || form.items.length === 0) {
      errors.items = 'Proposal must have at least one item';
      return errors; // Return early if no items
    }
    
    // Validate each item
    form.items.forEach((item, index) => {
      const itemErrors = {};
      
      // Validate type
      const typeStr = typeof item.type === 'string' ? item.type.trim() : '';
      if (!typeStr) {
        itemErrors.type = 'Item type is required';
      } else if (typeStr.length > 100) {
        itemErrors.type = 'Item type must be 100 characters or less';
      }
      
      // Validate quantity (non-negative only)
      const quantity = parseFloat(item.quantity);
      if (isNaN(quantity) || quantity < 0) {
        itemErrors.quantity = 'Quantity must be a non-negative number';
      }
      
      // Validate unit_price (negative rates allowed, e.g. credits/discounts)
      const unitPrice = parseFloat(item.unit_price);
      if (isNaN(unitPrice)) {
        itemErrors.unit_price = 'Rate must be a valid number';
      }
      
      // Validate description (optional, but if provided, check length)
      if (item.description && item.description.length > 1000) {
        itemErrors.description = 'Description must not exceed 1000 characters';
      }
      
      if (Object.keys(itemErrors).length > 0) {
        if (!errors.items) errors.items = {};
        errors.items[index] = itemErrors;
      }
    });
    
    return errors;
  };

  const beginEditingProposal = (proposal) => {
    if (!proposal?.items || proposal.items.length === 0) {
      const legacyItem = {
        type: proposal.type || 'labor',
        description: proposal.proposal_text || '',
        quantity: proposal.quantity || 1,
        unit_price: proposal.unit_price || 0,
      };
      setProposals((current) => current.map((p) =>
        p.id === proposal.id ? { ...p, items: [legacyItem] } : p
      ));
    }
    setEditingProposal(proposal.id);
  };

  const invoicedEditNeedsWarn = () =>
    !!(jobDetails?.quickbooks_invoiced || jobDetails?.sent_to_client);

  const requestEditProposal = (proposal) => {
    if (proposal.invoiced && invoicedEditNeedsWarn()) {
      setPendingInvoicedEditProposal(proposal);
      setShowInvoicedEditWarn(true);
      return;
    }
    beginEditingProposal(proposal);
  };

  const handleConfirmInvoicedEdit = () => {
    const proposal = pendingInvoicedEditProposal;
    setShowInvoicedEditWarn(false);
    setPendingInvoicedEditProposal(null);
    if (proposal) beginEditingProposal(proposal);
  };

  const handleDiscardInvoicedEdit = () => {
    setShowInvoicedEditWarn(false);
    setPendingInvoicedEditProposal(null);
  };

  const getInvoicedEditWarningText = () => {
    const qb = !!jobDetails?.quickbooks_invoiced;
    const sent = !!jobDetails?.sent_to_client;
    const flags = [
      qb ? 'marked as QuickBooks invoiced' : null,
      sent ? 'marked as sent to the client' : null,
    ].filter(Boolean).join(' and ');
    return `This job is already ${flags}. Saving changes will update the proposal, NTE, and Invoiced / Invoiced/Loss status in ServiceGeek. It will not update QuickBooks or any invoice already sent.`;
  };

  // Create proposal
  const handleCreateProposal = async (requestApproval = false) => {
    const errors = validateProposalForm(proposalForm);
    if (Object.keys(errors).length > 0) {
      setProposalErrors(errors);
      return;
    }

    if (proposalForm.client_approval) {
      const blockReason = getClientApprovalBlockReason();
      if (blockReason) {
        setShowError(blockReason);
        return;
      }
    }

    setLoadingProposals(prev => ({ ...prev, creating: true }));
    setProposalErrors({});

    // Prepare items array with calculated prices
    const items = proposalForm.items.map(item => ({
      type: (item.type || 'labor').toString().trim().slice(0, 100),
      description: item.description || null,
      quantity: parseFloat(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
    }));

    try {
      const { status, message } = await POST(
        `${api.getJobById}/${selectedJob.id}/proposals`,
        {},
        {
          proposal_text: proposalForm.proposal_text,
          items: items,
          is_approved: (isAdmin(user) || isManagement(user)) ? true : proposalForm.is_approved,
          client_approval: proposalForm.client_approval,
          // Dual-role (admin+dispatcher): never request approval when auto-approving
          request_approval: requestApproval && !(isAdmin(user) || isManagement(user)),
        }
      );

      if (status === 201) {
        // Refresh job details to get updated proposals
        await fetchJobDetails(selectedJob.id);
        setProposalForm(getEmptyProposalForm());
        setCreatingProposal(false);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to create proposal');
      }
    } catch (err) {
      setShowError('Failed to create proposal');
    } finally {
      setLoadingProposals(prev => ({ ...prev, creating: false }));
    }
  };

  // Update proposal
  const handleUpdateProposal = async (proposalId) => {
    const proposal = proposals.find(p => p.id === proposalId);
    if (!proposal) return;

    const errors = validateProposalForm(proposal);
    if (Object.keys(errors).length > 0) {
      setProposalErrors({ [proposalId]: errors });
      return;
    }

    if (proposal.client_approval) {
      const blockReason = getClientApprovalBlockReason(proposal);
      if (blockReason) {
        setShowError(blockReason);
        return;
      }
    }

    setLoadingProposals(prev => ({ ...prev, [proposalId]: true }));
    setProposalErrors({});

    // Prepare items array with calculated prices
    const items = proposal.items.map(item => ({
      type: (item.type || 'labor').toString().trim().slice(0, 100),
      description: item.description || null,
      quantity: parseFloat(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
    }));

    try {
      const { status, message } = await PUT(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}`,
        {},
        {
          proposal_text: proposal.proposal_text,
          items: items,
          is_approved: proposal.is_approved,
          client_approval: proposal.client_approval
        }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setEditingProposal(null);
        setShowSuccessToast(true);
        if (proposal.invoiced && canAccessAccounting(user)) {
          dispatch(fetchAccountingTabCounts());
        }
      } else {
        setShowError(message || 'Failed to update proposal');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to update proposal');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [proposalId]: false }));
    }
  };

  // Delete proposal
  const handleDeleteProposal = async () => {
    if (!proposalToDelete) return;

    setLoadingProposals(prev => ({ ...prev, [proposalToDelete.id]: true }));

    try {
      const { status, message } = await DELETE(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalToDelete.id}`
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowDeleteProposalConfirm(false);
        setProposalToDelete(null);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to delete proposal');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to delete proposal');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [proposalToDelete.id]: false }));
    }
  };

  // Toggle approval
  const handleToggleApproval = async (proposalId, isApproved) => {
    setLoadingProposals(prev => ({ ...prev, [`approve_${proposalId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/approve`,
        {},
        { is_approved: isApproved }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        // When approving, remove from pending proposals in Redux store (clears request)
        if (isApproved) {
          dispatch(removePendingProposal({ jobId: selectedJob.id, proposalId }));
        }
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to update approval status');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to update approval status');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`approve_${proposalId}`]: false }));
    }
  };

  // Toggle client approval
  const handleToggleClientApproval = async (proposalId, clientApproval) => {
    if (clientApproval) {
      const blockReason = getClientApprovalBlockReason({ id: proposalId });
      if (blockReason) {
        setShowError(blockReason);
        return;
      }
    }

    setLoadingProposals(prev => ({ ...prev, [`client_approve_${proposalId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/client-approve`,
        {},
        { is_approved: clientApproval }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to update client approval status');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to update client approval status');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`client_approve_${proposalId}`]: false }));
    }
  };

  // Lock proposal
  const handleLockProposal = async (proposalId) => {
    setLoadingProposals(prev => ({ ...prev, [`lock_${proposalId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/lock`
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to lock proposal');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to lock proposal');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`lock_${proposalId}`]: false }));
    }
  };

  // Unlock proposal
  const handleUnlockProposal = async (proposalId) => {
    setLoadingProposals(prev => ({ ...prev, [`unlock_${proposalId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/unlock`
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        // Remove from pending proposals in Redux store (unlock request fulfilled)
        dispatch(removePendingProposal({ jobId: selectedJob.id, proposalId }));
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to unlock proposal');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to unlock proposal');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`unlock_${proposalId}`]: false }));
    }
  };

  // Request approval
  const handleRequestApproval = async (proposalId) => {
    setLoadingProposals(prev => ({ ...prev, [`request_approval_${proposalId}`]: true }));

    try {
      const { status, message } = await POST(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/request-approval`
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to request approval');
      }
    } catch (err) {
      setShowError('Failed to request approval');
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`request_approval_${proposalId}`]: false }));
    }
  };

  // Request unlock
  const handleRequestUnlock = async (proposalId) => {
    setLoadingProposals(prev => ({ ...prev, [`request_unlock_${proposalId}`]: true }));

    try {
      const { status, message } = await POST(
        `${api.getJobById}/${selectedJob.id}/proposals/${proposalId}/request-unlock`
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to request unlock');
      }
    } catch (err) {
      setShowError('Failed to request unlock');
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`request_unlock_${proposalId}`]: false }));
    }
  };

  // Reject proposal
  const handleRejectProposal = async () => {
    if (!rejectingProposal) return;

    // Validate: if notes are empty, checkbox must be checked
    const notesEmpty = !rejectionForm.notes || rejectionForm.notes.trim().length === 0;
    if (notesEmpty && !rejectionForm.rejectWithoutNote) {
      setRejectionErrors({
        notes: 'Please provide rejection notes or check "Reject without a note"',
        rejectWithoutNote: 'Please check this box if not providing notes'
      });
      return;
    }

    setLoadingProposals(prev => ({ ...prev, [`reject_${rejectingProposal}`]: true }));
    setRejectionErrors({});

    try {
      const { status, message } = await POST(
        `${api.getJobById}/${selectedJob.id}/proposals/${rejectingProposal}/reject`,
        {},
        {
          rejection_notes: rejectionForm.notes?.trim() || null
        }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setRejectingProposal(null);
        setRejectionForm({ notes: '', rejectWithoutNote: false });
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to reject proposal');
        // Refetch to update UI in case proposal was invoiced
        await fetchJobDetails(selectedJob.id);
      }
    } catch (err) {
      setShowError('Failed to reject proposal');
      // Refetch to update UI in case proposal was invoiced
      await fetchJobDetails(selectedJob.id);
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`reject_${rejectingProposal}`]: false }));
    }
  };

  // Invoice proposal
  const handleInvoiceProposal = async (proposalId) => {
    const disabledReason = getInvoiceProposalDisabledReason(proposalId);

    if (disabledReason) {
      setShowError(disabledReason);
      return;
    }

    if (jobHasNoTechnicianAssigned()) {
      setPendingNoTechnicianAction({ type: 'invoice', proposalId });
      setShowNoTechnicianConfirm(true);
      return;
    }

    promptInvoiceDateOrInvoice(proposalId);
  };

  // Modify invoice proposal
  const handleUninvoiceProposal = async (proposalId) => {
    setLoadingProposals(prev => ({ ...prev, [`uninvoice_${proposalId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.uninvoiceProposal}/${selectedJob.id}/proposals/${proposalId}/uninvoice`,
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to modify invoice');
      }
    } catch (err) {
      setShowError('Failed to modify invoice');
    } finally {
      setLoadingProposals(prev => ({ ...prev, [`uninvoice_${proposalId}`]: false }));
    }
  };

  // ===== END PROPOSAL FUNCTIONS =====

  // Helper function to check if a technician needs attention
  const technicianNeedsAttention = (tech) => {
    // Check scheduled_date field (works for both assignment objects and API technician objects)
    const scheduledDate = tech.scheduled_date;
    const hasNoScheduledDate = !scheduledDate || scheduledDate === '' || scheduledDate === null;
    
    // Also check for specific statuses: Request payment (5), No show (6), Reschedule (7), Need part (8)
    const status = tech.status;
    const hasSpecialStatus = status === 5 || status === 6 || status === 7 || status === 8;
    
    // Check if technician has requested unlock
    const hasUnlockRequest = tech.requested_unlock === true;
    
    return hasNoScheduledDate || hasSpecialStatus || hasUnlockRequest;
  };

  // Get count of technicians without scheduled visit date (for badge)
  const getTechniciansWithoutScheduledDateCount = () => {
    const techniciansToCheck = jobDetails?.technicians || [];
    
    if (!techniciansToCheck || techniciansToCheck.length === 0) return 0;
    
    return techniciansToCheck.filter(tech => technicianNeedsAttention(tech)).length;
  };

  // ============================================
  // Parts CRUD Functions
  // ============================================

  const handleCreatePart = async (requestApproval = false) => {
    const errors = validatePartForm(partForm);
    if (Object.keys(errors).length > 0) {
      setPartErrors(errors);
      return;
    }

    setLoadingParts(prev => ({ ...prev, creating: true }));
    setPartErrors({});

    try {
      const requestBody = {
        name: partForm.name,
        description: partForm.description || '',
        url: partForm.url || '',
        cost: parseFloat(partForm.cost) || 0,
        order_status: partForm.order_status || 1,
        technician_id: partForm.technician_id || null,
        notes: partForm.notes || ''
      };

      if (requestApproval) {
        requestBody.request_approval = true;
      }

      const { status, message } = await POST(
        `${api.createPart}/${selectedJob.id}/parts`,
        {},
        requestBody
      );

      if (status === 201) {
        await fetchJobDetails(selectedJob.id);
        setPartForm({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
        setCreatingPart(false);
        setPartErrors({});
        setShowSuccessToast(true);
      } else {
        setPartErrors({ generalError: message || 'Failed to create part' });
      }
    } catch (err) {
      setPartErrors({ generalError: 'Failed to create part' });
    } finally {
      setLoadingParts(prev => ({ ...prev, creating: false }));
    }
  };

  const handleUpdatePart = async (partId) => {
    const partData = parts.find(p => p.id === partId);
    if (!partData) return;

    const errors = validatePartForm(partData);
    if (Object.keys(errors).length > 0) {
      setPartErrors(prev => ({ ...prev, [partId]: errors }));
      return;
    }

    setLoadingParts(prev => ({ ...prev, [partId]: true }));
    setPartErrors(prev => ({ ...prev, [partId]: {} }));

    try {
      const { status, message } = await PUT(
        `${api.updatePart}/${selectedJob.id}/parts/${partId}`,
        {},
        {
          name: partData.name,
          description: partData.description || '',
          url: partData.url || '',
          cost: parseFloat(partData.cost) || 0,
          order_status: partData.order_status || 1,
          technician_id: partData.technician_id || null,
          notes: partData.notes || ''
        }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setEditingPart(null);
        setShowSuccessToast(true);
      } else {
        setPartErrors(prev => ({ ...prev, [partId]: { generalError: message || 'Failed to update part' } }));
      }
    } catch (err) {
      setPartErrors(prev => ({ ...prev, [partId]: { generalError: 'Failed to update part' } }));
    } finally {
      setLoadingParts(prev => ({ ...prev, [partId]: false }));
    }
  };

  const handleDeletePart = async () => {
    if (!partToDelete) return;

    setLoadingParts(prev => ({ ...prev, [partToDelete]: true }));

    try {
      const { status, message } = await DELETE(
        `${api.deletePart}/${selectedJob.id}/parts/${partToDelete}`,
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowDeletePartConfirm(false);
        setPartToDelete(null);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to delete part');
      }
    } catch (err) {
      setShowError('Failed to delete part');
    } finally {
      setLoadingParts(prev => ({ ...prev, [partToDelete]: false }));
    }
  };

  const handleTogglePartApproval = async (partId, isApproved) => {
    setLoadingParts(prev => ({ ...prev, [`approve_${partId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.approvePart}/${selectedJob.id}/parts/${partId}/approve`,
        {},
        { is_approved: isApproved }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to toggle part approval');
      }
    } catch (err) {
      setShowError('Failed to toggle part approval');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`approve_${partId}`]: false }));
    }
  };

  const handleLockPart = async (partId) => {
    setLoadingParts(prev => ({ ...prev, [`lock_${partId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.lockPart}/${selectedJob.id}/parts/${partId}/lock`,
        {},
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to lock part');
      }
    } catch (err) {
      setShowError('Failed to lock part');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`lock_${partId}`]: false }));
    }
  };

  const handleUnlockPart = async (partId) => {
    setLoadingParts(prev => ({ ...prev, [`unlock_${partId}`]: true }));

    try {
      const { status, message } = await PATCH(
        `${api.unlockPart}/${selectedJob.id}/parts/${partId}/unlock`,
        {},
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to unlock part');
      }
    } catch (err) {
      setShowError('Failed to unlock part');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`unlock_${partId}`]: false }));
    }
  };

  const handleRequestPartApproval = async (partId) => {
    setLoadingParts(prev => ({ ...prev, [`request_approval_${partId}`]: true }));

    try {
      const { status, message } = await POST(
        `${api.requestPartApproval}/${selectedJob.id}/parts/${partId}/request-approval`,
        {},
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to request part approval');
      }
    } catch (err) {
      setShowError('Failed to request part approval');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`request_approval_${partId}`]: false }));
    }
  };

  const handleRequestPartUnlock = async (partId) => {
    setLoadingParts(prev => ({ ...prev, [`request_unlock_${partId}`]: true }));

    try {
      const { status, message } = await POST(
        `${api.requestPartUnlock}/${selectedJob.id}/parts/${partId}/request-unlock`,
        {},
        {}
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to request part unlock');
      }
    } catch (err) {
      setShowError('Failed to request part unlock');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`request_unlock_${partId}`]: false }));
    }
  };

  const handleRejectPart = async () => {
    if (!rejectingPart) return;

    // Validate: if notes are empty, checkbox must be checked
    const notesEmpty = !partRejectionForm.notes || partRejectionForm.notes.trim().length === 0;
    if (notesEmpty && !partRejectionForm.rejectWithoutNote) {
      setPartRejectionErrors({
        notes: 'Please provide rejection notes or check "Reject without a note"',
        rejectWithoutNote: 'Please check this box if not providing notes'
      });
      return;
    }

    setLoadingParts(prev => ({ ...prev, [`reject_${rejectingPart}`]: true }));
    setPartRejectionErrors({});

    try {
      const { status, message } = await POST(
        `${api.rejectPart}/${selectedJob.id}/parts/${rejectingPart}/reject`,
        {},
        {
          rejection_notes: partRejectionForm.notes?.trim() || null
        }
      );

      if (status === 200) {
        await fetchJobDetails(selectedJob.id);
        setRejectingPart(null);
        setPartRejectionForm({ notes: '', rejectWithoutNote: false });
        setShowSuccessToast(true);
      } else {
        setShowError(message || 'Failed to reject part');
      }
    } catch (err) {
      setShowError('Failed to reject part');
    } finally {
      setLoadingParts(prev => ({ ...prev, [`reject_${rejectingPart}`]: false }));
    }
  };

  // Removed local getStatusLabel - now using imported function from jobStatuses.js
  // getPriorityLabel imported from jobPriorities.js

  // Cost calculation helpers
  const calculateTechniciansCost = () => {
    if (!jobDetails?.technicians || jobDetails.technicians.length === 0) return 0;
    return jobDetails.technicians.reduce((sum, tech) => sum + (parseFloat(tech.tech_cost) || 0), 0);
  };

  const calculatePartsCost = () => {
    if (!jobDetails?.parts || jobDetails.parts.length === 0) return 0;
    return jobDetails.parts.reduce((sum, part) => sum + (parseFloat(part.cost) || 0), 0);
  };

  const calculateTotalCost = () => {
    return calculateTechniciansCost() + calculatePartsCost();
  };

  const calculateProfit = () => {
    const nte = parseFloat(jobDetails?.nte || editJob?.nte || 0);
    return nte - calculateTotalCost();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Part Attachment handlers
  const handlePartFileSelect = (partId, event) => {
    const files = Array.from(event.target.files);
    uploadPartFiles(partId, files);
  };

  const uploadPartFiles = async (partId, files) => {
    if (!files || files.length === 0) return;

    // Clear any previous errors
    setPartAttachmentErrors(prev => {
      const newState = { ...prev };
      delete newState[partId];
      return newState;
    });

    const fileIds = {};
    files.forEach((file, index) => {
      const fileId = `${Date.now()}_${index}`;
      fileIds[fileId] = { file, progress: 0 };
    });

    setUploadingPartFiles(prev => ({
      ...prev,
      [partId]: { ...prev[partId], ...fileIds }
    }));

    for (const [fileId, { file }] of Object.entries(fileIds)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'part');
        formData.append('entityId', partId);

        const { data, status, message } = await POST(
          api.uploadAttachment,
          {},
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadingPartFiles(prev => ({
                ...prev,
                [partId]: {
                  ...prev[partId],
                  [fileId]: { ...prev[partId]?.[fileId], progress }
                }
              }));
            }
          }
        );

        if (status === 200 || status === 201) {
          // Add the new attachment to the part's attachments
          // Extract the attachment from the nested response structure
          const attachment = data?.data?.attachment;
          if (attachment) {
            setPartAttachments(prev => ({
              ...prev,
              [partId]: [...(prev[partId] || []), attachment]
            }));
          }

          // Remove from uploading state
          setUploadingPartFiles(prev => {
            const newState = { ...prev };
            if (newState[partId]) {
              delete newState[partId][fileId];
              if (Object.keys(newState[partId]).length === 0) {
                delete newState[partId];
              }
            }
            return newState;
          });
        } else {
          console.error('Upload failed:', message);
          setPartAttachmentErrors(prev => ({
            ...prev,
            [partId]: message || 'Failed to upload file'
          }));
          
          // Remove from uploading state
          setUploadingPartFiles(prev => {
            const newState = { ...prev };
            if (newState[partId]) {
              delete newState[partId][fileId];
              if (Object.keys(newState[partId]).length === 0) {
                delete newState[partId];
              }
            }
            return newState;
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
        setPartAttachmentErrors(prev => ({
          ...prev,
          [partId]: err.response?.data?.message || 'Failed to upload file'
        }));
        
        // Remove from uploading state
        setUploadingPartFiles(prev => {
          const newState = { ...prev };
          if (newState[partId]) {
            delete newState[partId][fileId];
            if (Object.keys(newState[partId]).length === 0) {
              delete newState[partId];
            }
          }
          return newState;
        });
      }
    }
  };

  const handleDeletePartAttachment = async (partId, attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    // Clear any previous errors
    setPartAttachmentErrors(prev => {
      const newState = { ...prev };
      delete newState[partId];
      return newState;
    });

    setDeletingPartAttachments(prev => ({ ...prev, [attachmentId]: true }));

    try {
      const { status, message } = await DELETE(`${api.deleteAttachment}/${attachmentId}`, {});

      if (status === 200) {
        // Remove from part attachments
        setPartAttachments(prev => ({
          ...prev,
          [partId]: (prev[partId] || []).filter(att => att.id !== attachmentId)
        }));
        setShowSuccessToast(true);
      } else {
        setPartAttachmentErrors(prev => ({
          ...prev,
          [partId]: message || 'Failed to delete attachment'
        }));
      }
    } catch (err) {
      console.error('Delete error:', err);
      setPartAttachmentErrors(prev => ({
        ...prev,
        [partId]: err.response?.data?.message || 'Failed to delete attachment'
      }));
    } finally {
      setDeletingPartAttachments(prev => {
        const newState = { ...prev };
        delete newState[attachmentId];
        return newState;
      });
    }
  };

  const handlePartAttachmentPreview = async (partId, attachment) => {
    // Clear any previous errors
    setPartAttachmentErrors(prev => {
      const newState = { ...prev };
      delete newState[partId];
      return newState;
    });

    try {
      const { data, status } = await GET(`${api.getDownloadUrl}/${attachment.id}`);
      
      if (status === 200 && data.data?.downloadUrl) {
        setPreviewAttachment({
          src: data.data.downloadUrl,
          name: attachment.file_name,
          type: attachment.file_type,
          size: attachment.file_size,
          uploadedBy: `${attachment.first_name || ''} ${attachment.last_name || ''}`.trim() || attachment.email,
          uploadedAt: new Date(attachment.created_at).toLocaleString()
        });
        setImagePreviewOpen(true);
      } else {
        setPartAttachmentErrors(prev => ({
          ...prev,
          [partId]: 'Failed to load attachment preview'
        }));
      }
    } catch (err) {
      console.error('Preview error:', err);
      setPartAttachmentErrors(prev => ({
        ...prev,
        [partId]: err.response?.data?.message || 'Failed to load attachment preview'
      }));
    }
  };

  const handlePartAttachmentDownload = async (partId, attachment) => {
    try {
      const { data, status } = await GET(`${api.getDownloadUrl}/${attachment.id}`);
      
      if (status === 200 && data.data?.downloadUrl) {
        // Fetch the file as a blob to force download
        const response = await fetch(data.data.downloadUrl);
        const blob = await response.blob();
        
        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      } else {
        setPartAttachmentErrors(prev => ({
          ...prev,
          [partId]: 'Failed to download attachment'
        }));
      }
    } catch (err) {
      console.error('Download error:', err);
      setPartAttachmentErrors(prev => ({
        ...prev,
        [partId]: err.response?.data?.message || 'Failed to download attachment'
      }));
    }
  };

  // Attachment helper functions
  const getPresignedUrl = async (attachmentId, attachmentName) => {
    try {
      console.log('Getting presigned URL for attachment:', attachmentId, attachmentName);
      setLoadingAttachments(prev => ({ ...prev, [attachmentId]: true }));
      setDownloadProgress(prev => ({ ...prev, [attachmentId]: 0 }));
      
      const { data, status } = await GET(`${api.getAttachmentDownloadUrl}/${attachmentId}`);
      console.log('API response:', { data, status });
      
      setDownloadProgress(prev => ({ ...prev, [attachmentId]: 100 }));
      
      if (status === 200) {
        // Clear progress after short delay
        setTimeout(() => {
          setDownloadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[attachmentId];
            return newProgress;
          });
          setLoadingAttachments(prev => {
            const newLoading = { ...prev };
            delete newLoading[attachmentId];
            return newLoading;
          });
        }, 1000);
        
        const downloadUrl = data.data.downloadUrl;
        console.log('Download URL:', downloadUrl);
        return downloadUrl;
      }
      console.log('API call failed with status:', status);
      return null;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      setDownloadErrors(prev => ({
        ...prev,
        [attachmentId]: `Failed to load ${attachmentName}: ${error.message || 'Unknown error'}`
      }));
      setLoadingAttachments(prev => {
        const newLoading = { ...prev };
        delete newLoading[attachmentId];
        return newLoading;
      });
      return null;
    }
  };

  const getFileIcon = (fileType) => {
    // Safety check: return default icon if fileType is undefined or null
    if (!fileType) {
      return <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary' }} />;
    }
    
    if (fileType.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 40, color: '#4caf50' }} />;
    } else if (fileType.startsWith('video/')) {
      return <VideocamIcon sx={{ fontSize: 40, color: '#9c27b0' }} />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: '#dc3545' }} />;
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return <DescriptionIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return <TableChartIcon sx={{ fontSize: 40, color: '#28a745' }} />;
    } else if (fileType.startsWith('text/')) {
      return <TextSnippetIcon sx={{ fontSize: 40, color: '#6c757d' }} />;
    } else {
      return <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary' }} />;
    }
  };

  const handleAttachmentPreview = async (attachment) => {
    console.log('Preview clicked for attachment:', attachment);
    const presignedUrl = await getPresignedUrl(attachment.id, attachment.file_name);
    console.log('Preview presigned URL:', presignedUrl);
    
    if (presignedUrl) {
      setPreviewAttachment({
        src: presignedUrl,
        name: attachment.file_name,
        size: attachment.file_size,
        type: attachment.file_type,
        uploadedBy: `${attachment.first_name} ${attachment.last_name}`,
        uploadedAt: new Date(attachment.created_at).toLocaleDateString()
      });
      setImagePreviewOpen(true);
      console.log('Preview modal opened');
    } else {
      console.log('No presigned URL received for preview');
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    console.log('Download clicked for attachment:', attachment);
    const presignedUrl = await getPresignedUrl(attachment.id, attachment.file_name);
    console.log('Download presigned URL:', presignedUrl);
    
    if (presignedUrl) {
      try {
        // Fetch the file as a blob to force download
        const response = await fetch(presignedUrl);
        const blob = await response.blob();
        
        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
        console.log('Download completed');
      } catch (error) {
        console.error('Download error:', error);
      }
    } else {
      console.log('No presigned URL received for download');
    }
  };

  const getVisibleJobAttachments = () => filterJobAttachmentsForUser(attachments, user);

  const handleDownloadAllAttachments = async () => {
    const visibleAttachments = getVisibleJobAttachments();

    if (!visibleAttachments.length || downloadingAllAttachments) {
      return;
    }

    setDownloadingAllAttachments(true);
    try {
      const zip = new JSZip();
      const usedPaths = new Set();

      const sanitizePathPart = (value) =>
        String(value || 'file')
          .replace(/[\\/:*?"<>|]+/g, '_')
          .replace(/\s+/g, ' ')
          .trim() || 'file';

      const uniqueZipPath = (category, fileName) => {
        const folder = sanitizePathPart(getCategoryDisplayName(category) || category || 'Other');
        const safeName = sanitizePathPart(fileName);
        let candidate = `${folder}/${safeName}`;
        if (!usedPaths.has(candidate)) {
          usedPaths.add(candidate);
          return candidate;
        }

        const lastDot = safeName.lastIndexOf('.');
        const base = lastDot > 0 ? safeName.slice(0, lastDot) : safeName;
        const ext = lastDot > 0 ? safeName.slice(lastDot) : '';
        let i = 2;
        do {
          candidate = `${folder}/${base} (${i})${ext}`;
          i += 1;
        } while (usedPaths.has(candidate));
        usedPaths.add(candidate);
        return candidate;
      };

      // Assign zip paths up front so parallel fetches can't collide on duplicate names
      const attachmentsWithPaths = visibleAttachments.map((attachment) => ({
        attachment,
        zipPath: uniqueZipPath(attachment.category, attachment.file_name),
      }));

      const results = await Promise.allSettled(
        attachmentsWithPaths.map(async ({ attachment, zipPath }) => {
          const { data, status } = await GET(`${api.getAttachmentDownloadUrl}/${attachment.id}`);
          if (status !== 200 || !data?.data?.downloadUrl) {
            throw new Error(`Failed to get download URL for ${attachment.file_name}`);
          }
          const response = await fetch(data.data.downloadUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${attachment.file_name}`);
          }
          const blob = await response.blob();
          zip.file(zipPath, blob);
          return attachment.file_name;
        })
      );

      const failed = results.filter((r) => r.status === 'rejected');
      const succeeded = results.filter((r) => r.status === 'fulfilled');

      if (!succeeded.length) {
        setShowError('Failed to download attachments. Please try again.');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const workOrder = sanitizePathPart(jobDetails?.work_order || jobDetails?.id || 'job');
      const zipName = `job-${workOrder}-attachments.zip`;
      const blobUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      if (failed.length) {
        setShowError(`Downloaded ${succeeded.length} file(s), but ${failed.length} failed.`);
      }
    } catch (error) {
      console.error('Download all attachments error:', error);
      setShowError('Failed to download all attachments. Please try again.');
    } finally {
      setDownloadingAllAttachments(false);
    }
  };

  const retryDownload = async (attachmentId, attachmentName) => {
    setDownloadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[attachmentId];
      return newErrors;
    });
    const presignedUrl = await getPresignedUrl(attachmentId, attachmentName);
    if (presignedUrl) {
      try {
        // Fetch the file as a blob to force download
        const response = await fetch(presignedUrl);
        const blob = await response.blob();
        
        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachmentName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Retry download error:', error);
      }
    }
  };

  // Attachment modal handlers (multi-file)
  const handleOpenAttachmentModal = () => {
    setAttachmentModalOpen(true);
    setModalFiles([]);
    setModalErrors({});
    setModalDragOver(false);
  };

  const handleCloseAttachmentModal = () => {
    // Do not discard the staging list while an upload is running; cancel first.
    if (modalUploading) {
      modalUploadAbortRef.current?.abort();
      return;
    }
    modalFiles.forEach(mf => {
      if (mf.preview) URL.revokeObjectURL(mf.preview);
    });
    setAttachmentModalOpen(false);
    setModalFiles([]);
    setModalErrors({});
    setModalUploadProgress({});
    setModalDragOver(false);
  };

  const handleCancelModalUpload = () => {
    modalUploadAbortRef.current?.abort();
  };

  const addModalFiles = (files, category = 'Before') => {
    const incoming = Array.from(files);
    const rejected = [];
    const accepted = [];

    incoming.forEach(file => {
      const validationError = validateAttachmentFile(file);
      if (validationError) rejected.push(validationError);
      else accepted.push(file);
    });

    const newEntries = accepted.map(file => ({
      id: crypto.randomUUID(),
      file,
      fileName: file.name,
      category,
      is_private: false,
      preview: isPreviewableMedia(file.type) ? URL.createObjectURL(file) : null,
    }));
    setModalFiles(prev => [...prev, ...newEntries]);
    setModalErrors(prev => {
      const next = { ...prev };
      newEntries.forEach(e => delete next[e.id]);
      return next;
    });

    if (rejected.length) {
      setShowError(rejected.join('\n'));
    }
  };

  const handleModalFileSelect = (event) => {
    const files = event.target.files;
    if (files?.length) addModalFiles(files);
    event.target.value = '';
  };

  const updateModalFile = (id, updates) => {
    setModalFiles(prev => prev.map(f => (f.id === id ? { ...f, ...updates } : f)));
    setModalErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const removeModalFile = (id) => {
    setModalFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
      return prev.filter(f => f.id !== id);
    });
    setModalErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleModalUpload = async () => {
    const errors = {};
    modalFiles.forEach(mf => {
      if (!(mf.fileName ?? mf.file?.name ?? '').trim()) errors[mf.id] = 'Please enter a file name';
      else if (!mf.category || mf.category.trim() === '') errors[mf.id] = 'Please select a category';
    });
    if (Object.keys(errors).length > 0) {
      setModalErrors(errors);
      setShowError('Please fix the errors below for all files.');
      return;
    }
    const abortController = new AbortController();
    modalUploadAbortRef.current = abortController;
    setModalErrors({});
    setModalUploading(true);
    setModalUploadProgress({});
    setShowError('');
    let failed = 0;
    let succeeded = 0;
    let cancelled = false;
    const failedMessages = [];
    const succeededIds = [];
    try {
      for (const mf of modalFiles) {
        if (abortController.signal.aborted) {
          cancelled = true;
          break;
        }

        const displayName = (mf.fileName ?? mf.file.name ?? '').trim() || mf.file.name;
        const effectivePrivacy = isOnlyDispatcher(user) ? false : mf.is_private;

        // Videos exceed the API body limit, so they go straight to S3 with a
        // presigned POST instead of through /uploads/direct.
        if (isVideoFile(mf.file.type)) {
          const { error, message, cancelled: wasCancelled } = await uploadViaPresignedPost(
            mf.file,
            {
              entityType: 'job',
              entityId: selectedJob.id,
              fileName: displayName,
              category: mf.category,
              isPrivate: effectivePrivacy,
            },
            (percent) => setModalUploadProgress(prev => ({ ...prev, [mf.id]: percent })),
            { signal: abortController.signal },
          );

          if (wasCancelled || isUploadCancelledError({ message, cancelled: wasCancelled })) {
            cancelled = true;
            failed++;
            failedMessages.push(`${displayName}: Upload cancelled`);
            break;
          }

          if (error) {
            failed++;
            failedMessages.push(`${displayName}: ${message}`);
          } else {
            succeeded++;
            succeededIds.push(mf.id);
          }
          continue;
        }

        const fileToSend = displayName !== mf.file.name
          ? new File([mf.file], displayName, { type: mf.file.type, lastModified: mf.file.lastModified })
          : mf.file;
        const formData = new FormData();
        formData.append('file', fileToSend);
        formData.append('entityType', 'job');
        formData.append('entityId', selectedJob.id);
        formData.append('category', mf.category);
        formData.append('is_private', effectivePrivacy);
        const { status, error, message, data } = await UPLOAD(api.uploadJobFiles, formData);
        const uploadMessage =
          data?.errors?.[0]?.msg ||
          message ||
          data?.message ||
          'Failed to upload file';

        if (!error && (status === 200 || status === 201)) {
          succeeded++;
          succeededIds.push(mf.id);
        } else {
          failed++;
          failedMessages.push(`${displayName}: ${uploadMessage}`);
        }
      }

      // Drop files that already uploaded so a retry only retries the rest.
      if (succeededIds.length > 0) {
        setModalFiles(prev => {
          const remaining = [];
          prev.forEach(mf => {
            if (succeededIds.includes(mf.id)) {
              if (mf.preview) URL.revokeObjectURL(mf.preview);
            } else {
              remaining.push(mf);
            }
          });
          return remaining;
        });
        await fetchJobDetails(selectedJob.id);
      }

      if (succeeded > 0 && failed === 0 && !cancelled) {
        setShowSuccessToast(true);
        // Clear abort ref before close so handleCloseAttachmentModal does not re-abort.
        modalUploadAbortRef.current = null;
        setModalUploading(false);
        setModalUploadProgress({});
        handleCloseAttachmentModal();
        return;
      }

      if (cancelled) {
        setShowError(
          succeeded > 0
            ? `Upload cancelled. ${succeeded} file(s) uploaded; remaining files were not uploaded.`
            : 'Upload cancelled.'
        );
      } else if (failed > 0) {
        setShowError(
          failed === 1
            ? failedMessages[0]
            : `${failed} file(s) failed to upload:\n${failedMessages.join('\n')}`
        );
      }

      if (succeeded > 0 && failed > 0) {
        setShowSuccessToast(true);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setShowError(error?.response?.data?.message || `Upload error: ${error.message || 'Unknown error'}`);
    } finally {
      modalUploadAbortRef.current = null;
      setModalUploading(false);
      setModalUploadProgress({});
    }
  };

  // Attachment delete function
  const handleDeleteAttachment = async (attachmentId) => {
    setDeletingAttachments(prev => ({ ...prev, [attachmentId]: true }));
    
    try {
      const { status } = await DELETE(`${api.deleteFile}/${attachmentId}`);
      
      if (status === 200) {
        // Remove from local state
        setAttachments(prev => prev.filter(attachment => attachment.id !== attachmentId));
        setShowSuccessToast(true);
      } else {
        setShowError('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Delete attachment error:', error);
      setShowError('Failed to delete attachment');
    } finally {
      setDeletingAttachments(prev => {
        const newDeleting = { ...prev };
        delete newDeleting[attachmentId];
        return newDeleting;
      });
    }
  };

  const confirmDeleteAttachment = (attachment) => {
    setAttachmentToDelete(attachment);
    setShowDeleteAttachmentConfirm(true);
  };

  // Update attachment category
  const handleUpdateCategory = async (attachmentId, newCategory) => {
    if (isJobLocked && !canManageAttachmentsWhenLocked) {
      setShowError('Job is locked — attachment category cannot be changed until the job is unlocked');
      return;
    }

    setEditingCategory(prev => ({ ...prev, [attachmentId]: true }));
    
    try {
      const { status } = await PUT(`${api.updateAttachmentCategory}/${attachmentId}/category`, {}, { category: newCategory });
      
      if (status === 200) {
        // Update local state - also set is_private to true if category is quickbooks_invoice
        // (backend automatically sets this, but we need to reflect it in the UI)
        setAttachments(prev => prev.map(att => 
          att.id === attachmentId 
            ? { 
                ...att, 
                category: newCategory,
                ...(newCategory === 'quickbooks_invoice' ? { is_private: true } : {})
              } 
            : att
        ));
        setEditingCategory(prev => {
          const newState = { ...prev };
          delete newState[attachmentId];
          return newState;
        });
        setShowSuccessToast(true);
      } else {
        setShowError('Failed to update category');
      }
    } catch (error) {
      console.error('Update category error:', error);
      setShowError('Failed to update category');
      setEditingCategory(prev => {
        const newState = { ...prev };
        delete newState[attachmentId];
        return newState;
      });
    }
  };

  // Update attachment privacy
  const handleUpdatePrivacy = async (attachmentId, newPrivacy) => {
    try {
      const { status } = await PUT(`${api.updateAttachmentPrivacy}/${attachmentId}/privacy`, {}, { is_private: newPrivacy });
      
      if (status === 200) {
        // Update local state
        setAttachments(prev => prev.map(att => 
          att.id === attachmentId ? { ...att, is_private: newPrivacy } : att
        ));
        setShowSuccessToast(true);
      } else {
        setShowError('Failed to update privacy');
      }
    } catch (error) {
      console.error('Update privacy error:', error);
      setShowError('Failed to update privacy');
    }
  };

  // Attachment drag and drop handlers
  const hasDraggedMediaFiles = (dataTransfer) => {
    if (!dataTransfer) return false;

    if (dataTransfer.items?.length) {
      return Array.from(dataTransfer.items).some(
        (item) => item.kind === 'file' && isPreviewableMedia(item.type)
      );
    }

    if (dataTransfer.files?.length) {
      return Array.from(dataTransfer.files).some((file) => isPreviewableMedia(file.type));
    }

    return false;
  };

  const getDraggedMediaFiles = (dataTransfer) => {
    if (!dataTransfer?.files?.length) return [];
    return Array.from(dataTransfer.files).filter((file) => isPreviewableMedia(file.type));
  };

  const handleAttachmentTabDragOver = (e) => {
    if (hasDraggedMediaFiles(e.dataTransfer)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsExternalAttachmentDrag(true);
    }
  };

  const handleAttachmentTabDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsExternalAttachmentDrag(false);
      setDragOverCategory(null);
    }
  };

  const handleAttachmentDragStart = (e, attachment) => {
    // Don't allow drag if clicking on buttons or interactive elements
    if (e.target.closest('button') || e.target.closest('[role="button"]') || e.target.closest('a')) {
      e.preventDefault();
      return;
    }
    
    setDraggedAttachment(attachment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', attachment.id);
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleAttachmentDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedAttachment(null);
    setDragOverCategory(null);
  };

  const handleAttachmentDragOver = (e, category) => {
    const hasExternalMedia = hasDraggedMediaFiles(e.dataTransfer);
    if (hasExternalMedia) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsExternalAttachmentDrag(true);
      setDragOverCategory(category);
      setExpandedCategories(prev => ({
        ...prev,
        [category]: true
      }));
      return;
    }

    if (isJobLocked && !canManageAttachmentsWhenLocked) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedAttachment && draggedAttachment.category !== category) {
      setDragOverCategory(category);
      // Expand the category if it's collapsed
      setExpandedCategories(prev => ({
        ...prev,
        [category]: true
      }));
    }
  };

  const handleAttachmentDragLeave = (e) => {
    // Only clear if we're actually leaving the drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverCategory(null);
    }
  };

  const handleAttachmentDrop = async (e, targetCategory) => {
    e.preventDefault();
    e.stopPropagation();

    // Admins may view public QB files but cannot upload/re-categorize into QB.
    if (targetCategory === 'quickbooks_invoice' && !canAccessQuickbooksInvoice(user)) {
      setDragOverCategory(null);
      setIsExternalAttachmentDrag(false);
      setDraggedAttachment(null);
      return;
    }

    const droppedMediaFiles = getDraggedMediaFiles(e.dataTransfer);
    if (droppedMediaFiles.length > 0) {
      setAttachmentModalOpen(true);
      setModalErrors({});
      setModalDragOver(false);
      addModalFiles(droppedMediaFiles, targetCategory);
      setDragOverCategory(null);
      setIsExternalAttachmentDrag(false);
      setDraggedAttachment(null);
      return;
    }

    if ((isJobLocked && !canManageAttachmentsWhenLocked) || !draggedAttachment || draggedAttachment.category === targetCategory) {
      setDragOverCategory(null);
      setIsExternalAttachmentDrag(false);
      return;
    }

    // Update the category via API
    await handleUpdateCategory(draggedAttachment.id, targetCategory);
    
    setDragOverCategory(null);
    setIsExternalAttachmentDrag(false);
    setDraggedAttachment(null);
  };

  const handleConfirmDeleteAttachment = async () => {
    if (attachmentToDelete) {
      await handleDeleteAttachment(attachmentToDelete.id);
      setShowDeleteAttachmentConfirm(false);
      setAttachmentToDelete(null);
    }
  };

  if (!selectedJob) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={!!selectedJob}
        onClose={handleClose}
        fullScreen={isSmallScreen}
        PaperComponent={isSmallScreen ? undefined : PaperComponent}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          paper: {
            sx: {
              borderRadius: isSmallScreen ? 0 : 2,
              width: isSmallScreen ? '100%' : '80vw',
              height: isSmallScreen ? '100%' : '80vh',
              maxWidth: isSmallScreen ? '100%' : 'none',
              maxHeight: isSmallScreen ? '100%' : 'none',
              m: isSmallScreen ? 0 : undefined,
            }
          }
        }}
      >
      {/* Header */}
      <Box 
        id="draggable-dialog-title" 
        sx={{ 
          cursor: isSmallScreen ? 'default' : 'move',
          backgroundColor: 'background.paper',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 }, 
          pb: 2,
          gap: { xs: 1.5, md: 0 },
        }}>
          <Box sx={{ flex: 1 }}>
            {jobDetails && (
              <>
                {jobDetails.work_order ? (
                  <Tooltip title="Click to copy work order">
                    <Button
                      onClick={handleCopyWorkOrder}
                      onMouseDown={(e) => e.stopPropagation()}
                      endIcon={<ContentCopyIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />}
                      variant="outlined"
                      aria-label={`Copy work order ${jobDetails.work_order}`}
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: { xs: '1.25rem', md: '1.75rem' },
                        mb: 0.5,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        lineHeight: 1.2,
                        px: 1.5,
                        py: 0.5,
                        borderColor: isDarkMode ? '#475569' : '#c4c4c4',
                        backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: isDarkMode ? '#334155' : '#f0f0f0',
                        },
                      }}
                    >
                      {jobDetails.work_order}
                    </Button>
                  </Tooltip>
                ) : (
                  <Typography variant="h4" sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                    mb: 0.5,
                    fontFamily: 'sans-serif'
                  }}>
                    Untitled Work Order
                  </Typography>
                )}
                <Typography variant="body1" sx={{ 
                  fontWeight: 400, 
                  color: 'text.secondary',
                  fontSize: '0.95rem',
                  fontFamily: 'sans-serif'
                }}>
                  {jobDetails.client_name || 'No client'}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
            {/* Refresh button - visible when Edit Job button is visible */}
            <IconButton
              onClick={() => {
                if (selectedJob?.id) {
                  fetchJobDetails(selectedJob.id);
                }
              }}
              disabled={loadingAdditionalData}
              sx={{
                color: isDarkMode ? 'text.secondary' : 'text.primary',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#334155' : '#f5f5f5',
                },
                '&:disabled': {
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                }
              }}
              size="small"
            >
              {loadingAdditionalData ? (
                <CircularProgress size={20} sx={{ color: isDarkMode ? 'text.secondary' : 'text.primary' }} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>

            
            {/* Edit/Discard button - hidden on Parts (2) and Proposal (4) tabs */}
            {(canCreateOrEditJob(user) || isDispatcher(user)) && tabValue === 0 && !isJobLocked  && (
              <Button 
                variant="contained"
                startIcon={<EditIcon />}
                sx={{ 
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : 'white',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#333',
                    boxShadow: 'none',
                  }
                }} 
                onClick={() => {
                  if (editMode) {
                    setEditJob(null);
                    setShowCancelNteConfirm(false);
                    setPendingCancelStatus(null);

                    // Restore technician assignments from jobDetails
                    // Restore parts from jobDetails
                    if (jobDetails?.parts) {
                      const partsData = jobDetails.parts.map(part => ({
                        id: part.id,
                        name: part.name,
                        description: part.description || '',
                        url: part.url || '',
                        cost: part.cost || 0,
                        order_status: part.order_status || 1,
                        technician_id: part.technician_id || null,
                        technician_type: part.technician_type,
                        technician_first_name: part.technician_first_name,
                        technician_last_name: part.technician_last_name,
                        technician_company_name: part.technician_company_name,
                        notes: part.notes || '',
                        created_by: part.created_by || part.created_by_email || '',
                        created_by_email: part.created_by_email || part.created_by || '',
                        created_at: part.created_at,
                        updated_at: part.updated_at,
                        updated_by: part.updated_by,
                        is_approved: part.is_approved,
                        is_locked: part.is_locked,
                        approved_by: part.approved_by,
                        approved_at: part.approved_at,
                        locked_by: part.locked_by,
                        locked_at: part.locked_at,
                        rejected_by: part.rejected_by,
                        rejected_at: part.rejected_at,
                        rejection_notes: part.rejection_notes,
                        is_rejected: part.is_rejected,
                        requested_approval_at: part.requested_approval_at,
                        requested_unlock_at: part.requested_unlock_at
                      }));
                      setParts(partsData);
                    } else {
                      setParts([]);
                    }
                  } else {
                    // Clear errors when entering edit mode
                    setErrors({});
                    setShowError('');
                    // Initialize edit job data immediately when entering edit mode
                    if (jobDetails) {
                      setEditJob({
                        status: jobDetails.status,
                        priority: jobDetails.priority,
                        sla: toLocalDateTimeInputValue(jobDetails.sla),
                        description: jobDetails.description || '',
                        city: jobDetails.city || '',
                        state: jobDetails.state || '',
                        address: jobDetails.address || '',
                        address_2: jobDetails.address_2 || '',
                        zip_code: jobDetails.zip_code || '',
                        latitude: jobDetails.latitude || null,
                        longitude: jobDetails.longitude || null,
                        trade: jobDetails.trade || '',
                        store_name: jobDetails.store_name || '',
                        tags: jobDetails.tags || [],
                        nte: jobDetails.nte || 0,
                        work_order: jobDetails.work_order || '',
                        notes: jobDetails.notes || '',
                        admin_id: jobDetails.admin_id || null,
                        dispatcher_id: jobDetails.dispatcher_id || null,
                        client_id: jobDetails.client_id || null
                      });
                    }
                  }
                  setEditMode(!editMode);
                }}
              >
                {editMode ? 'Discard' : 'Edit Job'}
              </Button>
            )}
            
            {/* Delete button - blocked while job is locked */}
            {(isOwner(user) || isAdmin(user)) && !isJobLocked && (
              <Button 
                variant="text"
                startIcon={<DeleteIcon />}
                sx={{ 
                  color: '#d32f2f',
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 2,
                  py: 1.25,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                  }
                }} 
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
            )}
            
            <IconButton 
              onClick={handleClose} 
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Tabs - moved to header */}
        {jobDetails && (
          <Box sx={{ 
            display: 'inline-flex',
            px: { xs: 0.5, md: 1.5 }, 
            py: 0.75,
            ml: { xs: 1, md: 3 },
            mr: { xs: 1, md: 0 },
            maxWidth: '100%',
            overflowX: 'auto',
            backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5', 
            borderRadius: '8px 8px 0 0',
            boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="work order details tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 'auto',
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTabs-scrollButtons': {
                  color: 'text.secondary'
                },
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'text.primary'
                  }
                }
              }}
            >
              <Tab 
                label="Description" 
                {...a11yProps(0)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 0 ? 700 : 400,
                  color: tabValue === 0 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 0 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 0 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 0 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={getTechniciansWithoutScheduledDateCount()} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    Technicians
                  </Badge>
                } 
                {...a11yProps(1)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 1 ? 700 : 400,
                  color: tabValue === 1 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 1 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 1 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 1 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={parts?.length || 0} 
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    Parts
                  </Badge>
                } 
                {...a11yProps(2)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 2 ? 700 : 400,
                  color: tabValue === 2 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 2 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 2 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 2 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab 
                label={
                  <Badge
                    badgeContent={getVisibleJobAttachments().length}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                      },
                    }}
                  >
                    Attachments
                  </Badge>
                }
                {...a11yProps(3)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 3 ? 700 : 400,
                  color: tabValue === 3 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 3 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 3 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 3 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={proposals?.length || 0} 
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    Proposal
                  </Badge>
                } 
                {...a11yProps(4)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 4 ? 700 : 400,
                  color: tabValue === 4 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 4 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 4 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 4 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab 
                label="Invoice" 
                {...a11yProps(5)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 5 ? 700 : 400,
                  color: tabValue === 5 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 5 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 5 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 5 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab
                label={
                  <Badge
                    badgeContent={commentsCount}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                      },
                    }}
                  >
                    Comments
                  </Badge>
                }
                {...a11yProps(6)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 6 ? 700 : 400,
                  color: tabValue === 6 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 6 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 6 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 6 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
              <Tab
                label={
                  <Badge
                    badgeContent={activitiesCount}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                      },
                    }}
                  >
                    Activity
                  </Badge>
                }
                {...a11yProps(7)}
                sx={{
                  minHeight: 'auto',
                  py: 0.75,
                  px: 2,
                  mr: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: tabValue === 7 ? 700 : 400,
                  color: tabValue === 7 ? (isDarkMode ? 'text.primary' : '#000') : (isDarkMode ? 'text.secondary' : '#666'),
                  backgroundColor: tabValue === 7 ? (isDarkMode ? 'background.paper' : '#ffffff') : 'transparent',
                  border: tabValue === 7 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                  borderTop: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: tabValue === 7 ? (isDarkMode ? 'background.paper' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0')
                  }
                }}
              />
            </Tabs>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Error Message */}
        {showError && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{showError}</Alert>
          </Box>
        )}

        {/* Job Locked Banner -
            Auto-locks once the job moves to a terminal status (Recall No Charge /
            Invoiced / Cancelled / Invoiced/Loss).
            Owner / GM / accountant can unlock directly (resets status to Open).
            Admin (with client access) can request an unlock; everyone else is read-only. */}
        {isJobLocked && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert
              severity="warning"
              icon={<LockIcon fontSize="inherit" />}
              sx={{
                alignItems: 'center',
                '& .MuiAlert-action': {
                  pt: 0,
                  alignItems: 'center',
                },
              }}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {canUnlockThisJob && (
                    <Button
                      size="small"
                      color="warning"
                      variant="contained"
                      startIcon={<LockOpenIcon />}
                      disabled={unlockingJob}
                      onClick={handleUnlockJob}
                    >
                      {unlockingJob ? 'Unlocking…' : 'Unlock Job'}
                    </Button>
                  )}
                  {!canUnlockThisJob && canRequestUnlockThisJob && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      startIcon={<NotificationsActiveIcon />}
                      disabled={requestingUnlockJob}
                      onClick={() => setShowRequestUnlockDialog(true)}
                    >
                      {jobDetails?.requested_unlock_at ? 'Re-request Unlock' : 'Request Unlock'}
                    </Button>
                  )}
                </Box>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                This job is locked because its status is{' '}
                "{getStatusLabel(jobDetails?.locked_reason_status || jobDetails?.status)}".
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Most edits, additions, deletions, approvals, locks and invoice
                actions are disabled until the job is unlocked.
                {isAccounting(user) ? ' Owner, general manager, and accountant can still edit the invoiced proposal and existing technician assignments.' : ''}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Pending unlock request banner — who / when / reason; owner/GM/accountant can dismiss */}
        {isJobLocked && jobDetails?.requested_unlock_at && (
          <Box sx={{ px: 3, pt: 1 }}>
            <Alert
              severity="warning"
              icon={<NotificationsActiveIcon fontSize="inherit" />}
              sx={{
                alignItems: 'center',
                backgroundColor: isDarkMode
                  ? 'rgba(33, 150, 243, 0.16)'
                  : 'rgba(33, 150, 243, 0.12)',
                color: 'inherit',
                '& .MuiAlert-icon': {
                  color: isDarkMode ? '#64b5f6' : '#1976d2',
                },
                '& .MuiAlert-action': {
                  pt: 0,
                  alignItems: 'center',
                },
              }}
              action={
                canUnlockThisJob ? (
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    disabled={dismissingUnlockRequest}
                    onClick={() => setShowDismissUnlockDialog(true)}
                  >
                    Dismiss Request
                  </Button>
                ) : null
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Unlock requested
                {' by '}
                {resolveUserDisplayNameById(jobDetails.requested_unlock_by, usersList) || 'Unknown'}
                {' on '}
                {new Date(jobDetails.requested_unlock_at).toLocaleString()}
              </Typography>
              {jobDetails.requested_unlock_reason ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  Reason: &quot;{jobDetails.requested_unlock_reason}&quot;
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block">
                  No reason was provided.
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {/* Loading State - only show if no jobDetails at all */}
        {!jobDetails && (
          <Box sx={{ width: '100%' }}>
            {/* Tabs Skeleton */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rectangular" width={120} height={40} />
                <Skeleton variant="rectangular" width={100} height={40} />
                <Skeleton variant="rectangular" width={80} height={40} />
                <Skeleton variant="rectangular" width={100} height={40} />
                <Skeleton variant="rectangular" width={90} height={40} />
                <Skeleton variant="rectangular" width={80} height={40} />
                <Skeleton variant="rectangular" width={100} height={40} />
              </Box>
            </Box>

            {/* Content Skeleton */}
            <Box sx={{ p: 3 }}>
              <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
              
              {/* Grid layout skeleton */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                {Array.from({ length: 9 }).map((_, index) => (
                  <Box key={index}>
                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Box>
                ))}
              </Box>
              
              {/* Location skeleton */}
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={300} height={24} />
              </Box>
              
              {/* Description skeleton */}
              <Box>
                <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={24} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Job Details with Tab Panels */}
        {jobDetails && (
          <Box sx={{ width: '100%' }}>
            {/* Tab Panels */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
                {/* Job Details Section - Boxed */}
                <Box sx={{ 
                  backgroundColor: 'background.paper', 
                  borderRadius: '8px', 
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  p: 2.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.1rem' }}>
                      Work Order Details
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {editMode ? (
                        <Autocomplete
                          options={jobStatusEditOptions}
                          value={{ value: editJob?.status, label: getStatusLabel(editJob?.status) }}
                          onChange={(event, value) => handleStatusChange(value)}
                          getOptionLabel={(option) => option.label}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          renderInput={(params) => <TextField {...params} placeholder="Select status" size="small" />}
                          sx={{ minWidth: 160 }}
                        />
                      ) : (() => {
                        const { bgColor, textColor, borderColor } = getStatusChipStyles(jobDetails.status);
                        return (
                          <Chip
                            label={getStatusLabel(jobDetails.status)}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                              borderRadius: '12px',
                              backgroundColor: bgColor,
                              color: textColor,
                              border: `1px solid ${borderColor}`,
                            }}
                          />
                        );
                      })()}
                      <FlagIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                  </Box>

                  {/* Flat responsive grid: 1 / 2 / 3 columns — wide fields span as needed */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                    gap: 2,
                    alignItems: 'start',
                  }}>
                    {/* Work Order | Client | Priority */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <InfoIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Work Order
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <TextField
                            value={editJob?.work_order || ''}
                            onChange={(e) => setEditJob({ ...editJob, work_order: e.target.value })}
                            fullWidth
                            placeholder="Enter work order..."
                            error={!!errors.work_order}
                            helperText={errors.work_order}
                            size="small"
                            inputProps={{ maxLength: 100 }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.work_order || 'Not assigned'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <PersonIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Client
                        </Typography>
                        {editMode && canPickClient ? (
                          <Autocomplete
                            options={clientOptionsForEdit}
                            value={editJob?.client_id ? clientOptionsForEdit.find(client => client.id === editJob.client_id) || null : null}
                            onChange={(event, value) => {
                              setEditJob({ ...editJob, client_id: value?.id || null });
                              if (errors.client_id) {
                                setErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.client_id;
                                  return next;
                                });
                              }
                            }}
                            getOptionLabel={(option) => option.client_name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select client"
                                size="small"
                                error={!!errors.client_id}
                                helperText={errors.client_id || ''}
                              />
                            )}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.client_name || 'Not assigned'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <FlagIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Priority
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <Autocomplete
                            options={PRIORITY_OPTIONS}
                            value={{ value: editJob?.priority, label: getPriorityLabel(editJob?.priority) }}
                            onChange={(event, value) => setEditJob({ ...editJob, priority: value?.value ?? 2 })}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value?.value}
                            renderInput={(params) => <TextField {...params} placeholder="Select priority" size="small" />}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {(jobDetails.priority >= 1 && jobDetails.priority <= 4) ? getPriorityLabel(jobDetails.priority) : 'Not set'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Address (span 2) | Address Line 2 */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, gridColumn: { sm: 'span 2', md: 'span 2' } }}>
                      <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Address
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <GooglePlacesAutocomplete
                            label=""
                            value={editJob?.address || ''}
                            onChange={(value) => setEditJob({ ...editJob, address: value })}
                            onPlaceSelect={handlePlaceSelect}
                            error={!!errors.address}
                            helperText={errors.address}
                            placeholder="Address"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.address
                              ? `${jobDetails.address}, ${jobDetails.city}, ${jobDetails.state}${jobDetails.zip_code ? ` ${jobDetails.zip_code}` : ''}`
                              : 'Not specified'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <SignpostIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Address Line 2
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <TextField
                            placeholder="Suite, unit, floor, etc."
                            value={editJob?.address_2 || ''}
                            onChange={(event) => setEditJob({ ...editJob, address_2: event.target.value })}
                            fullWidth
                            size="small"
                            error={!!errors.address_2}
                            helperText={errors.address_2}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.address_2 || '—'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* City | State | Zip — edit mode only */}
                    {(editMode && canEditJobFieldsInDialog) && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                          <LocationCityIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                              City
                            </Typography>
                            <OptimizedCityAutocomplete
                              value={editJob?.city || ''}
                              onChange={(value) => setEditJob({ ...editJob, city: value })}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="City"
                                  error={!!errors.city}
                                  helperText={errors.city}
                                  size="small"
                                />
                              )}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                            State
                          </Typography>
                          <Autocomplete
                            options={Object.keys(states)}
                            value={editJob?.state}
                            onChange={(event, value) => setEditJob({ ...editJob, state: value })}
                            getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="State"
                                error={!!errors.state}
                                helperText={errors.state}
                                size="small"
                              />
                            )}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                            Zip Code
                          </Typography>
                          <TextField
                            placeholder="Zip Code"
                            value={editJob?.zip_code || ''}
                            onChange={(event) => {
                              const formattedZip = formatZipCode(event.target.value);
                              setEditJob({ ...editJob, zip_code: formattedZip });
                            }}
                            fullWidth
                            size="small"
                            error={!!errors.zip_code}
                            helperText={errors.zip_code}
                          />
                        </Box>
                      </>
                    )}

                    {/* Trade | SLA | Store name */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <BuildIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Trade
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <Autocomplete
                            options={['Plumbing', 'HVAC', 'Electrical', 'Handyman', 'Doors', 'Pest Control', 'Locksmith', 'Appliance', 'Glass']}
                            value={editJob?.trade}
                            onChange={(event, value) => setEditJob({ ...editJob, trade: value })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select trade"
                                size="small"
                                error={!!errors.trade}
                                helperText={errors.trade}
                              />
                            )}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.trade || 'Not specified'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          SLA
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <TextField
                            type="datetime-local"
                            value={editJob?.sla || ''}
                            onChange={(e) => setEditJob({ ...editJob, sla: e.target.value })}
                            fullWidth
                            size="small"
                            error={!!errors.sla}
                            helperText={errors.sla || 'Must be 30+ minutes in the future (unchanged past values allowed).'}
                            slotProps={{
                              input: {
                                min: getMinimumSlaInputValue()
                              },
                              inputLabel: {
                                shrink: true
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {formatSlaDisplay(jobDetails.sla)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Store name
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <Autocomplete
                            freeSolo
                            options={storeNameOptions}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                            value={editJob?.store_name ?? ''}
                            onChange={async (event, value) => {
                              const str = typeof value === 'string' ? value : (value?.name ?? '');
                              setEditJob({ ...editJob, store_name: str });
                              if (str && str.trim() && canManageStoreNames && !storeNameOptions.some((option) => option.name === str.trim())) {
                                const created = await addStoreNameSuggestion(str.trim());
                                if (created) {
                                  setStoreNameOptions((prev) => upsertStoreNameOption(prev, created));
                                }
                              }
                            }}
                            onInputChange={(event, value) => {
                              setEditJob({ ...editJob, store_name: value ?? '' });
                            }}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;
                              const name = typeof option === 'string' ? option : option.name;
                              const optionId = typeof option === 'string' ? null : option.id;
                              return (
                                <li
                                  key={key}
                                  {...optionProps}
                                  style={{
                                    ...optionProps.style,
                                    padding: '8px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="body2">{name}</Typography>
                                  {canManageStoreNames && optionId && (
                                    <IconButton
                                      size="small"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const removed = await removeStoreNameSuggestion(optionId);
                                        if (removed) {
                                          setStoreNameOptions((prev) => prev.filter((item) => item.id !== optionId));
                                        }
                                      }}
                                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                      aria-label={`Remove "${name}" from suggestions`}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Store name"
                                size="small"
                                error={!!errors.store_name}
                                helperText={errors.store_name}
                              />
                            )}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {jobDetails.store_name || 'Not specified'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Description — full width */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, gridColumn: '1 / -1' }}>
                      <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Description
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <TextField
                            value={editJob?.description || ''}
                            onChange={(e) => setEditJob({ ...editJob, description: e.target.value })}
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="Describe the work that needs to be done..."
                            error={!!errors.description}
                            helperText={errors.description}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '0.95rem' }}>
                            {jobDetails.description || 'No description'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Tags (span 2) | Created date */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, gridColumn: { sm: 'span 2', md: 'span 2' } }}>
                      <LocalOfferIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Tags
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <JobTagsField
                            value={editJob?.tags || []}
                            onChange={(tags) => setEditJob({ ...editJob, tags })}
                            tagOptions={tagOptions}
                            setTagOptions={setTagOptions}
                            canCreateTags={canCreateJobTags}
                            canDeleteTags={canDeleteJobTags}
                            size="small"
                            placeholder="Select tags"
                            label=""
                          />
                        ) : (
                          <JobTagsField
                            value={jobDetails.tags || []}
                            readOnly
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Created date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                          {jobDetails.created_at ? new Date(jobDetails.created_at).toLocaleDateString() : 'Not specified'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Admin | Dispatcher | Invoiced fields when present */}
                    {/* Admin field temporarily hidden in view and edit mode*/}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <PersonIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                          Admin
                        </Typography>
                        {editMode && isOwner(user) ? (
                          <Autocomplete
                            options={admins || []}
                            value={editJob?.admin_id ? usersList?.find((u) => u.id === editJob.admin_id) : null}
                            onChange={(event, value) => setEditJob({ ...editJob, admin_id: value?.id || null })}
                            getOptionLabel={(option) => formatUserDisplayName(option) || option.email || ''}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select admin"
                                size="small"
                              />
                            )}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {displayUserByEmail(jobDetails.admin) || 'Not assigned'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <PersonIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                          Dispatcher
                        </Typography>
                        {editMode && canPickDispatcher ? (
                          <Autocomplete
                            options={dispatchers || []}
                            value={editJob?.dispatcher_id ? usersList?.find((u) => u.id === editJob.dispatcher_id) : null}
                            onChange={(event, value) => setEditJob({ ...editJob, dispatcher_id: value?.id || null })}
                            getOptionLabel={(option) => formatUserDisplayName(option) || option.email || ''}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select dispatcher"
                                size="small"
                                error={!!errors.dispatcher_id}
                                helperText={errors.dispatcher_id}
                              />
                            )}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                            {displayUserByEmail(jobDetails.dispatcher) || 'Not assigned'}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {firstInvoicedDate && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                          <PersonIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                              Invoiced by
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                              {firstInvoicedBy || (loadingAdditionalData ? '…' : '—')}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                          <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                              Invoiced date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                              {formatWallClockDate(firstInvoicedDate)}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Financial Details Section - Boxed */}
                <Box sx={{ 
                  backgroundColor: 'background.paper', 
                  borderRadius: '8px', 
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  p: 3
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 2, fontSize: '1.1rem' }}>
                    Financial Details
                  </Typography>
                  
                  {loadingAdditionalData ? (
                    // Skeleton loading state - single row with 5 fields
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Box key={index}>
                          <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width={60} height={24} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    // Actual cost values - single row with 5 fields
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          NTE
                        </Typography>
                        {(editMode && canEditJobFieldsInDialog) ? (
                          <TextField
                            type="number"
                            value={editJob?.nte ?? 0}
                            onChange={(e) => setEditJob({ ...editJob, nte: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })}
                            onFocus={(e) => {e.target.select()}}
                            onWheel={(e) => e.target.blur()}
                            error={!!errors.nte}
                            helperText={errors.nte}
                            slotProps={{
                              input: {
                                startAdornment: <Box sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>$</Box>,
                              },
                              htmlInput: {
                                step: '0.01',
                                min: 0,
                              },
                            }}
                            sx={{ mt: 0.5 }}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatCurrency(jobDetails?.nte || 0)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Total Cost
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(calculateTotalCost())}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Profit
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: calculateProfit() >= 0 ? '#2e7d32' : '#d32f2f' }}>
                          {formatCurrency(calculateProfit())}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Total Technicians Cost
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatCurrency(calculateTechniciansCost())}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Parts Cost
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatCurrency(calculatePartsCost())}
                        </Typography>
                      </Box>
                      
                      {/* QuickBooks Invoiced Toggle */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 0.5 }}>
                          QB Invoiced
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {loadingQuickbooksInvoiced ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={jobDetails?.quickbooks_invoiced || false}
                              onChange={handleToggleQuickbooksInvoiced}
                              disabled={!canEditFinancialToggles()}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4caf50',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: '#4caf50',
                                  },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                  backgroundColor: '#4caf50',
                                },
                                '& .MuiSwitch-track': {
                                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                                },
                              }}
                            />
                          )}
                          <Typography variant="body2" sx={{ color: jobDetails?.quickbooks_invoiced ? '#4caf50' : 'text.secondary', fontWeight: 500 }}>
                            {jobDetails?.quickbooks_invoiced ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Sent to Client Toggle */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 0.5 }}>
                          Sent to client
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {loadingSentToClient ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={jobDetails?.sent_to_client || false}
                              onChange={handleToggleSentToClient}
                              disabled={!canEditSentToClientToggle()}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4caf50',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: '#4caf50',
                                  },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                  backgroundColor: '#4caf50',
                                },
                                '& .MuiSwitch-track': {
                                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                                },
                              }}
                            />
                          )}
                          <Typography variant="body2" sx={{ color: jobDetails?.sent_to_client ? '#4caf50' : 'text.secondary', fontWeight: 500 }}>
                            {jobDetails?.sent_to_client ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Paid Toggle */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 0.5 }}>
                          Paid
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {loadingPaid ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={jobDetails?.paid || false}
                              onChange={handleTogglePaid}
                              disabled={!canEditFinancialToggles()}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4caf50',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: '#4caf50',
                                  },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                  backgroundColor: '#4caf50',
                                },
                                '& .MuiSwitch-track': {
                                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                                },
                              }}
                            />
                          )}
                          <Typography variant="body2" sx={{ color: jobDetails?.paid ? '#4caf50' : 'text.secondary', fontWeight: 500 }}>
                            {jobDetails?.paid ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Factored Toggle */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 0.5 }}>
                          Factored
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {loadingFactored ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={jobDetails?.factored || false}
                              onChange={handleToggleFactored}
                              disabled={!canEditFinancialToggles()}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4caf50',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: '#4caf50',
                                  },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                  backgroundColor: '#4caf50',
                                },
                                '& .MuiSwitch-track': {
                                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                                },
                              }}
                            />
                          )}
                          <Typography variant="body2" sx={{ color: jobDetails?.factored ? '#4caf50' : 'text.secondary', fontWeight: 500 }}>
                            {jobDetails?.factored ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Notes Section - below Financial Details; editable only by admins with client access */}
                <Box sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  p: 3,
                  mt: 3
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 2, fontSize: '1.1rem' }}>
                    Notes
                  </Typography>
                  {loadingAdditionalData ? (
                    <Skeleton variant="text" width="100%" height={48} />
                  ) : (editMode && canEditJobNotes()) ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={6}
                      value={editJob?.notes ?? ''}
                      onChange={(e) => setEditJob({ ...editJob, notes: e.target.value })}
                      placeholder="Internal notes (max 500 characters)"
                      error={!!errors.notes}
                      helperText={errors.notes}
                      size="small"
                      slotProps={{ input: { maxLength: 500 } }}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                      {jobDetails?.notes && jobDetails.notes.trim() ? jobDetails.notes : '—'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TechniciansTab
                jobDetails={jobDetails}
                jobId={jobDetails?.id}
                loadingAdditionalData={loadingAdditionalData}
                technicians={technicians}
                onRefreshJob={() => jobDetails?.id && fetchJobDetails(jobDetails.id)}
                user={user}
                isJobLocked={isJobLocked}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Parts
                </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton
                      onClick={() => fetchJobDetails(selectedJob.id)}
                      disabled={loadingAdditionalData}
                      sx={{
                        color: isDarkMode ? 'text.secondary' : 'text.primary',
                        bgcolor: isDarkMode ? 'background.paper' : 'grey.100',
                        '&:hover': { bgcolor: 'action.hover' },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        '&:disabled': { color: 'text.disabled' }
                      }}
                      title="Refresh parts"
                    >
                      <RefreshIcon sx={{ 
                        fontSize: '1.2rem',
                        animation: loadingAdditionalData ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        }
                      }} />
                    </IconButton>
                    {canCreatePart() && !creatingPart && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreatingPart(true)}
                        sx={{
                          bgcolor: '#1a1a1a',
                          '&:hover': { bgcolor: '#333' },
                          textTransform: 'none',
                          borderRadius: '8px'
                        }}
                      >
                        Add Part
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Loading state */}
                {loadingAdditionalData && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
                          </Box>
                )}

                {/* Create Part Dialog */}
                <Dialog
                  open={creatingPart}
                  onClose={() => {
                    setCreatingPart(false);
                    setPartForm({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
                    setPartErrors({});
                  }}
                  maxWidth="md" fullWidth
                  fullScreen={isSmallScreen}
                  slotProps={{paper: {sx: {borderRadius: '12px',boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',}}}}
                >
                  <DialogTitle
                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',pb: 1,borderBottom: '1px solid rgba(0, 0, 0, 0.08)'}}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                        Add Part or Material
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Add a part or material used in this job.
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => {
                        setCreatingPart(false);
                        setPartForm({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
                        setPartErrors({});
                      }}
                      sx={{color: 'text.secondary','&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)'}}}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    
                      {/* Part Name */}
                      <TextField
                        fullWidth
                        label="Part Name"
                        value={partForm.name}
                        onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                        error={!!partErrors.name}
                        helperText={partErrors.name}
                        required
                      />

                      {/* Part URL/Link */}
                      <TextField
                        fullWidth
                        label="Part URL/Link"
                        value={partForm.url}
                        onChange={(e) => setPartForm({ ...partForm, url: e.target.value })}
                        placeholder="https://example.com/part-link"
                      />

                      {/* Description */}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={partForm.description}
                      onChange={(e) => setPartForm({ ...partForm, description: e.target.value })}
                      error={!!partErrors.description}
                      helperText={partErrors.description}
                    />

                      {/* Cost and Order Status Row */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cost"
                        value={partForm.cost}
                        onChange={(e) => setPartForm({ ...partForm, cost: e.target.value })}
                        onFocus={(e) => {e.target.select()}}
                        error={!!partErrors.cost}
                        helperText={partErrors.cost}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <Typography sx={{ 
                                mr: 1, 
                                fontSize: '0.875rem',
                                  fontWeight: 700,
                                  color: 'text.primary'
                              }}>
                                $
                              </Typography>
                              ),
                              sx: {
                                fontWeight: 700,
                              }
                          }
                        }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontWeight: 700,
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 700,
                          }
                        }}
                      />

                      <TextField
                        select
                        fullWidth
                        label="Order Status"
                        value={partForm.order_status || 1}
                        onChange={(e) => setPartForm({ ...partForm, order_status: Number(e.target.value) || 1 })}
                        error={!!partErrors.order_status}
                        helperText={partErrors.order_status}
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontWeight: 700,
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 700,
                          },
                          '& .MuiInputBase-input': {
                            fontWeight: 700,
                          }
                        }}
                      >
                        {PART_ORDER_STATUSES.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      </Box>

                      {/* Technician */}
                      <Autocomplete
                        fullWidth
                        value={technicians?.find(t => t.id === partForm.technician_id) || null}
                        onChange={(event, value) => setPartForm({ ...partForm, technician_id: value?.id || null })}
                        options={(technicians || []).filter(isTechnicianAssignable)}
                        getOptionLabel={(option) => getTechnicianDisplayName(option)}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Technician"
                            placeholder="Optional"
                          />
                        )}
                      />

                      {/* Additional Notes */}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Additional Notes"
                      value={partForm.notes}
                      onChange={(e) => setPartForm({ ...partForm, notes: e.target.value })}
                      placeholder="Additional notes for this part..."
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.87)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.87)',
                            }
                          }
                        }}
                      />

                    {partErrors.generalError && (
                      <Alert 
                        severity="error" 
                        sx={{ 
                            borderRadius: '8px',
                          '& .MuiAlert-icon': {
                            alignItems: 'center'
                          }
                        }}
                      >
                        {partErrors.generalError}
                      </Alert>
                    )}
                    </Box>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)', gap: 1.5 }}>
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          setCreatingPart(false);
                          setPartForm({ name: '', description: '', url: '', cost: 0, order_status: 1, technician_id: null, notes: '' });
                          setPartErrors({});
                        }}
                        sx={{ 
                          textTransform: 'none',
                        borderRadius: '8px',
                          px: 3,
                          py: 1,
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          color: 'rgba(0, 0, 0, 0.87)',
                        fontWeight: 500,
                          '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                          bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    {(isAdmin(user) || isDispatcher(user)) && (
                      <Button
                        variant="contained"
                        startIcon={loadingParts.creating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <NotificationsActiveIcon />}
                        onClick={() => handleCreatePart(true)}
                        disabled={loadingParts.creating}
                        sx={{ 
                          bgcolor: '#1a1a1a',
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: '#333',
                          },
                          '&:disabled': {
                            bgcolor: 'rgba(0, 0, 0, 0.12)',
                          }
                        }}
                      >
                        {loadingParts.creating ? 'Saving...' : 'Save & Request Approval'}
                      </Button>
                    )}
                        <Button
                          variant="contained"
                      startIcon={loadingParts.creating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
                      onClick={() => handleCreatePart(false)}
                          disabled={loadingParts.creating}
                          sx={{ 
                        bgcolor: '#1a1a1a',
                            textTransform: 'none',
                        borderRadius: '8px',
                            px: 3,
                            py: 1,
                        fontWeight: 500,
                            '&:hover': {
                          bgcolor: '#333',
                            },
                            '&:disabled': {
                          bgcolor: 'rgba(0, 0, 0, 0.12)',
                            }
                          }}
                        >
                      {loadingParts.creating ? 'Saving...' : 'Add Part'}
                        </Button>
                  </DialogActions>
                </Dialog>

                {/* Parts List */}
                {!loadingAdditionalData && parts && parts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {parts.map((part) => (
                          <Accordion 
                            key={part.id}
                        expanded={expandedPart === part.id}
                        onChange={(e, isExpanded) => setExpandedPart(isExpanded ? part.id : null)}
                            sx={{
                          borderRadius: '12px !important',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', pr: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {part.name}
                              </Typography>
                              
                              {part.is_approved && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Approved"
                                  size="small"
                              sx={{
                                    bgcolor: '#4caf50',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {part.is_locked && (
                                <Chip
                                  icon={<LockIcon />}
                                  label="Locked"
                                  size="small"
                                  sx={{
                                    bgcolor: '#f44336',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {part.requested_approval_at && !part.is_approved && (
                                <Chip
                                  icon={<NotificationsActiveIcon />}
                                  label="Approval Requested"
                                  size="small"
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {part.requested_unlock_at && part.is_locked && (
                                <Chip
                                  icon={<NotificationsActiveIcon />}
                                  label="Unlock Requested"
                                  size="small"
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {part.is_rejected && (
                                <Chip
                                  icon={<BlockIcon />}
                                  label="Rejected"
                                  size="small"
                                  sx={{
                                    bgcolor: '#d32f2f',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                            </Box>
                            
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              ${parseFloat(part.cost || 0).toFixed(2)} • {part.order_status === 1 ? 'Pending' : part.order_status === 2 ? 'Ordered' : 'Delivered'}
                            </Typography>
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ 
                              p: 3, 
                              background: isDarkMode 
                                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                            }}>
                          {editingPart === part.id ? (
                            // Edit Mode
                            <Box>
                                <TextField 
                                  fullWidth 
                                  label="Part Name" 
                                  value={part.name} 
                                onChange={(e) => {
                                  setParts(parts.map(p => 
                                    p.id === part.id ? { ...p, name: e.target.value } : p
                                  ));
                                }}
                                error={!!partErrors[part.id]?.name}
                                helperText={partErrors[part.id]?.name}
                                  required 
                                sx={{ mb: 2 }}
                              />

                                <TextField 
                                  fullWidth 
                                multiline
                                rows={3}
                                label="Description"
                                value={part.description || ''}
                                onChange={(e) => {
                                  setParts(parts.map(p => 
                                    p.id === part.id ? { ...p, description: e.target.value } : p
                                  ));
                                }}
                                sx={{ mb: 2 }}
                              />

                                <TextField 
                                  fullWidth 
                                  label="Part URL/Link" 
                                value={part.url || ''}
                                onChange={(e) => {
                                  setParts(parts.map(p => 
                                    p.id === part.id ? { ...p, url: e.target.value } : p
                                  ));
                                }}
                                  placeholder="https://example.com/part-link"
                                sx={{ mb: 2 }}
                                />
                                
                              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                  <TextField 
                                    fullWidth 
                                  type="number"
                                  label="Cost"
                                    value={part.cost} 
                                  onChange={(e) => {
                                    setParts(parts.map(p => 
                                      p.id === part.id ? { ...p, cost: e.target.value } : p
                                    ));
                                  }}
                                    onFocus={(e) => {e.target.select()}}
                                  error={!!partErrors[part.id]?.cost}
                                  helperText={partErrors[part.id]?.cost}
                                    slotProps={{ 
                                      input: { 
                                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                                      } 
                                    }}
                                  />
                                  
                                  <Autocomplete
                                  fullWidth
                                  value={{ value: part.order_status || 1, label: part.order_status === 1 ? 'Pending' : part.order_status === 2 ? 'Ordered' : 'Delivered' }}
                                  onChange={(event, value) => {
                                    setParts(parts.map(p => 
                                      p.id === part.id ? { ...p, order_status: value?.value || 1 } : p
                                    ));
                                  }}
                                    options={PART_ORDER_STATUSES}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                    <TextField {...params} label="Order Status" />
                                  )}
                                  />
                                </Box>
                                
                              <Autocomplete
                                  fullWidth 
                                value={technicians?.find(t => t.id === part.technician_id) || null}
                                onChange={(event, value) => {
                                  setParts(parts.map(p => 
                                    p.id === part.id ? { ...p, technician_id: value?.id || null } : p
                                  ));
                                }}
                                options={(technicians || []).filter(isTechnicianAssignable)}
                                getOptionLabel={(option) => getTechnicianDisplayName(option)}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Requested by Technician"
                                    placeholder="Select technician (optional)"
                                  />
                                )}
                                sx={{ mb: 2 }}
                              />

                                <TextField 
                                  fullWidth 
                                  multiline
                                  rows={3}
                                label="Additional Notes"
                                value={part.notes || ''}
                                onChange={(e) => {
                                  setParts(parts.map(p => 
                                    p.id === part.id ? { ...p, notes: e.target.value } : p
                                  ));
                                }}
                                  placeholder="Additional notes for this part..."
                                sx={{ mb: 2 }}
                              />

                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                  variant="contained"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleUpdatePart(part.id)}
                                  disabled={loadingParts[part.id]}
                          sx={{ 
                                    bgcolor: '#1a1a1a',
                                    '&:hover': { bgcolor: '#333' },
                                    textTransform: 'none'
                                  }}
                                >
                                  {loadingParts[part.id] ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  onClick={() => {
                                    setEditingPart(null);
                                    setParts(jobDetails.parts || []);
                                    setPartErrors({});
                                  }}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                              {partErrors[part.id]?.generalError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  {partErrors[part.id].generalError}
                                </Alert>
                              )}
                            </Box>
                          ) : (
                            // View Mode
                            <Box>
                              {part.description && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Description:
                              </Typography>
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {part.description}
                              </Typography>
                            </Box>
                              )}

                              {part.url && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    URL:
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    component="a" 
                                    href={part.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                  >
                                    {part.url}
                                  </Typography>
                                </Box>
                              )}

                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                            <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Cost
                              </Typography>
                              <Typography variant="body1">
                                    ${parseFloat(part.cost || 0).toFixed(2)}
                              </Typography>
                            </Box>
                            
                            <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Order Status
                              </Typography>
                              <Typography variant="body1">
                                {part.order_status === 1 ? 'Pending' : part.order_status === 2 ? 'Ordered' : 'Delivered'}
                              </Typography>
                            </Box>
                            
                                {(part.technician_type === 'vendor' ? part.technician_company_name : (part.technician_first_name && part.technician_last_name)) && (
                            <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Requested by {part.technician_type === 'vendor' ? 'Vendor' : 'Technician'}
                              </Typography>
                              <Typography variant="body1">
                                      {part.technician_type === 'vendor' ? part.technician_company_name : `${part.technician_first_name} ${part.technician_last_name}`}
                              </Typography>
                            </Box>
                                )}
                            
                            <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Created By
                              </Typography>
                              <Typography variant="body1">
                                    {displayUserByEmail(part.created_by)}
                              </Typography>
                            </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Created At
                                  </Typography>
                                  <Typography variant="body1">
                                    {new Date(part.created_at).toLocaleString()}
                                  </Typography>
                          </Box>
                          
                                {part.updated_at && part.updated_at !== part.created_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Updated At
                              </Typography>
                              <Typography variant="body1">
                                      {new Date(part.updated_at).toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                          
                                {part.updated_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Updated By
                              </Typography>
                                    <Typography variant="body1">
                                      {displayUserByEmail(part.updated_by)}
                              </Typography>
                            </Box>
                          )}
                          
                                {part.approved_by && (
                            <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Approved By
                              </Typography>
                              <Typography variant="body1">
                                      {displayUserByEmail(part.approved_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {part.approved_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Approved At
                                    </Typography>
                                    <Typography variant="body1">
                                      {new Date(part.approved_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {part.locked_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Locked By
                                    </Typography>
                                    <Typography variant="body1">
                                      {displayUserByEmail(part.locked_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {part.locked_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Locked At
                                    </Typography>
                                    <Typography variant="body1">
                                      {new Date(part.locked_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {part.rejected_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Rejected By
                                    </Typography>
                                    <Typography variant="body1">
                                      {displayUserByEmail(part.rejected_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {part.rejected_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Rejected At
                                    </Typography>
                                    <Typography variant="body1">
                                      {new Date(part.rejected_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {part.rejection_notes && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: '8px' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                                    Rejection Notes:
                                  </Typography>
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {part.rejection_notes}
                                  </Typography>
                                </Box>
                              )}

                              {part.notes && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Additional Notes:
                                  </Typography>
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {part.notes}
                              </Typography>
                            </Box>
                              )}

                              {/* Part Attachments Section */}
                              <Box sx={{ mt: 3, mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachFileIcon fontSize="small" />
                                  Attachments {partAttachments[part.id] && `(${partAttachments[part.id].length})`}
                                </Typography>

                                {/* Upload Section - Only show if part is unlocked */}
                                {!part.is_locked && (
                                  <Box sx={{ mb: 2 }}>
                                    <input
                                      type="file"
                                      multiple
                                      id={`part-file-input-${part.id}`}
                                      style={{ display: 'none' }}
                                      onChange={(e) => handlePartFileSelect(part.id, e)}
                                    />
                                    <label htmlFor={`part-file-input-${part.id}`}>
                                      <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{ textTransform: 'none' }}
                                      >
                                        Upload Files
                                      </Button>
                                    </label>
                                  </Box>
                                )}

                                {/* Uploading Files Progress */}
                                {uploadingPartFiles[part.id] && Object.keys(uploadingPartFiles[part.id]).length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    {Object.entries(uploadingPartFiles[part.id]).map(([fileId, { file, progress }]) => (
                                      <Box key={fileId} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                          <Typography variant="body2" sx={{ flex: 1 }}>
                                            {file?.name || 'Uploading...'}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#666' }}>
                                            {progress}%
                                          </Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={progress} />
                                      </Box>
                                    ))}
                                  </Box>
                                )}

                                {/* Upload Error Message */}
                                {partAttachmentErrors[part.id] && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {partAttachmentErrors[part.id]}
                                  </Alert>
                                )}

                                {/* Attachments List */}
                                {partAttachments[part.id] && partAttachments[part.id].length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {partAttachments[part.id].map((attachment) => (
                                      <Box
                                        key={attachment.id}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          p: 1.5,
                                          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                                          borderRadius: '8px',
                                          bgcolor: '#fafafa',
                                          '&:hover': { bgcolor: '#f5f5f5' }
                                        }}
                                      >
                                        <Box 
                                          onClick={() => handlePartAttachmentPreview(part.id, attachment)}
                                          sx={{ 
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&:hover': { opacity: 0.7 }
                                          }}
                                        >
                                          {getFileIcon(attachment.file_type)}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {attachment.file_name}
                                          </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {(attachment.file_size / 1024).toFixed(2)} KB • Uploaded by {`${attachment.first_name || ''} ${attachment.last_name || ''}`.trim() || attachment.email}
                                          </Typography>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => handlePartAttachmentPreview(part.id, attachment)}
                                          sx={{ color: '#1976d2' }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handlePartAttachmentDownload(part.id, attachment)}
                                          sx={{ color: '#1976d2' }}
                                        >
                                          <DownloadIcon fontSize="small" />
                                        </IconButton>
                                        {!isJobLocked && !part.is_locked && attachment.uploaded_by === user.id && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeletePartAttachment(part.id, attachment.id)}
                                            disabled={deletingPartAttachments[attachment.id]}
                                            sx={{ color: '#d32f2f' }}
                                          >
                                            {deletingPartAttachments[attachment.id] ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <DeleteIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                                    No attachments yet
                                  </Typography>
                                )}
                              </Box>

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                                {canEditPart(part) && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditingPart(part.id)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Edit
                                  </Button>
                                )}

                                {canDeletePart(part) && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      setPartToDelete(part.id);
                                      setShowDeletePartConfirm(true);
                                    }}
                                    disabled={part.is_locked}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Delete
                                  </Button>
                                )}

                                {canApprovePart() && (
                                  <Button
                                    variant={part.is_approved ? 'contained' : 'outlined'}
                                    color={part.is_approved ? 'success' : 'primary'}
                                    startIcon={part.is_approved ? <CheckCircleIcon /> : <CheckCircleIcon />}
                                    onClick={() => handleTogglePartApproval(part.id, !part.is_approved)}
                                    disabled={loadingParts[`approve_${part.id}`]}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {loadingParts[`approve_${part.id}`] 
                                      ? 'Processing...' 
                                      : part.is_approved ? 'Modify' : 'Approve'}
                                  </Button>
                                )}

                                {canLockPart() && !part.is_locked && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<LockIcon />}
                                    onClick={() => handleLockPart(part.id)}
                                    disabled={loadingParts[`lock_${part.id}`]}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {loadingParts[`lock_${part.id}`] ? 'Locking...' : 'Lock'}
                                  </Button>
                                )}

                                {canLockPart() && part.is_locked && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<LockOpenIcon />}
                                    onClick={() => handleUnlockPart(part.id)}
                                    disabled={loadingParts[`unlock_${part.id}`]}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {loadingParts[`unlock_${part.id}`] ? 'Unlocking...' : 'Unlock'}
                                  </Button>
                                )}

                                {canRequestPartApproval(part) && !part.is_approved && (
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<NotificationsActiveIcon />}
                                    onClick={() => handleRequestPartApproval(part.id)}
                                    disabled={loadingParts[`request_approval_${part.id}`] || !!part.requested_approval_at}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {loadingParts[`request_approval_${part.id}`]
                                      ? 'Requesting...'
                                      : part.requested_approval_at
                                      ? 'Approval Requested'
                                      : 'Request Approval'}
                                  </Button>
                                )}

                                {canRequestPartUnlock(part) && (
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<NotificationsActiveIcon />}
                                    onClick={() => handleRequestPartUnlock(part.id)}
                                    disabled={loadingParts[`request_unlock_${part.id}`] || !!part.requested_unlock_at}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {loadingParts[`request_unlock_${part.id}`]
                                      ? 'Requesting...'
                                      : part.requested_unlock_at
                                      ? 'Unlock Requested'
                                      : 'Request Unlock'}
                                  </Button>
                                )}

                                {canRejectPart() && !part.is_rejected && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<BlockIcon />}
                                    onClick={() => setRejectingPart(part.id)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Reject
                                  </Button>
                          )}
                        </Box>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                      ))}
                    </Box>
                ) : !loadingAdditionalData && (
                  <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                    No parts for this job. {canCreatePart() ? 'Click "Add Part" to create one.' : ''}
                    </Typography>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Box
                onDragEnter={handleAttachmentTabDragOver}
                onDragOver={handleAttachmentTabDragOver}
                onDragLeave={handleAttachmentTabDragLeave}
                onDrop={() => {
                  setIsExternalAttachmentDrag(false);
                  setDragOverCategory(null);
                }}
                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {getVisibleJobAttachments().length > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAllAttachments}
                        disabled={downloadingAllAttachments || loadingAdditionalData}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          borderColor: isDarkMode ? '#64748b' : '#ccc',
                          color: isDarkMode ? '#e2e8f0' : '#1a1a1a',
                          '&:hover': {
                            borderColor: isDarkMode ? '#94a3b8' : '#999',
                            backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        {downloadingAllAttachments ? 'Preparing ZIP...' : 'Download All'}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAttachmentModal}
                      sx={{
                        bgcolor: '#1a1a1a',
                        '&:hover': { bgcolor: '#333' },
                        textTransform: 'none',
                        borderRadius: '8px'
                      }}
                    >
                      Add Attachment
                    </Button>
                  </Box>
                </Box>

                <JobLinksSection
                  jobId={jobDetails?.id}
                  links={jobLinks}
                  onLinksChange={handleJobLinksChange}
                  user={user}
                  onError={setShowError}
                  isLocked={!!jobDetails?.is_locked}
                />
                
                {/* Loading state for attachments tab */}
                {loadingAdditionalData && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        p: 2, 
                        border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: isDarkMode ? 'background.paper' : '#fff'
                      }}>
                        <Skeleton variant="rectangular" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width={150} height={20} />
                          <Skeleton variant="text" width={200} height={16} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                
                {/* Existing Attachments - Grouped by Category */}
                {!loadingAdditionalData && ((attachments && attachments.length > 0) || isExternalAttachmentDrag || !!draggedAttachment) ? (
                  (() => {
                    // Accounting: all QB; admin: public QB only; others: no QB
                    const filteredAttachments = filterJobAttachmentsForUser(attachments, user);
                    
                    // Group attachments by category
                    const groupedAttachments = filteredAttachments.reduce((acc, attachment) => {
                      const category = attachment.category || 'Before';
                      if (!acc[category]) {
                        acc[category] = [];
                      }
                      acc[category].push(attachment);
                      return acc;
                    }, {});

                    // Sort categories in consistent order (Quickbooks Invoice first for viewers)
                    const baseCategoryOrder = ['from_client', 'Before', 'After', 'Sign off', 'Other'];
                    const canViewQb = canViewQuickbooksInvoiceAttachments(user);
                    const canManageQb = canAccessQuickbooksInvoice(user);
                    const viewCategoryOrder = canViewQb
                      ? ['quickbooks_invoice', ...baseCategoryOrder]
                      : baseCategoryOrder;
                    // Drop targets omit QB for admins (view-only)
                    const dropCategoryOrder = canManageQb
                      ? ['quickbooks_invoice', ...baseCategoryOrder]
                      : baseCategoryOrder;
                    const sortedCategories = viewCategoryOrder.filter(cat => groupedAttachments[cat]);
                    const shouldShowAllDropCategories = isExternalAttachmentDrag || !!draggedAttachment;
                    const visibleCategories = shouldShowAllDropCategories
                      ? [...new Set([...dropCategoryOrder, ...sortedCategories])]
                      : sortedCategories;

                    // Category color mapping
                    // const getCategoryColor = (cat) => {
                    //   const colors = {
                    //     'Before': { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#fff' },
                    //     'After': { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#fff' },
                    //     'Sign off': { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#fff' },
                    //     'Other': { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#fff' }
                    //   };
                    //   return colors[cat] || { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#fff' };
                    // };

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                        {visibleCategories.map((category, categoryIndex) => {
                          // const categoryColor = getCategoryColor(category);
                          const categoryAttachments = groupedAttachments[category] || [];
                          const isFirstCategory = categoryIndex === 0;
                          return (
                            <Box key={category} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                              {/* Attachments List */}
                              <Accordion
                                expanded={expandedCategories[category] !== undefined ? expandedCategories[category] : isFirstCategory}
                                onChange={(event, isExpanded) => {
                                  setExpandedCategories(prev => ({
                                    ...prev,
                                    [category]: isExpanded
                                  }));
                                }}
                                onDragOver={(e) => {
                                  if (category === 'quickbooks_invoice' && !canManageQb) return;
                                  const hasExternalMedia = hasDraggedMediaFiles(e.dataTransfer);
                                  if (((!isJobLocked || canManageAttachmentsWhenLocked) && draggedAttachment && draggedAttachment.category !== category) || hasExternalMedia) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (hasExternalMedia) {
                                      e.dataTransfer.dropEffect = 'copy';
                                      setIsExternalAttachmentDrag(true);
                                    }
                                    setExpandedCategories(prev => ({
                                      ...prev,
                                      [category]: true
                                    }));
                                    setDragOverCategory(category);
                                  }
                                }}
                                sx={{
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  boxShadow: 'none',
                                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                                  backgroundColor: 'background.paper',
                                  width: '100%',
                                  '&:before': { display: 'none' },
                                  '&.Mui-expanded': {
                                    margin: 0,
                                  }
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                                  onDragOver={(e) => {
                                    if (category === 'quickbooks_invoice' && !canManageQb) return;
                                    const hasExternalMedia = hasDraggedMediaFiles(e.dataTransfer);
                                    if (((!isJobLocked || canManageAttachmentsWhenLocked) && draggedAttachment && draggedAttachment.category !== category) || hasExternalMedia) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (hasExternalMedia) {
                                        e.dataTransfer.dropEffect = 'copy';
                                        setIsExternalAttachmentDrag(true);
                                      }
                                      setExpandedCategories(prev => ({
                                        ...prev,
                                        [category]: true
                                      }));
                                      setDragOverCategory(category);
                                    }
                                  }}
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    minHeight: 'auto',
                                    width: '100%',
                                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                                    '&.Mui-expanded': {
                                      minHeight: 'auto',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                      margin: 0,
                                      width: '100%'
                                    }
                                  }}
                                >
                                  {/* Category Header */}
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: 700, 
                                      color: 'text.primary',
                                      fontSize: '1rem',
                                    }}
                                  >
                                    {getCategoryDisplayName(category)} ({categoryAttachments.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails 
                                  onDragOver={(e) => handleAttachmentDragOver(e, category)}
                                  onDragLeave={handleAttachmentDragLeave}
                                  onDrop={(e) => handleAttachmentDrop(e, category)}
                                  sx={{ 
                                    p: 2, 
                                    width: '100%',
                                    minHeight: dragOverCategory === category ? '100px' : 'auto',
                                    backgroundColor: dragOverCategory === category ? '#e3f2fd' : 'transparent',
                                    border: dragOverCategory === category ? '2px dashed #1976d2' : 'none',
                                    borderRadius: dragOverCategory === category ? '8px' : '0',
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                                    gap: 2, 
                                    width: '100%' 
                                  }}>
                                    {categoryAttachments.map((attachment) => {
                                      // Owner/admin/assigned dispatcher can manage any attachment;
                                      // accountants are limited to attachments they uploaded themselves.
                                      const canManageAttachments = canManageJobAttachmentOnJob(user, attachment, jobDetails, isJobLocked);
                                      const canDeleteAttachment = canDeleteJobAttachment(user, attachment, jobDetails, isJobLocked);
                                      const isEditingCategoryForThis = editingCategory[attachment.id];
                                      
                                      const isDragging = draggedAttachment?.id === attachment.id;
                                      const canDrag =
                                        canManageAttachments &&
                                        !isEditingCategoryForThis &&
                                        !deletingAttachments[attachment.id];
                                      
                                      return (
                                        <Box 
                                          key={attachment.id}
                                          draggable={canDrag}
                                          onDragStart={(e) => handleAttachmentDragStart(e, attachment)}
                                          onDragEnd={handleAttachmentDragEnd}
                                          sx={{ 
                                            backgroundColor: 'background.paper',
                                            borderRadius: '8px',
                                            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5,
                                            minWidth: 0,
                                            boxSizing: 'border-box',
                                            cursor: canDrag ? 'grab' : 'default',
                                            opacity: isDragging ? 0.5 : 1,
                                            transition: 'all 0.2s ease-in-out',
                                            '&:active': {
                                              cursor: canDrag ? 'grabbing' : 'default'
                                            },
                                            '&:hover': {
                                              boxShadow: canDrag ? '0 2px 6px rgba(0, 0, 0, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                                            }
                                          }}
                                        >
                                          {/* File Preview */}
                                          <Box sx={{ mb: 1 }}>
                                            <Box
                                              onClick={() => handleAttachmentPreview(attachment)}
                                              sx={{
                                                width: '100%',
                                                height: 180,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: isDarkMode ? 'action.hover' : 'grey.100',
                                                borderRadius: '10px',
                                                cursor: loadingAttachments[attachment.id] ? 'wait' : 'pointer',
                                                transition: 'all 0.2s ease-in-out',
                                                flexShrink: 0,
                                                overflow: 'hidden',
                                                position: 'relative',
                                                '&:hover': {
                                                  transform: loadingAttachments[attachment.id] ? 'none' : 'scale(1.05)',
                                                  backgroundColor: isDarkMode ? 'action.selected' : 'grey.200'
                                                },
                                                '& svg': {
                                                  fontSize: '46px !important'
                                                },
                                                '& img': {
                                                  transition: 'transform 0.2s ease-in-out'
                                                },
                                                '&:hover img': {
                                                  transform: loadingAttachments[attachment.id] ? 'none' : 'scale(1.03)'
                                                }
                                              }}
                                            >
                                              {isImageFile(attachment.file_type) && attachmentPreviewUrls[attachment.id] ? (
                                                <img
                                                  src={attachmentPreviewUrls[attachment.id]}
                                                  alt={attachment.file_name}
                                                  style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: 6
                                                  }}
                                                  onError={() => {
                                                    // Mark failed to avoid endless retry loops for broken/expired URLs.
                                                    setFailedAttachmentPreviews((prev) => ({
                                                      ...prev,
                                                      [attachment.id]: true
                                                    }));
                                                    setAttachmentPreviewUrls((prev) => {
                                                      const next = { ...prev };
                                                      delete next[attachment.id];
                                                      return next;
                                                    });
                                                  }}
                                                />
                                              ) : isVideoFile(attachment.file_type) && attachmentPreviewUrls[attachment.id] ? (
                                                <>
                                                  <video
                                                    src={attachmentPreviewUrls[attachment.id]}
                                                    muted
                                                    playsInline
                                                    preload="metadata"
                                                    style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      objectFit: 'cover',
                                                      borderRadius: 6,
                                                      backgroundColor: '#000'
                                                    }}
                                                    onError={() => {
                                                      setFailedAttachmentPreviews((prev) => ({
                                                        ...prev,
                                                        [attachment.id]: true
                                                      }));
                                                      setAttachmentPreviewUrls((prev) => {
                                                        const next = { ...prev };
                                                        delete next[attachment.id];
                                                        return next;
                                                      });
                                                    }}
                                                  />
                                                  <PlayCircleOutlineIcon
                                                    sx={{
                                                      position: 'absolute',
                                                      top: '50%',
                                                      left: '50%',
                                                      transform: 'translate(-50%, -50%)',
                                                      color: 'rgba(255,255,255,0.95)',
                                                      pointerEvents: 'none',
                                                      fontSize: '52px !important',
                                                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.65))',
                                                    }}
                                                  />
                                                </>
                                              ) : isPreviewableMedia(attachment.file_type) && loadingAttachmentPreviews[attachment.id] ? (
                                                <CircularProgress size={18} />
                                              ) : (
                                                getFileIcon(attachment.file_type)
                                              )}
                                            </Box>
                                          </Box>

                                          {/* File Details */}
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                              <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                  fontWeight: 600,
                                                  color: 'text.primary',
                                                  fontSize: '0.8125rem',
                                                  cursor: loadingAttachments[attachment.id] ? 'wait' : 'pointer',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  lineHeight: 1.3,
                                                  flex: 1,
                                                  minWidth: 0,
                                                  '&:hover': { color: '#1976d2' }
                                                }}
                                                onClick={() => handleAttachmentPreview(attachment)}
                                              >
                                                {attachment.file_name}
                                              </Typography>

                                              {!isEditingCategoryForThis && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                                                  <Chip
                                                    label={getCategoryDisplayName(attachment.category || 'Before')}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: isDarkMode ? 'action.hover' : 'grey.100',
                                                      color: 'text.secondary',
                                                      fontWeight: 600,
                                                      fontSize: '0.68rem',
                                                      height: 20,
                                                      '& .MuiChip-label': {
                                                        px: 1
                                                      }
                                                    }}
                                                  />
                                                  {canManageAttachments && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => setEditingCategory(prev => ({ ...prev, [attachment.id]: true }))}
                                                      sx={{ 
                                                        color: 'text.secondary',
                                                        p: 0.25,
                                                        '&:hover': { 
                                                          backgroundColor: '#e3f2fd',
                                                          color: '#1976d2'
                                                        }
                                                      }}
                                                    >
                                                      <EditIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                  )}

                                                  <Chip
                                                    label={attachment.is_private ? 'Private' : 'Public'}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: attachment.is_private ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)',
                                                      color: attachment.is_private ? '#f44336' : '#2e7d32',
                                                      fontWeight: 700,
                                                      fontSize: '0.68rem',
                                                      height: 20,
                                                      '& .MuiChip-label': {
                                                        px: 1
                                                      }
                                                    }}
                                                  />
                                                  {canUpdateAttachmentPrivacyOnJob(user, attachment, isJobLocked) && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleUpdatePrivacy(attachment.id, !attachment.is_private)}
                                                      sx={{
                                                        p: 0.25,
                                                        color: attachment.is_private ? '#f44336' : '#2e7d32',
                                                        '&:hover': {
                                                          backgroundColor: attachment.is_private ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)'
                                                        }
                                                      }}
                                                    >
                                                      {attachment.is_private ? (
                                                        <LockOpenIcon sx={{ fontSize: 14 }} />
                                                      ) : (
                                                        <LockIcon sx={{ fontSize: 14 }} />
                                                      )}
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              )}
                                            </Box>
                                            
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                              Uploaded {new Date(attachment.created_at).toLocaleDateString()} by {attachment.first_name} {attachment.last_name}
                                            </Typography>
                                            
                                            {/* Category Display/Edit */}
                                            {isEditingCategoryForThis && (
                                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Autocomplete
                                                  size="small"
                                                  options={canAccessQuickbooksInvoice(user) ? ATTACHMENT_CATEGORIES : ATTACHMENT_CATEGORIES.filter(cat => cat !== 'quickbooks_invoice')}
                                                  getOptionLabel={(option) => getCategoryDisplayName(option)}
                                                  value={attachment.category || 'Before'}
                                                  onChange={(event, newValue) => {
                                                    if (newValue && newValue !== attachment.category) {
                                                      handleUpdateCategory(attachment.id, newValue);
                                                    } else {
                                                      setEditingCategory(prev => {
                                                        const newState = { ...prev };
                                                        delete newState[attachment.id];
                                                        return newState;
                                                      });
                                                    }
                                                  }}
                                                  onBlur={() => {
                                                    setEditingCategory(prev => {
                                                      const newState = { ...prev };
                                                      delete newState[attachment.id];
                                                      return newState;
                                                    });
                                                  }}
                                                  renderInput={(params) => (
                                                    <TextField
                                                      {...params}
                                                      label="Category"
                                                      size="small"
                                                      sx={{
                                                        minWidth: 150,
                                                        '& .MuiOutlinedInput-root': {
                                                          backgroundColor: 'background.paper',
                                                          borderRadius: '6px',
                                                          fontSize: '0.875rem',
                                                          '&:hover': {
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                              borderColor: '#bdbdbd',
                                                            },
                                                          },
                                                          '&.Mui-focused': {
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                              borderColor: '#1976d2',
                                                              borderWidth: '1.5px',
                                                            },
                                                          },
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                          fontSize: '0.75rem',
                                                          color: 'text.secondary',
                                                          '&.Mui-focused': {
                                                            color: '#1976d2',
                                                          },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                          padding: '6px 12px',
                                                          color: 'text.primary',
                                                        },
                                                      }}
                                                    />
                                                  )}
                                                  sx={{
                                                    '& .MuiAutocomplete-inputRoot': {
                                                      padding: '0 !important',
                                                    },
                                                  }}
                                                  autoFocus
                                                />
                                              </Box>
                                            )}
                                              
                                              {/* Download Progress */}
                                              {loadingAttachments[attachment.id] && (
                                                <Box sx={{ mt: 1 }}>
                                                  <LinearProgress 
                                                    variant="determinate" 
                                                    value={downloadProgress[attachment.id] || 0}
                                                    sx={{
                                                      height: 4,
                                                      borderRadius: 2,
                                                      backgroundColor: '#e0e0e0',
                                                      '& .MuiLinearProgress-bar': {
                                                        backgroundColor: '#1976d2'
                                                      }
                                                    }}
                                                  />
                                                  <Typography variant="caption" sx={{ color: '#1976d2', fontSize: '0.7rem' }}>
                                                    Loading... {downloadProgress[attachment.id] || 0}%
                                                  </Typography>
                                                </Box>
                                              )}
                                              
                                              {/* Download Error */}
                                              {downloadErrors[attachment.id] && (
                                                <Box sx={{ mt: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem', fontWeight: 500 }}>
                                                      ⚠️ {downloadErrors[attachment.id]}
                                                    </Typography>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={() => retryDownload(attachment.id, attachment.file_name)}
                                                      sx={{ 
                                                        fontSize: '0.6rem', 
                                                        minWidth: 'auto', 
                                                        px: 1, 
                                                        py: 0.5,
                                                        borderColor: '#d32f2f',
                                                        color: '#d32f2f',
                                                        '&:hover': {
                                                          backgroundColor: '#d32f2f',
                                                          color: 'white'
                                                        }
                                                      }}
                                                    >
                                                      Retry
                                                    </Button>
                                                  </Box>
                                                </Box>
                                              )}
                                          </Box>
                                          
                                          {/* Action Buttons */}
                                          <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1 }}>
                                            <Button
                                              variant="outlined"
                                              startIcon={<DownloadIcon />}
                                              onClick={() => handleAttachmentDownload(attachment)}
                                              disabled={loadingAttachments[attachment.id]}
                                              fullWidth
                                              sx={{
                                                backgroundColor: 'background.paper',
                                                color: 'text.primary',
                                                borderColor: '#e0e0e0',
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                borderRadius: '6px',
                                                py: 0.5,
                                                '&:hover': {
                                                  backgroundColor: '#f5f5f5',
                                                  borderColor: '#bdbdbd'
                                                },
                                                '&:disabled': {
                                                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                                                  color: '#bdbdbd'
                                                }
                                              }}
                                            >
                                              Download
                                            </Button>
                                            
                                            {canDeleteAttachment && (
                                              <Button
                                                variant="outlined"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => confirmDeleteAttachment(attachment)}
                                                disabled={deletingAttachments[attachment.id] || isEditingCategoryForThis}
                                                fullWidth
                                                sx={{
                                                  backgroundColor: 'background.paper',
                                                  color: 'text.primary',
                                                  borderColor: '#e0e0e0',
                                                  textTransform: 'none',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 500,
                                                  borderRadius: '6px',
                                                  py: 0.5,
                                                  '&:hover': {
                                                    backgroundColor: '#ffebee',
                                                    borderColor: '#d32f2f',
                                                    color: '#d32f2f'
                                                  },
                                                  '&:disabled': {
                                                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                                                    color: '#bdbdbd'
                                                  }
                                                }}
                                              >
                                                {deletingAttachments[attachment.id] ? 'Deleting...' : 'Delete'}
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()
                ) : !loadingAdditionalData ? (
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No attachments available for this job.
                  </Typography>
                ) : null}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Proposals
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton
                      onClick={() => fetchJobDetails(selectedJob.id)}
                      disabled={loadingAdditionalData}
                      sx={{
                      color: isDarkMode ? 'text.secondary' : 'text.primary',
                      bgcolor: isDarkMode ? 'background.paper' : 'grey.100',
                      '&:hover': { bgcolor: 'action.hover' },
                      border: '1px solid',
                      borderColor: 'divider',
                        borderRadius: '8px',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      '&:disabled': { color: 'text.disabled' }
                      }}
                      title="Refresh proposals"
                    >
                      <RefreshIcon sx={{ 
                        fontSize: '1.2rem',
                        animation: loadingAdditionalData ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        }
                      }} />
                    </IconButton>
                    {canCreateProposal() && !creatingProposal && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setProposalForm(getEmptyProposalForm());
                          setCreatingProposal(true);
                        }}
                        sx={{
                          bgcolor: '#1a1a1a',
                          '&:hover': { bgcolor: '#333' },
                          textTransform: 'none',
                          borderRadius: '8px'
                        }}
                      >
                        Add Proposal
                      </Button>
                    )}
                  </Box>
                </Box>

                {!!getClientApprovedProposal() && (
                  <Alert
                    severity="info"
                    sx={{ mb: 1 }}
                  >
                    Client approval is set on &quot;{formatProposalClientApprovalLabel(getClientApprovedProposal())}&quot;.
                    Only one proposal can be client-approved per job — revoke it before approving another.
                  </Alert>
                )}

                {/* Loading state */}
                {loadingAdditionalData && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
                  </Box>
                )}

                {/* Create Proposal Form */}
                {creatingProposal && (
                  <Box sx={{
                    p: 3,
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                      Add New Proposal
                    </Typography>
                    
                    {/* Proposal Text Field */}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Proposal Description"
                      placeholder="Enter proposal description"
                      value={proposalForm.proposal_text}
                      onChange={(e) => setProposalForm({ ...proposalForm, proposal_text: e.target.value })}
                      error={!!proposalErrors.proposal_text}
                      helperText={proposalErrors.proposal_text}
                      sx={getModernTextFieldSx(isDarkMode, { mb: 1 })}
                    />

                    <GenerateProposalButton
                      proposalText={proposalForm.proposal_text}
                      allowedTypes={billableItemTypeOptions.map((t) => t.value)}
                      onTextGenerated={(text) => {
                        setProposalForm((prev) => ({ ...prev, proposal_text: text }));
                        if (proposalErrors.proposal_text) {
                          setProposalErrors((prev) => {
                            const next = { ...prev };
                            delete next.proposal_text;
                            return next;
                          });
                        }
                      }}
                      onItemsGenerated={(items) => {
                        items.forEach((item) => {
                          if (item?.type) persistCustomBillableItemType(item.type);
                        });
                        setProposalForm((prev) => ({
                          ...prev,
                          items: appendGeneratedBillableItems(prev.items, items),
                        }));
                      }}
                    />

                    {/* Items Section */}
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                      Billable Items
                    </Typography>
                    {proposalErrors.items && typeof proposalErrors.items === 'string' && (
                      <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                        {proposalErrors.items}
                      </Typography>
                    )}

                    <Box sx={getBillableItemsPanelSx(isDarkMode)}>
                    {proposalForm.items.map((item, index) => (
                      <Box key={index} sx={{ py: 0.75 }}>
                        <Box
                          sx={{
                            width: '100%',
                            display: 'grid',
                            gap: 1.5,
                            alignItems: 'center',
                            gridTemplateColumns: BILLABLE_ITEMS_GRID_COLUMNS,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              {index + 1}
                            </Typography>
                          </Box>
                          <Box>
                            {/* Type Field - select existing or add new */}
                            <Autocomplete
                              fullWidth
                              freeSolo
                              selectOnFocus
                              clearOnBlur={false}
                              handleHomeEndKeys
                              autoHighlight
                              options={billableItemTypeOptions}
                              onBlur={() => {
                                const currentType = (item.type || '').trim();
                                const isDefaultType = BILLABLE_ITEM_TYPES.some(
                                  (t) => t.value.toLowerCase() === currentType.toLowerCase() || t.label.toLowerCase() === currentType.toLowerCase()
                                );
                                if (currentType && !isDefaultType) {
                                  persistCustomBillableItemType(currentType);
                                }
                              }}
                              value={billableItemTypeOptions.find((t) => t.value === item.type) || item.type || ''}
                              onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'input') return;
                                const input = (newInputValue || '').trim();
                                const match = BILLABLE_ITEM_TYPES.find(
                                  (t) => t.label.toLowerCase() === input.toLowerCase() || t.value.toLowerCase() === input.toLowerCase()
                                );
                                const nextType = match ? match.value : input;
                                const newItems = [...proposalForm.items];
                                newItems[index] = { ...newItems[index], type: nextType };
                                setProposalForm({ ...proposalForm, items: newItems });
                              }}
                              onChange={(event, newValue) => {
                                let selected = '';
                                if (typeof newValue === 'string') {
                                  selected = newValue.trim();
                                } else if (newValue && typeof newValue === 'object' && newValue.inputValue) {
                                  selected = newValue.inputValue.trim();
                                } else {
                                  selected = newValue?.value || '';
                                }
                                const match = BILLABLE_ITEM_TYPES.find(
                                  (t) => t.label.toLowerCase() === selected.toLowerCase() || t.value.toLowerCase() === selected.toLowerCase()
                                );
                                const nextType = (match ? match.value : selected) || 'labor';
                                if (!match && selected) persistCustomBillableItemType(selected);

                                const newItems = [...proposalForm.items];
                                newItems[index] = { ...newItems[index], type: nextType };
                                setProposalForm({ ...proposalForm, items: newItems });
                              }}
                              filterOptions={(options, params) => {
                                const filtered = billableItemTypeFilter(options, params);
                                const inputValue = (params.inputValue || '').trim();
                                const isExisting = options.some((option) =>
                                  option.label.toLowerCase() === inputValue.toLowerCase() ||
                                  option.value.toLowerCase() === inputValue.toLowerCase()
                                );
                                if (inputValue && !isExisting) {
                                  filtered.push({
                                    inputValue,
                                    value: inputValue,
                                    label: `Add "${inputValue}"`,
                                  });
                                }
                                return filtered;
                              }}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                if (option.inputValue) return option.inputValue;
                                return option.label || '';
                              }}
                              isOptionEqualToValue={(option, value) => option.value === value?.value}
                              renderOption={(props, option) => (
                                <li {...props} key={`${option.value}-${option.label}`}>
                                  {option.label}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Type"
                                  placeholder='Search/select type or Add "New Type"'
                                  error={!!(proposalErrors.items && proposalErrors.items[index]?.type)}
                                  helperText={proposalErrors.items && proposalErrors.items[index]?.type}
                                  sx={getModernTextFieldSx(isDarkMode)}
                                />
                              )}
                            />
                          </Box>
                          <Box>
                            {/* Item Description Field */}
                            <TextField
                              fullWidth
                              label="Item Description (Optional)"
                              placeholder="Enter item description"
                              value={item.description || ''}
                              onChange={(e) => {
                                const newItems = [...proposalForm.items];
                                newItems[index] = { ...newItems[index], description: e.target.value };
                                setProposalForm({ ...proposalForm, items: newItems });
                              }}
                              error={!!(proposalErrors.items && proposalErrors.items[index]?.description)}
                              helperText={proposalErrors.items && proposalErrors.items[index]?.description}
                              sx={getModernTextFieldSx(isDarkMode)}
                            />
                          </Box>
                          <Box>
                            <TextField
                              fullWidth
                              type="text"
                              inputMode="decimal"
                              label="Quantity"
                              value={String(item.quantity ?? '')}
                              onChange={(e) => {
                                const qty = e.target.value;
                                if (!isPartialBillableQuantityInput(qty)) return;
                                const newItems = [...proposalForm.items];
                                newItems[index] = { ...newItems[index], quantity: qty };
                                setProposalForm({ ...proposalForm, items: newItems });
                              }}
                              error={!!(proposalErrors.items && proposalErrors.items[index]?.quantity)}
                              helperText={proposalErrors.items && proposalErrors.items[index]?.quantity}
                              sx={getModernTextFieldSx(isDarkMode)}
                            />
                          </Box>
                          <Box>
                            <TextField
                              fullWidth
                              type="text"
                              inputMode="decimal"
                              label="Rate ($)"
                              value={String(item.unit_price ?? '')}
                              onChange={(e) => {
                                const rate = e.target.value;
                                if (!isPartialBillableRateInput(rate)) return;
                                const newItems = [...proposalForm.items];
                                newItems[index] = { ...newItems[index], unit_price: rate };
                                setProposalForm({ ...proposalForm, items: newItems });
                              }}
                              error={!!(proposalErrors.items && proposalErrors.items[index]?.unit_price)}
                              helperText={proposalErrors.items && proposalErrors.items[index]?.unit_price}
                              slotProps={{
                                input: {
                                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                }
                              }}
                              sx={getModernTextFieldSx(isDarkMode)}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                              ${billableLineAmount(item.quantity, item.unit_price).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {proposalForm.items.length > 1 ? (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newItems = proposalForm.items.filter((_, i) => i !== index);
                                  setProposalForm({ ...proposalForm, items: newItems });
                                  // Clear errors for this item
                                  if (proposalErrors.items && proposalErrors.items[index]) {
                                    const newErrors = { ...proposalErrors };
                                    delete newErrors.items[index];
                                    if (Object.keys(newErrors.items).length === 0) {
                                      delete newErrors.items;
                                    }
                                    setProposalErrors(newErrors);
                                  }
                                }}
                                sx={{ color: '#d32f2f' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 28, height: 28 }} />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                      <Box sx={getBillableTotalRowSx(isDarkMode)}>
                        <Box />
                        <Box sx={{ gridColumn: { xs: '1', sm: '2 / span 2', md: '2 / span 4' } }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.02em' }}>
                            Total
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', whiteSpace: 'nowrap' }}>
                            ${proposalForm.items.reduce((sum, item) => (
                              sum + billableLineAmount(item.quantity, item.unit_price)
                            ), 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box />
                      </Box>
                    </Box>

                    {/* Add Item Button - add more billable line items */}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setProposalForm({
                          ...proposalForm,
                          items: [...proposalForm.items, { type: 'labor', description: '', quantity: 1, unit_price: 0 }]
                        });
                      }}
                      sx={{ 
                        mb: 3,
                        textTransform: 'none',
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.primary',
                        '& .MuiButton-startIcon': { color: 'inherit' },
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fafafa'
                        }
                      }}
                    >
                      Add Item
                    </Button>

                    {canApproveProposal() && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Typography sx={{ mr: 2 }}>Client Approval:</Typography>
                        <Button
                          variant={proposalForm.client_approval ? 'contained' : 'outlined'}
                          onClick={() => {
                            if (!proposalForm.client_approval) {
                              const blockReason = getClientApprovalBlockReason();
                              if (blockReason) {
                                setShowError(blockReason);
                                return;
                              }
                            }
                            setProposalForm({ ...proposalForm, client_approval: !proposalForm.client_approval });
                          }}
                          disabled={!proposalForm.is_approved || (!proposalForm.client_approval && !canSetClientApproval())}
                          title={
                            !proposalForm.is_approved
                              ? 'Proposal must be approved before client approval'
                              : (!proposalForm.client_approval ? getClientApprovalBlockReason() : '')
                          }
                          sx={{
                            bgcolor: proposalForm.client_approval ? '#2196f3' : 'transparent',
                            color: proposalForm.client_approval ? 'white' : '#2196f3',
                            borderColor: '#2196f3',
                            '&:hover': {
                              bgcolor: proposalForm.client_approval ? '#1976d2' : 'rgba(33, 150, 243, 0.1)'
                            }
                          }}
                        >
                          {proposalForm.client_approval ? 'Approved By Client' : 'Client Unapproved'}
                        </Button>
                        {!proposalForm.client_approval && !!getClientApprovalBlockReason() && (
                          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                            {getClientApprovalBlockReason()}
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCreatingProposal(false);
                          setProposalForm(getEmptyProposalForm());
                          setProposalErrors({});
                        }}
                        sx={{ 
                          textTransform: 'none',
                          borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                          color: 'text.primary',
                          '&:hover': {
                            borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fafafa'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      {isDispatcher(user) && !(isAdmin(user) || isManagement(user)) && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleCreateProposal(true)}
                          disabled={loadingProposals.creating}
                          sx={{
                            bgcolor: '#000',
                            color: '#fff',
                            '&:hover': { 
                              bgcolor: '#333'
                            },
                            textTransform: 'none',
                            '&:disabled': {
                              bgcolor: '#ccc',
                              color: '#fff'
                            }
                          }}
                        >
                          {loadingProposals.creating ? 'Adding...' : 'Add & Request Approval'}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateProposal(false)}
                        disabled={loadingProposals.creating}
                        sx={{
                          bgcolor: '#000',
                          color: '#fff',
                          '&:hover': { 
                            bgcolor: '#333'
                          },
                          textTransform: 'none',
                          '&:disabled': {
                            bgcolor: '#ccc',
                            color: '#fff'
                          }
                        }}
                      >
                        {loadingProposals.creating ? 'Adding...' : 'Add Proposal'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Proposals List */}
                {!loadingAdditionalData && proposals && proposals.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {proposals.map((proposal) => (
                      <Accordion
                        key={proposal.id}
                        expanded={expandedProposal === proposal.id}
                        onChange={(e, isExpanded) => setExpandedProposal(isExpanded ? proposal.id : null)}
                        sx={{
                          borderRadius: '12px !important',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', pr: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                ${parseFloat(proposal.price || 0).toFixed(2)}
                              </Typography>
                              
                              {proposal.items && proposal.items.length > 0 && (
                                <Chip
                                  label={`${proposal.items.length} Item${proposal.items.length > 1 ? 's' : ''}`}
                                  size="small"
                                  sx={{
                                    bgcolor: isDarkMode ? 'rgba(25, 118, 210, 0.2)' : '#e3f2fd',
                                    color: isDarkMode ? '#64b5f6' : '#1976d2',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.is_approved && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Approved"
                                  size="small"
                                  sx={{
                                    bgcolor: '#4caf50',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.client_approval && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Approved By Client"
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.is_locked && (
                                <Chip
                                  icon={<LockIcon />}
                                  label="Locked"
                                  size="small"
                                  sx={{
                                    bgcolor: '#f44336',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.requested_approval_at && !proposal.is_approved && (
                                <Chip
                                  icon={<NotificationsActiveIcon />}
                                  label="Approval Requested"
                                  size="small"
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.requested_unlock_at && (proposal.is_locked || proposal.invoiced) && (
                                <Chip
                                  icon={<NotificationsActiveIcon />}
                                  label="Unlock Requested"
                                  size="small"
                                  sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.is_rejected && (
                                <Chip
                                  icon={<BlockIcon />}
                                  label="Rejected"
                                  size="small"
                                  sx={{
                                    bgcolor: '#d32f2f',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              
                              {proposal.invoiced && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Invoiced"
                                  size="small"
                                  sx={{
                                    bgcolor: '#ffd700',
                                    color: 'text.primary',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                            
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              by: {displayUserByEmail(proposal.created_by)}
                            </Typography>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ 
                          p: 3, 
                          background: isDarkMode 
                            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' 
                            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' 
                        }}>
                          {editingProposal === proposal.id ? (
                            // Edit Mode
                            <Box>
                              {/* Proposal Text Field */}
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Proposal Description"
                                placeholder="Enter proposal description"
                                value={proposal.proposal_text}
                                onChange={(e) => {
                                  setProposals(proposals.map(p => 
                                    p.id === proposal.id ? { ...p, proposal_text: e.target.value } : p
                                  ));
                                }}
                                error={!!proposalErrors[proposal.id]?.proposal_text}
                                helperText={proposalErrors[proposal.id]?.proposal_text || `${proposal.proposal_text.length}/10000 characters`}
                                sx={getModernTextFieldSx(isDarkMode, { mb: 1 })}
                              />

                              <GenerateProposalButton
                                proposalText={proposal.proposal_text}
                                allowedTypes={billableItemTypeOptions.map((t) => t.value)}
                                onTextGenerated={(text) => {
                                  setProposals((prev) => prev.map((p) => (
                                    p.id === proposal.id ? { ...p, proposal_text: text } : p
                                  )));
                                }}
                                onItemsGenerated={(items) => {
                                  items.forEach((item) => {
                                    if (item?.type) persistCustomBillableItemType(item.type);
                                  });
                                  setProposals((prev) => prev.map((p) => (
                                    p.id === proposal.id
                                      ? { ...p, items: appendGeneratedBillableItems(p.items || [], items) }
                                      : p
                                  )));
                                }}
                              />

                              {/* Items Section */}
                              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                Billable Items
                              </Typography>
                              {proposalErrors[proposal.id]?.items && typeof proposalErrors[proposal.id].items === 'string' && (
                                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                                  {proposalErrors[proposal.id].items}
                                </Typography>
                              )}

                              <Box sx={getBillableItemsPanelSx(isDarkMode)}>
                              {(proposal.items || []).map((item, index) => (
                                <Box key={index} sx={{ py: 0.75 }}>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      display: 'grid',
                                      gap: 1.5,
                                      alignItems: 'center',
                                      gridTemplateColumns: BILLABLE_ITEMS_GRID_COLUMNS,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        {index + 1}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      {/* Type Field - select existing or add new */}
                                      <Autocomplete
                                        fullWidth
                                        freeSolo
                                        selectOnFocus
                                        clearOnBlur={false}
                                        handleHomeEndKeys
                                        autoHighlight
                                        options={billableItemTypeOptions}
                                        onBlur={() => {
                                          const currentType = (item.type || '').trim();
                                          const isDefaultType = BILLABLE_ITEM_TYPES.some(
                                            (t) => t.value.toLowerCase() === currentType.toLowerCase() || t.label.toLowerCase() === currentType.toLowerCase()
                                          );
                                          if (currentType && !isDefaultType) {
                                            persistCustomBillableItemType(currentType);
                                          }
                                        }}
                                        value={billableItemTypeOptions.find((t) => t.value === item.type) || item.type || ''}
                                        onInputChange={(event, newInputValue, reason) => {
                                          if (reason !== 'input') return;
                                          const input = (newInputValue || '').trim();
                                          const match = BILLABLE_ITEM_TYPES.find(
                                            (t) => t.label.toLowerCase() === input.toLowerCase() || t.value.toLowerCase() === input.toLowerCase()
                                          );
                                          const nextType = match ? match.value : input;
                                          const newItems = [...(proposal.items || [])];
                                          newItems[index] = { ...newItems[index], type: nextType };
                                          setProposals(proposals.map(p => p.id === proposal.id ? { ...p, items: newItems } : p));
                                        }}
                                        onChange={(event, newValue) => {
                                          let selected = '';
                                          if (typeof newValue === 'string') {
                                            selected = newValue.trim();
                                          } else if (newValue && typeof newValue === 'object' && newValue.inputValue) {
                                            selected = newValue.inputValue.trim();
                                          } else {
                                            selected = newValue?.value || '';
                                          }
                                          const match = BILLABLE_ITEM_TYPES.find(
                                            (t) => t.label.toLowerCase() === selected.toLowerCase() || t.value.toLowerCase() === selected.toLowerCase()
                                          );
                                          const nextType = (match ? match.value : selected) || 'labor';
                                          if (!match && selected) persistCustomBillableItemType(selected);

                                          const newItems = [...(proposal.items || [])];
                                          newItems[index] = { ...newItems[index], type: nextType };
                                          setProposals(proposals.map(p => p.id === proposal.id ? { ...p, items: newItems } : p));
                                        }}
                                        filterOptions={(options, params) => {
                                          const filtered = billableItemTypeFilter(options, params);
                                          const inputValue = (params.inputValue || '').trim();
                                          const isExisting = options.some((option) =>
                                            option.label.toLowerCase() === inputValue.toLowerCase() ||
                                            option.value.toLowerCase() === inputValue.toLowerCase()
                                          );
                                          if (inputValue && !isExisting) {
                                            filtered.push({
                                              inputValue,
                                              value: inputValue,
                                              label: `Add "${inputValue}"`,
                                            });
                                          }
                                          return filtered;
                                        }}
                                        getOptionLabel={(option) => {
                                          if (typeof option === 'string') return option;
                                          if (option.inputValue) return option.inputValue;
                                          return option.label || '';
                                        }}
                                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                                        renderOption={(props, option) => (
                                          <li {...props} key={`${option.value}-${option.label}`}>
                                            {option.label}
                                          </li>
                                        )}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="Type"
                                            placeholder='Search/select type or Add "New Type"'
                                            error={!!(proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.type)}
                                            helperText={proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.type}
                                            sx={getModernTextFieldSx(isDarkMode)}
                                          />
                                        )}
                                      />
                                    </Box>
                                    <Box>
                                      {/* Item Description Field */}
                                      <TextField
                                        fullWidth
                                        label="Item Description (Optional)"
                                        placeholder="Enter item description"
                                        value={item.description || ''}
                                        onChange={(e) => {
                                          const newItems = [...(proposal.items || [])];
                                          newItems[index] = { ...newItems[index], description: e.target.value };
                                          setProposals(proposals.map(p => 
                                            p.id === proposal.id ? { ...p, items: newItems } : p
                                          ));
                                        }}
                                        error={!!(proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.description)}
                                        helperText={proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.description}
                                        sx={getModernTextFieldSx(isDarkMode)}
                                      />
                                    </Box>
                                    <Box>
                                      <TextField
                                        fullWidth
                                        type="text"
                                        inputMode="decimal"
                                        label="Quantity"
                                        value={String(item.quantity ?? '')}
                                        onChange={(e) => {
                                          const qty = e.target.value;
                                          if (!isPartialBillableQuantityInput(qty)) return;
                                          const newItems = [...(proposal.items || [])];
                                          newItems[index] = { ...newItems[index], quantity: qty };
                                          setProposals(proposals.map(p => 
                                            p.id === proposal.id ? { ...p, items: newItems } : p
                                          ));
                                        }}
                                        error={!!(proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.quantity)}
                                        helperText={proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.quantity}
                                        sx={getModernTextFieldSx(isDarkMode)}
                                      />
                                    </Box>
                                    <Box>
                                      <TextField
                                        fullWidth
                                        type="text"
                                        inputMode="decimal"
                                        label="Rate ($)"
                                        value={String(item.unit_price ?? '')}
                                        onChange={(e) => {
                                          const rate = e.target.value;
                                          if (!isPartialBillableRateInput(rate)) return;
                                          const newItems = [...(proposal.items || [])];
                                          newItems[index] = { ...newItems[index], unit_price: rate };
                                          setProposals(proposals.map(p => 
                                            p.id === proposal.id ? { ...p, items: newItems } : p
                                          ));
                                        }}
                                        error={!!(proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.unit_price)}
                                        helperText={proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]?.unit_price}
                                        slotProps={{
                                          input: {
                                            startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                          }
                                        }}
                                        sx={getModernTextFieldSx(isDarkMode)}
                                      />
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        ${billableLineAmount(item.quantity, item.unit_price).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                      {(proposal.items || []).length > 1 ? (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newItems = (proposal.items || []).filter((_, i) => i !== index);
                                            setProposals(proposals.map(p => 
                                              p.id === proposal.id ? { ...p, items: newItems } : p
                                            ));
                                            // Clear errors for this item
                                            if (proposalErrors[proposal.id]?.items && proposalErrors[proposal.id].items[index]) {
                                              const newErrors = { ...proposalErrors };
                                              delete newErrors[proposal.id].items[index];
                                              if (Object.keys(newErrors[proposal.id].items).length === 0) {
                                                delete newErrors[proposal.id].items;
                                              }
                                              setProposalErrors(newErrors);
                                            }
                                          }}
                                          sx={{ color: isDarkMode ? '#ef5350' : '#d32f2f' }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      ) : (
                                        <Box sx={{ width: 28, height: 28 }} />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                                <Box sx={getBillableTotalRowSx(isDarkMode)}>
                                  <Box />
                                  <Box sx={{ gridColumn: { xs: '1', sm: '2 / span 2', md: '2 / span 4' } }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.02em' }}>
                                      Total
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', whiteSpace: 'nowrap' }}>
                                      ${(proposal.items || []).reduce((sum, item) => (
                                        sum + billableLineAmount(item.quantity, item.unit_price)
                                      ), 0).toFixed(2)}
                                    </Typography>
                                  </Box>
                                  <Box />
                                </Box>
                              </Box>

                              {/* Add Item Button */}
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const currentItems = proposal.items || [];
                                  setProposals(proposals.map(p => 
                                    p.id === proposal.id ? { ...p, items: [...currentItems, { type: 'labor', description: '', quantity: 1, unit_price: 0 }] } : p
                                  ));
                                }}
                                sx={{ 
                                  mb: 3,
                                  textTransform: 'none',
                                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                                  color: 'text.primary',
                                  '& .MuiButton-startIcon': { color: 'inherit' },
                                  '&:hover': {
                                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fafafa'
                                  }
                                }}
                              >
                                Add Item
                              </Button>


                              {canApproveProposal() && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography sx={{ mr: 2 }}>Approval Status:</Typography>
                                      <Button
                                        variant={proposal.is_approved ? 'contained' : 'outlined'}
                                        onClick={() => {
                                          setProposals(proposals.map(p => {
                                            if (p.id !== proposal.id) return p;
                                            const nextApproved = !p.is_approved;
                                            // Unapprove clears client approval (matches API PATCH /approve + PUT).
                                            return {
                                              ...p,
                                              is_approved: nextApproved,
                                              ...(nextApproved ? {} : { client_approval: false }),
                                            };
                                          }));
                                        }}
                                        sx={{
                                          bgcolor: proposal.is_approved ? '#4caf50' : 'transparent',
                                          color: proposal.is_approved ? 'white' : '#4caf50',
                                          borderColor: '#4caf50',
                                          '&:hover': {
                                            bgcolor: proposal.is_approved ? '#45a049' : 'rgba(76, 175, 80, 0.1)'
                                          }
                                        }}
                                      >
                                        {proposal.is_approved ? 'Approved' : 'Not Approved'}
                                      </Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                      <Typography sx={{ mr: 2 }}>Client Approval:</Typography>
                                      <Button
                                        variant={proposal.client_approval ? 'contained' : 'outlined'}
                                        onClick={() => {
                                          if (!proposal.client_approval) {
                                            const blockReason = getClientApprovalBlockReason(proposal);
                                            if (blockReason) {
                                              setShowError(blockReason);
                                              return;
                                            }
                                          }
                                          setProposals(proposals.map(p =>
                                            p.id === proposal.id ? { ...p, client_approval: !p.client_approval } : p
                                          ));
                                        }}
                                        disabled={!proposal.is_approved || (!proposal.client_approval && !canSetClientApproval(proposal))}
                                        title={
                                          !proposal.is_approved
                                            ? 'Proposal must be approved before client approval'
                                            : (!proposal.client_approval ? getClientApprovalBlockReason(proposal) : '')
                                        }
                                        sx={{
                                          bgcolor: proposal.client_approval ? '#2196f3' : 'transparent',
                                          color: proposal.client_approval ? 'white' : '#2196f3',
                                          borderColor: '#2196f3',
                                          '&:hover': {
                                            bgcolor: proposal.client_approval ? '#1976d2' : 'rgba(33, 150, 243, 0.1)'
                                          }
                                        }}
                                      >
                                        {proposal.client_approval ? 'Approved By Client' : 'Client Unapproved'}
                                      </Button>
                                      {!proposal.client_approval && !!getClientApprovalBlockReason(proposal) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                          {getClientApprovalBlockReason(proposal)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              )}

                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                  variant="contained"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleUpdateProposal(proposal.id)}
                                  disabled={loadingProposals[proposal.id]}
                                  sx={{
                                    bgcolor: '#1a1a1a',
                                    '&:hover': { bgcolor: '#333' },
                                    textTransform: 'none'
                                  }}
                                >
                                  {loadingProposals[proposal.id] ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  onClick={() => {
                                    setEditingProposal(null);
                                    setProposals(jobDetails.proposals || []);
                                    setProposalErrors({});
                                  }}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            // View Mode
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Proposal Text:
                              </Typography>
                              <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                                {proposal.proposal_text}
                              </Typography>

                              {/* Items List — stacked cards (same surface language as modern AI fields) */}
                              {(proposal.items && proposal.items.length > 0) && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                    Billable Items
                                  </Typography>
                                  <Box sx={getBillableItemsPanelSx(isDarkMode)}>
                                    {(() => {
                                      const surface = getModernFieldSurface(isDarkMode);
                                      return (
                                        <>
                                          {proposal.items.map((item, index) => {
                                            const lineAmount = parseFloat(
                                              item.price || billableLineAmount(item.quantity, item.unit_price) || 0
                                            );
                                            return (
                                              <Box
                                                key={index}
                                                sx={{
                                                  p: 1.5,
                                                  mb: index < proposal.items.length - 1 ? 1.25 : 0,
                                                  borderRadius: '12px',
                                                  border: '1px solid',
                                                  borderColor: surface.fieldBorder,
                                                  bgcolor: surface.fieldBg,
                                                  backdropFilter: 'blur(10px)',
                                                  boxShadow: isDarkMode
                                                    ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
                                                    : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 2px rgba(15,23,42,0.04)',
                                                  minWidth: 0,
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    gap: 1.5,
                                                    mb: item.description ? 1 : 1.25,
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        fontWeight: 700,
                                                        color: surface.muted,
                                                        minWidth: 18,
                                                      }}
                                                    >
                                                      {index + 1}
                                                    </Typography>
                                                    <Chip
                                                      label={getBillableItemLabel(item.type)}
                                                      size="small"
                                                      sx={{
                                                        bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : 'rgba(15, 23, 42, 0.06)',
                                                        color: surface.accent,
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize',
                                                        maxWidth: '100%',
                                                      }}
                                                    />
                                                  </Box>
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 800,
                                                      fontSize: '0.95rem',
                                                      color: 'text.primary',
                                                      whiteSpace: 'nowrap',
                                                      flexShrink: 0,
                                                    }}
                                                  >
                                                    ${lineAmount.toFixed(2)}
                                                  </Typography>
                                                </Box>
                                                {!!item.description && (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: 'text.primary',
                                                      mb: 1.25,
                                                      wordBreak: 'break-word',
                                                    }}
                                                  >
                                                    {item.description}
                                                  </Typography>
                                                )}
                                                <Box
                                                  sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: 1,
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      px: 1.25,
                                                      py: 0.85,
                                                      borderRadius: '10px',
                                                      bgcolor: surface.codeBg,
                                                      border: '1px solid',
                                                      borderColor: surface.panelBorder,
                                                      minWidth: 0,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ display: 'block', fontWeight: 600, color: surface.muted, mb: 0.25 }}
                                                    >
                                                      Qty
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                      {item.quantity || 0}
                                                    </Typography>
                                                  </Box>
                                                  <Box
                                                    sx={{
                                                      px: 1.25,
                                                      py: 0.85,
                                                      borderRadius: '10px',
                                                      bgcolor: surface.codeBg,
                                                      border: '1px solid',
                                                      borderColor: surface.panelBorder,
                                                      minWidth: 0,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ display: 'block', fontWeight: 600, color: surface.muted, mb: 0.25 }}
                                                    >
                                                      Rate
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                      ${parseFloat(item.unit_price || 0).toFixed(2)}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            );
                                          })}
                                          <Box
                                            sx={{
                                              mt: 1.5,
                                              pt: 1.5,
                                              borderTop: '1px solid',
                                              borderColor: surface.panelBorder,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 1.5,
                                            }}
                                          >
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.02em' }}>
                                              Total
                                            </Typography>
                                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', whiteSpace: 'nowrap' }}>
                                              ${parseFloat(proposal.price || 0).toFixed(2)}
                                            </Typography>
                                          </Box>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                </Box>
                              )}

                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Total Price
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                    ${parseFloat(proposal.price || 0).toFixed(2)}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Created At
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                    {new Date(proposal.created_at).toLocaleString()}
                                  </Typography>
                                </Box>

                                {proposal.updated_at && proposal.updated_at !== proposal.created_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Updated At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.updated_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.updated_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Updated By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {displayUserByEmail(proposal.updated_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.approved_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Approved By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {displayUserByEmail(proposal.approved_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.approved_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Approved At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.approved_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.locked_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Locked By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {displayUserByEmail(proposal.locked_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.locked_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Locked At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.locked_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.requested_approval_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Approval Requested At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.requested_approval_at).toLocaleString()} by {displayUserByEmail(proposal.requested_approval_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.requested_unlock_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Unlock Requested At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.requested_unlock_at).toLocaleString()} by {displayUserByEmail(proposal.requested_unlock_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.rejected_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Rejected By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {displayUserByEmail(proposal.rejected_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.rejected_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Rejected At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {new Date(proposal.rejected_at).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.invoiced && proposal.invoiced_by && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Invoiced By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {displayUserByEmail(proposal.invoiced_by)}
                                    </Typography>
                                  </Box>
                                )}

                                {proposal.invoiced && proposal.invoiced_at && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Invoiced At
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {formatWallClockDateTime(proposal.invoiced_at)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {proposal.rejection_notes && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                    Rejection Notes
                                  </Typography>
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {proposal.rejection_notes}
                                  </Typography>
                                </Box>
                              )}

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {canEditProposal(proposal) && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => requestEditProposal(proposal)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Edit
                                  </Button>
                                )}

                                {canDeleteProposal(proposal) && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      setProposalToDelete(proposal);
                                      setShowDeleteProposalConfirm(true);
                                    }}
                                    disabled={loadingProposals[proposal.id]}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Delete
                                  </Button>
                                )}

                                {canApproveProposal(proposal) && (
                                  <>
                                    <Button
                                      variant="contained"
                                      startIcon={proposal.is_approved ? <ThumbDownIcon /> : <ThumbUpIcon />}
                                      onClick={() => handleToggleApproval(proposal.id, !proposal.is_approved)}
                                      disabled={loadingProposals[`approve_${proposal.id}`] || proposal.invoiced}
                                      sx={{
                                        bgcolor: proposal.is_approved ? '#f44336' : '#4caf50',
                                        '&:hover': {
                                          bgcolor: proposal.is_approved ? '#d32f2f' : '#45a049'
                                        },
                                        textTransform: 'none'
                                      }}
                                    >
                                      {loadingProposals[`approve_${proposal.id}`] ? 'Processing...' : (proposal.is_approved ? 'Revise' : 'Approved by Admin')}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      startIcon={proposal.client_approval ? <ThumbDownIcon /> : <ThumbUpIcon />}
                                      onClick={() => handleToggleClientApproval(proposal.id, !proposal.client_approval)}
                                      disabled={
                                        loadingProposals[`client_approve_${proposal.id}`]
                                        || proposal.invoiced
                                        || !proposal.is_approved
                                        || (!proposal.client_approval && !canSetClientApproval(proposal))
                                      }
                                      title={
                                        !proposal.is_approved
                                          ? 'Proposal must be approved before client approval'
                                          : (!proposal.client_approval ? getClientApprovalBlockReason(proposal) : '')
                                      }
                                      sx={{
                                        bgcolor: proposal.client_approval ? '#f44336' : '#2196f3',
                                        '&:hover': {
                                          bgcolor: proposal.client_approval ? '#d32f2f' : '#1976d2'
                                        },
                                        textTransform: 'none'
                                      }}
                                    >
                                      {loadingProposals[`client_approve_${proposal.id}`] ? 'Processing...' : (proposal.client_approval ? 'Client Approval Revoked' : 'Approved By Client')}
                                    </Button>
                                  </>
                                )}

                                {canLockProposal(proposal) && !proposal.is_locked && (
                                  <Button
                                    variant="contained"
                                    startIcon={<LockIcon />}
                                    onClick={() => handleLockProposal(proposal.id)}
                                    disabled={loadingProposals[`lock_${proposal.id}`]}
                                    sx={{
                                      bgcolor: '#f44336',
                                      '&:hover': { bgcolor: '#d32f2f' },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {loadingProposals[`lock_${proposal.id}`] ? 'Locking...' : 'Lock'}
                                  </Button>
                                )}

                                {canLockProposal(proposal) && proposal.is_locked && (
                                  <Button
                                    variant="contained"
                                    startIcon={<LockOpenIcon />}
                                    onClick={() => handleUnlockProposal(proposal.id)}
                                    disabled={loadingProposals[`unlock_${proposal.id}`]}
                                    sx={{
                                      bgcolor: '#4caf50',
                                      '&:hover': { bgcolor: '#45a049' },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {loadingProposals[`unlock_${proposal.id}`] ? 'Unlocking...' : 'Unlock'}
                                  </Button>
                                )}

                                {isAssignedDispatcher(user, jobDetails) &&
                                 proposal.created_by === user.email &&
                                 !proposal.is_approved && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<NotificationsActiveIcon />}
                                    onClick={() => handleRequestApproval(proposal.id)}
                                    disabled={loadingProposals[`request_approval_${proposal.id}`] || proposal.requested_approval_at}
                                    sx={{
                                      borderColor: '#ff9800',
                                      color: '#ff9800',
                                      '&:hover': {
                                        borderColor: '#f57c00',
                                        bgcolor: 'rgba(255, 152, 0, 0.1)'
                                      },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {proposal.requested_approval_at ? 'Approval Requested' : (loadingProposals[`request_approval_${proposal.id}`] ? 'Requesting...' : 'Request Approval')}
                                  </Button>
                                )}

                                {canRequestUnlock(proposal) && (
                                  <Button
                                    variant="outlined"
                                    startIcon={<NotificationsActiveIcon />}
                                    onClick={() => handleRequestUnlock(proposal.id)}
                                    disabled={loadingProposals[`request_unlock_${proposal.id}`] || proposal.requested_unlock_at}
                                    sx={{
                                      borderColor: '#ff9800',
                                      color: '#ff9800',
                                      '&:hover': {
                                        borderColor: '#f57c00',
                                        bgcolor: 'rgba(255, 152, 0, 0.1)'
                                      },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {proposal.requested_unlock_at ? 'Unlock Requested' : (loadingProposals[`request_unlock_${proposal.id}`] ? 'Requesting...' : 'Request Unlock')}
                                  </Button>
                                )}

                                {canRejectProposal(proposal) && !proposal.is_rejected && (
                                  <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<BlockIcon />}
                                    onClick={() => {
                                      setRejectingProposal(proposal.id);
                                      setRejectionForm({ notes: '', rejectWithoutNote: false });
                                      setRejectionErrors({});
                                    }}
                                    disabled={loadingProposals[`reject_${proposal.id}`]}
                                    sx={{
                                      bgcolor: '#d32f2f',
                                      '&:hover': { bgcolor: '#b71c1c' },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {loadingProposals[`reject_${proposal.id}`] ? 'Rejecting...' : 'Reject'}
                                  </Button>
                                )}

                                {canInvoiceProposal() && proposal.is_approved && proposal.client_approval && !proposal.invoiced && (() => {
                                  const invoiceDisabledReason = getInvoiceProposalDisabledReason(proposal.id);
                                  return (
                                    <Tooltip
                                      title={invoiceDisabledReason}
                                      disableHoverListener={!invoiceDisabledReason || isSmallScreen}
                                      disableFocusListener={!invoiceDisabledReason || isSmallScreen}
                                      disableTouchListener
                                    >
                                      <span
                                        onClick={() => {
                                          if (isSmallScreen && invoiceDisabledReason) {
                                            setShowError(invoiceDisabledReason);
                                          }
                                        }}
                                        style={{
                                          display: 'inline-flex',
                                          cursor: invoiceDisabledReason ? 'not-allowed' : undefined,
                                        }}
                                      >
                                        <Button
                                          variant="contained"
                                          startIcon={<CheckCircleIcon />}
                                          onClick={() => handleInvoiceProposal(proposal.id)}
                                          disabled={loadingProposals[`invoice_${proposal.id}`] || !!invoiceDisabledReason}
                                          sx={{
                                            bgcolor: invoiceDisabledReason ? 'grey.400' : '#ffd700',
                                            color: isDarkMode && !invoiceDisabledReason ? '#000' : 'text.primary',
                                            fontWeight: 600,
                                            '&:hover': {
                                              bgcolor: invoiceDisabledReason ? 'grey.400' : '#f9a825',
                                              color: '#000'
                                            },
                                            textTransform: 'none'
                                          }}
                                        >
                                          {loadingProposals[`invoice_${proposal.id}`] ? 'Invoicing...' : 'Invoice'}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  );
                                })()}

                                {canUninvoice() && proposal.invoiced && (
                                  <Button
                                    variant="contained"
                                    startIcon={<LockOpenIcon />}
                                    onClick={() => handleUninvoiceProposal(proposal.id)}
                                    disabled={loadingProposals[`uninvoice_${proposal.id}`]}
                                    sx={{
                                      bgcolor: '#9c27b0',
                                      color: '#fff',
                                      fontWeight: 600,
                                      '&:hover': { 
                                        bgcolor: '#7b1fa2',
                                        color: '#fff'
                                      },
                                      textTransform: 'none'
                                    }}
                                  >
                                    {loadingProposals[`uninvoice_${proposal.id}`] ? 'Modifying Invoice...' : 'Modify Invoice'}
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : !loadingAdditionalData && !creatingProposal ? (
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No proposals available for this job.
                  </Typography>
                ) : null}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <InvoiceTab 
                jobDetails={jobDetails} 
                proposals={proposals} 
                user={user}
                onProposalsUpdate={() => fetchJobDetails(selectedJob.id)}
                attachments={attachments}
                attachmentPreviewUrls={attachmentPreviewUrls}
                loadingAttachmentPreviews={loadingAttachmentPreviews}
                onAttachmentPreview={handleAttachmentPreview}
                onAttachmentDownload={handleAttachmentDownload}
                onAttachmentDelete={confirmDeleteAttachment}
                deletingAttachments={deletingAttachments}
                onToggleQuickbooksInvoiced={handleToggleQuickbooksInvoiced}
                onToggleFactored={handleToggleFactored}
                onTogglePaid={handleTogglePaid}
                onToggleSentToClient={handleToggleSentToClient}
                loadingQuickbooksInvoiced={loadingQuickbooksInvoiced}
                loadingFactored={loadingFactored}
                loadingPaid={loadingPaid}
                loadingSentToClient={loadingSentToClient}
                isJobLocked={isJobLocked}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={6}>
              <CommentsTab
                jobId={jobDetails?.id}
                user={user}
                loadingAdditionalData={loadingAdditionalData}
                onCommentsCountChange={setCommentsCount}
                initialComments={comments}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={7}>
              <ActivityTab
                jobId={jobDetails?.id}
                loadingAdditionalData={loadingAdditionalData}
                refreshToken={activityLogRefreshToken}
                onActivitiesCountChange={setActivitiesCount}
                initialLogs={activityLogs}
              />
            </TabPanel>
          </Box>
        )}
      </DialogContent>

      {/* Save Button Footer - shown only in edit mode */}
      {editMode && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          p: 1.5, 
          borderTop: '1px solid #e0e0e0' 
        }}>
          <Button 
            disabled={loading} 
            variant="contained" 
            onClick={handleSaveJob}
            sx={{ 
              backgroundColor: '#000',
              color: 'white',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#333',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#666',
                boxShadow: 'none'
              }
            }}
          >
            Save
          </Button>
        </Box>
      )}

      {/* Request Unlock dialog (Admin → Owner / GM / Accountant). Sends a notification to all
          owners + general_managers + accountants. Re-submitting overwrites the previous reason. */}
      <Dialog
        open={showRequestUnlockDialog}
        onClose={() => {
          if (requestingUnlockJob) return;
          setShowRequestUnlockDialog(false);
        }}
        aria-labelledby="request-unlock-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="request-unlock-dialog-title">Request Job Unlock</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            An owner, general manager, or accountant will be notified and may
            unlock the job. Optionally include a reason so they have context.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Reason (optional)"
            inputProps={{ maxLength: 500 }}
            value={requestUnlockReason}
            onChange={(e) => setRequestUnlockReason(e.target.value)}
            disabled={requestingUnlockJob}
            placeholder="Why does this job need to be unlocked?"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRequestUnlockDialog(false)}
            disabled={requestingUnlockJob}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestUnlockJob}
            color="warning"
            variant="contained"
            disabled={requestingUnlockJob}
            startIcon={<NotificationsActiveIcon />}
          >
            {requestingUnlockJob ? 'Sending…' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <DismissJobUnlockRequestDialog
        open={showDismissUnlockDialog}
        reason={dismissUnlockReason}
        onReasonChange={setDismissUnlockReason}
        loading={dismissingUnlockRequest}
        workOrder={jobDetails?.work_order}
        onClose={() => {
          if (dismissingUnlockRequest) return;
          setShowDismissUnlockDialog(false);
          setDismissUnlockReason('');
        }}
        onConfirm={handleDismissUnlockRequest}
      />

      {/* Cancelled / Recall No Charge status NTE confirmation */}
      <Dialog
        open={showCancelNteConfirm}
        onClose={() => { setShowCancelNteConfirm(false); setPendingCancelStatus(null); }}
        aria-labelledby="cancel-nte-dialog-title"
        aria-describedby="cancel-nte-dialog-description"
      >
        <DialogTitle id="cancel-nte-dialog-title">
          {pendingCancelStatus ? getStatusLabel(pendingCancelStatus) : 'Update'} Work Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-nte-dialog-description">
            {cancelConfirmNteAlreadyZero
              ? `This work order's NTE is already 0. Setting the status to ${pendingCancelStatus ? getStatusLabel(pendingCancelStatus) : 'this status'} will lock the work order. Do you want to continue?`
              : `Setting this work order to ${pendingCancelStatus ? getStatusLabel(pendingCancelStatus) : 'this status'} will set its NTE to 0 and lock the work order. Do you want to continue?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setShowCancelNteConfirm(false); setPendingCancelStatus(null); }}
            sx={{ color: isDarkMode ? undefined : '#000' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCancelStatus}
            color="error"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceDateChoiceDialog
        open={showInvoiceDateDialog}
        originalInvoicedAt={latestPreservedInvoiceDate}
        originalInvoiceNumber={
          pendingInvoiceProposalId
            ? getPreservedInvoiceNumber(pendingInvoiceProposalId)
            : null
        }
        jobCreatedAt={jobDetails?.created_at}
        loading={!!pendingInvoiceProposalId && !!loadingProposals[`invoice_${pendingInvoiceProposalId}`]}
        onClose={handleDiscardInvoiceDateChoice}
        onConfirm={handleConfirmInvoiceDateChoice}
      />

      <Dialog
        open={showInvoicedEditWarn}
        onClose={handleDiscardInvoicedEdit}
        aria-labelledby="invoiced-edit-warn-title"
        aria-describedby="invoiced-edit-warn-description"
      >
        <DialogTitle id="invoiced-edit-warn-title">
          Edit invoiced proposal
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="invoiced-edit-warn-description"
            sx={{ color: isDarkMode ? 'text.secondary' : 'text.primary' }}
          >
            {getInvoicedEditWarningText()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDiscardInvoicedEdit}
            sx={{ color: isDarkMode ? undefined : '#000' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmInvoicedEdit}
            variant="contained"
            color="warning"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* No technician assigned confirmation */}
      <Dialog
        open={showNoTechnicianConfirm}
        onClose={handleDiscardNoTechnicianAction}
        aria-labelledby="no-technician-dialog-title"
        aria-describedby="no-technician-dialog-description"
      >
        <DialogTitle id="no-technician-dialog-title">
          No Technician Assigned
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="no-technician-dialog-description"
            sx={{ color: isDarkMode ? 'text.secondary' : 'text.primary' }}
          >
            There is no technician assigned on this job. If work was done, you may have forgotten to add a technician and its cost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDiscardNoTechnicianAction}
            sx={{ color: isDarkMode ? undefined : '#000' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmNoTechnicianAction}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Work Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this work order? All related technicians and parts will also be removed. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteJob} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Toast for Update */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Work order updated successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={showCopyToast}
        autoHideDuration={2000}
        onClose={() => setShowCopyToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowCopyToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Work order copied to clipboard
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={!!showError}
        autoHideDuration={6000}
        onClose={() => setShowError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError('')}
          severity="error"
          variant="filled"
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {showError}
        </Alert>
      </Snackbar>

      {/* Success Toast for Delete */}
      {/* <Snackbar
        open={showDeleteSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowDeleteSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowDeleteSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Work order deleted successfully
        </Alert>
      </Snackbar> */}

      {/* Delete Attachment Confirmation Dialog */}
      <Dialog
        open={showDeleteAttachmentConfirm}
        onClose={() => setShowDeleteAttachmentConfirm(false)}
        aria-labelledby="delete-attachment-dialog-title"
        aria-describedby="delete-attachment-dialog-description"
      >
        <DialogTitle id="delete-attachment-dialog-title">
          Delete Attachment
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-attachment-dialog-description">
            Are you sure you want to delete "{attachmentToDelete?.file_name}"? This action cannot be undone and the file will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteAttachmentConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteAttachment} 
            color="error" 
            variant="contained"
            disabled={deletingAttachments[attachmentToDelete?.id]}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Attachment Modal */}
      <Dialog
        open={attachmentModalOpen}
        onClose={(_, reason) => {
          // Block backdrop/Escape dismiss while uploading; Cancel upload is explicit.
          if (modalUploading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          handleCloseAttachmentModal();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby="add-attachment-dialog-title"
      >
        <DialogTitle id="add-attachment-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Attachment
          <IconButton
            aria-label={modalUploading ? 'Cancel upload' : 'close'}
            onClick={modalUploading ? handleCancelModalUpload : handleCloseAttachmentModal}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Upload documents, photos, videos, or files to this job. You can select multiple files and set name, category, and privacy for each.
          </Typography>

          {/* Drop zone + multi file input */}
          <Box
            onDragOver={(e) => { e.preventDefault(); setModalDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setModalDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setModalDragOver(false);
              const files = e.dataTransfer.files;
              if (files?.length) addModalFiles(files);
            }}
            sx={{
              border: '2px dashed',
              borderColor: modalDragOver ? (isDarkMode ? '#64b5f6' : '#1976d2') : (isDarkMode ? '#334155' : '#e0e0e0'),
              borderRadius: 2,
              p: 3,
              mb: 2,
              backgroundColor: modalDragOver ? (isDarkMode ? 'rgba(100, 181, 246, 0.1)' : '#f0f7ff') : (isDarkMode ? 'rgba(255,255,255,0.02)' : '#fafafa'),
              textAlign: 'center',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Drag and drop files here, or choose files to add
            </Typography>
            <input
              type="file"
              multiple
              onChange={handleModalFileSelect}
              style={{ display: 'none' }}
              id="modal-file-input"
              accept={ACCEPT_ATTACHMENTS}
            />
            <label htmlFor="modal-file-input">
              <Button
                component="span"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#000',
                  color: 'white',
                  '&:hover': { backgroundColor: '#333' },
                }}
              >
                Choose Files
              </Button>
            </label>
          </Box>

          {/* List of selected files with per-file metadata */}
          {modalFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Files to upload ({modalFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 320, overflow: 'auto' }}>
                {modalFiles.map((mf) => (
                  <Box
                    key={mf.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: isDarkMode ? 'background.paper' : '#fff',
                    }}
                  >
                    <Box sx={{ flexShrink: 0, position: 'relative', width: 40, height: 40 }}>
                      {mf.preview && isVideoFile(mf.file?.type) ? (
                        <>
                          <video
                            src={mf.preview}
                            muted
                            preload="metadata"
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, backgroundColor: '#000' }}
                          />
                          <PlayCircleOutlineIcon
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'rgba(255,255,255,0.95)',
                              pointerEvents: 'none',
                              fontSize: '22px !important',
                              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.65))',
                            }}
                          />
                        </>
                      ) : mf.preview ? (
                        <img src={mf.preview} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        getFileIcon(mf.file?.type)
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TextField
                        size="small"
                        label="File name"
                        value={mf.fileName ?? mf.file?.name ?? ''}
                        onChange={(e) => updateModalFile(mf.id, { fileName: e.target.value })}
                        placeholder="e.g., Floor Plan.pdf"
                        fullWidth
                        sx={{ mb: 1 }}
                        error={!!modalErrors[mf.id]}
                        helperText={modalErrors[mf.id]}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 140 }}
                          options={canAccessQuickbooksInvoice(user) ? ATTACHMENT_CATEGORIES : ATTACHMENT_CATEGORIES.filter(cat => cat !== 'quickbooks_invoice')}
                          getOptionLabel={(option) => getCategoryDisplayName(option)}
                          value={mf.category || 'Before'}
                          onChange={(event, newValue) => {
                            updateModalFile(mf.id, { category: newValue || 'Before' });
                          }}
                          renderInput={(params) => <TextField {...params} placeholder="Category" />}
                        />
                        {!isOnlyDispatcher(user) && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant={mf.is_private === false ? 'contained' : 'outlined'}
                              onClick={() => updateModalFile(mf.id, { is_private: false })}
                              sx={{
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.75rem',
                                ...(mf.is_private === false ? { backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } } : { borderColor: '#4caf50', color: '#4caf50' }),
                              }}
                            >
                              Public
                            </Button>
                            <Button
                              size="small"
                              variant={mf.is_private === true ? 'contained' : 'outlined'}
                              onClick={() => updateModalFile(mf.id, { is_private: true })}
                              disabled={!canUploadPrivateAttachment(user)}
                              sx={{
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.75rem',
                                ...(mf.is_private === true ? { backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' } } : { borderColor: '#f44336', color: '#f44336' }),
                              }}
                            >
                              Private
                            </Button>
                          </Box>
                        )}
                      </Box>
                      {modalUploadProgress[mf.id] !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={modalUploadProgress[mf.id]}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              size="small"
                              onClick={handleCancelModalUpload}
                              sx={{ textTransform: 'none', minWidth: 'auto', px: 1, color: '#d32f2f' }}
                            >
                              Cancel
                            </Button>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Uploading {modalUploadProgress[mf.id]}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeModalFile(mf.id)}
                      disabled={modalUploading}
                      sx={{ color: '#dc3545', flexShrink: 0 }}
                      aria-label="Remove file"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={modalUploading ? handleCancelModalUpload : handleCloseAttachmentModal}
            sx={{ color: modalUploading ? '#d32f2f' : '#666', textTransform: 'none' }}
          >
            {modalUploading ? 'Cancel upload' : 'Cancel'}
          </Button>
          <Button
            onClick={handleModalUpload}
            variant="contained"
            disabled={modalFiles.length === 0 || modalUploading}
            sx={{
              backgroundColor: '#424242',
              color: 'white',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#616161' },
              '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            {modalUploading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Uploading...
              </>
            ) : (
              `Upload${modalFiles.length ? ` (${modalFiles.length})` : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Part Confirmation Dialog */}
      <Dialog
        open={showDeletePartConfirm}
        onClose={() => setShowDeletePartConfirm(false)}
        aria-labelledby="delete-part-dialog-title"
        aria-describedby="delete-part-dialog-description"
      >
        <DialogTitle id="delete-part-dialog-title">
          Delete Part
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-part-dialog-description">
            Are you sure you want to delete this part? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDeletePartConfirm(false);
            setPartToDelete(null);
          }} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePart} 
            color="error" 
            variant="contained"
            disabled={loadingParts[partToDelete]}
          >
            {loadingParts[partToDelete] ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Proposal Confirmation Dialog */}
      <Dialog
        open={showDeleteProposalConfirm}
        onClose={() => setShowDeleteProposalConfirm(false)}
        aria-labelledby="delete-proposal-dialog-title"
        aria-describedby="delete-proposal-dialog-description"
      >
        <DialogTitle id="delete-proposal-dialog-title">
          Delete Proposal
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-proposal-dialog-description">
            Are you sure you want to delete this proposal? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDeleteProposalConfirm(false);
            setProposalToDelete(null);
          }} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteProposal} 
            color="error" 
            variant="contained"
            disabled={loadingProposals[proposalToDelete?.id]}
          >
            {loadingProposals[proposalToDelete?.id] ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Proposal Dialog */}
      <Dialog
        open={!!rejectingProposal}
        onClose={() => {
          setRejectingProposal(null);
          setRejectionForm({ notes: '', rejectWithoutNote: false });
          setRejectionErrors({});
        }}
        aria-labelledby="reject-proposal-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="reject-proposal-dialog-title">
          Reject Proposal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {/* Please provide a reason for rejecting this proposal (optional but encouraged). */}
          </DialogContentText>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Notes"
            value={rejectionForm.notes}
            onChange={(e) => {
              setRejectionForm({ ...rejectionForm, notes: e.target.value });
              if (e.target.value.trim().length > 0) {
                setRejectionErrors({});
              }
            }}
            error={!!rejectionErrors.notes}
            helperText={rejectionErrors.notes || `${rejectionForm.notes.length}/5000 characters`}
            sx={{ mb: 2 }}
            placeholder="Enter rejection notes..."
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rejectionForm.rejectWithoutNote}
                onChange={(e) => {
                  setRejectionForm({ ...rejectionForm, rejectWithoutNote: e.target.checked });
                  if (e.target.checked) {
                    setRejectionErrors({});
                  }
                }}
                sx={{
                  color: !rejectionForm.notes?.trim() && !rejectionForm.rejectWithoutNote && rejectionErrors.rejectWithoutNote ? '#d32f2f' : undefined
                }}
              />
            }
            label={
              <Typography 
                sx={{ 
                  color: !rejectionForm.notes?.trim() && !rejectionForm.rejectWithoutNote && rejectionErrors.rejectWithoutNote ? '#d32f2f' : undefined
                }}
              >
                Reject without a note
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRejectingProposal(null);
              setRejectionForm({ notes: '', rejectWithoutNote: false });
              setRejectionErrors({});
            }} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRejectProposal} 
            color="error" 
            variant="contained"
            disabled={loadingProposals[`reject_${rejectingProposal}`]}
          >
            {loadingProposals[`reject_${rejectingProposal}`] ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Part Dialog */}
      <Dialog
        open={!!rejectingPart}
        onClose={() => {
          setRejectingPart(null);
          setPartRejectionForm({ notes: '', rejectWithoutNote: false });
          setPartRejectionErrors({});
        }}
        aria-labelledby="reject-part-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="reject-part-dialog-title">
          Reject Part
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {/* Please provide a reason for rejecting this part (optional but encouraged). */}
          </DialogContentText>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Notes"
            value={partRejectionForm.notes}
            onChange={(e) => {
              setPartRejectionForm({ ...partRejectionForm, notes: e.target.value });
              if (e.target.value.trim().length > 0) {
                setPartRejectionErrors({});
              }
            }}
            error={!!partRejectionErrors.notes}
            helperText={partRejectionErrors.notes || `${partRejectionForm.notes.length}/5000 characters`}
            sx={{ mb: 2 }}
            placeholder="Enter rejection notes..."
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={partRejectionForm.rejectWithoutNote}
                onChange={(e) => {
                  setPartRejectionForm({ ...partRejectionForm, rejectWithoutNote: e.target.checked });
                  if (e.target.checked) {
                    setPartRejectionErrors({});
                  }
                }}
                sx={{
                  color: !partRejectionForm.notes?.trim() && !partRejectionForm.rejectWithoutNote && partRejectionErrors.rejectWithoutNote ? '#d32f2f' : undefined
                }}
              />
            }
            label={
              <Typography 
                sx={{ 
                  color: !partRejectionForm.notes?.trim() && !partRejectionForm.rejectWithoutNote && partRejectionErrors.rejectWithoutNote ? '#d32f2f' : undefined
                }}
              >
                Reject without a note
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRejectingPart(null);
              setPartRejectionForm({ notes: '', rejectWithoutNote: false });
              setPartRejectionErrors({});
            }} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRejectPart} 
            color="error" 
            variant="contained"
            disabled={loadingParts[`reject_${rejectingPart}`]}
          >
            {loadingParts[`reject_${rejectingPart}`] ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        PaperComponent={isSmallScreen ? undefined : PreviewPaperComponent}
        aria-labelledby="draggable-preview-title"
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        {previewAttachment && (
          <>
            <Box 
              id="draggable-preview-title"
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 2, 
                borderBottom: '1px solid #333',
                cursor: 'move'
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                {previewAttachment.name}
              </Typography>
              <IconButton onClick={() => setImagePreviewOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: '400px', backgroundColor: '#000' }}>
              {previewAttachment.type.startsWith('image/') ? (
                <img src={previewAttachment.src} alt={previewAttachment.name} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
              ) : isVideoFile(previewAttachment.type) ? (
                <video
                  src={previewAttachment.src}
                  controls
                  autoPlay
                  playsInline
                  style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, backgroundColor: '#000' }}
                />
              ) : previewAttachment.type === 'application/pdf' ? (
                <iframe src={previewAttachment.src} style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }} title={previewAttachment.name} />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white', textAlign: 'center' }}>
                  {getFileIcon(previewAttachment.type)}
                  <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>{previewAttachment.name}</Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<DownloadIcon />} 
                    onClick={async () => {
                      try {
                        // Fetch the file as a blob to force download
                        const response = await fetch(previewAttachment.src);
                        const blob = await response.blob();
                        
                        // Create a blob URL and trigger download
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = previewAttachment.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        // Clean up the blob URL
                        window.URL.revokeObjectURL(blobUrl);
                      } catch (error) {
                        console.error('Download error:', error);
                      }
                    }} 
                    sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #333', backgroundColor: '#111' }}>
              <Typography variant="body2" sx={{ color: '#ccc', textAlign: 'center' }}>
                {(previewAttachment.size / 1024).toFixed(2)} KB • Uploaded by {previewAttachment.uploadedBy} on {previewAttachment.uploadedAt}
              </Typography>
            </Box>
          </>
        )}
      </Dialog>
    </Dialog>
    </LocalizationProvider>
  );
}
